import { ECONOMY } from './constants/economy-registry';
import { mountLayoutReadout, wantsLayoutReadout } from './layout-readout';
import { CoinMotionQueue } from '@puzzlemania/ui';
import { recordingWallet } from './coin-motion/recording-wallet';
import { Result as R } from '@puzzlemania/shared';
import { ANALYTICS_EVENTS } from './constants/analytics';
import { CELL_SIZE, FRAME_BORDER, MAX_UNDOS, SWIPE_GUARD_PX } from './constants/game-constants';
import { REMOTE_CONFIG_KEYS } from './constants/remote-config';
import { SAVE_KEYS } from './constants/save-keys';
import { resumeTrackBoard, resumedBudget } from './save/resume-board';
import { FreeLevelGenerator } from './generator/free-generator';
import { PerfOverlay } from './dev/perf-overlay';
import { confirmStage } from './powerups/confirm-stage';
import { confirmBadgeGates, confirmPreviewGates } from './powerups/confirm-preview';
import { boundingBox } from './model/shape';
import { DEFAULT_GENERATION_CONFIG, TRACK_IDS } from './config/generation-config';
import { trackPositionFor, rampLengthFor } from './generator/track-map';
import { TrackLevelGenerator } from './generator/track-generator';
import { LOOKAHEAD_GAP_MS, TrackLookahead, lookaheadTarget, } from './generator/puzzle-lookahead';
import { RouteCache } from './generator/route-cache';
import { applyFreeMove } from './model/free-replay';
import { SHORT_SOLVE_DEFAULT_NODES, solveRequest, solveRequestFor, } from './solver/short-solve';
import { REVEAL_REASON_ID, REVEAL_TIP_SEEN, shouldHighlightReveal, shouldShowRevealTip, } from './model/reveal-highlight';
import { hasNoFreeMoves } from './model/stuck-detector';
import { isOutOfMoves, moveBudgetFor, movesRemaining } from './model/move-budget';
import { boardBaselineUnlocks, guidedPowerupFor, guidedTargetFor, markUnlocksSeen, pendingUnlocks, } from './onboarding/unlock-events';
import { markTutorialSeen, migrateSeenTutorials, pendingTutorial, } from './onboarding/tutorial-triggers';
import { homeTourEligible } from './onboarding/home-tour';
import { exploredCount, explorableTopics, howToTopics, isAllExplored, } from './howto/topics';
import { BlockJamAudioId } from './constants/audio-ids';
import { LEVEL_COMPLETE_SOURCE_ID, REWARD_AD_DOUBLE_SOURCE_ID, REWARD_AD_SHOP_TOPUP_SOURCE_ID, REWARD_AD_BREAK_THANKS_SOURCE_ID, DAILY_COMPLETE_SOURCE_ID, DAILY_REWARD_COINS, HOWTO_EXPLORED_COINS, HOWTO_EXPLORED_SOURCE_ID, WELCOME_GIFT_COINS, WELCOME_GIFT_SOURCE_ID, DAILY_LOGIN_SOURCE_ID, QUEST_SOURCE_PREFIX, MILESTONE_SOURCE_PREFIX, QUESTS, STREAK_REPAIR_PRICE, STREAK_REPAIR_SOURCE_ID, avatarReasonId, DAILY_RETRY_PRICE, DAILY_RETRY_SOURCE_ID, EXTRA_MOVES_GRANT, EXTRA_MOVES_PRICES, EXTRA_MOVES_SOURCE_ID, RESCUES_PER_ATTEMPT, PLAYER_TITLES, FREE_COINS_COOLDOWN_MS, IAP_COIN_PACK_PRODUCT_ID, IAP_COIN_PACK_SOURCE_ID, STREAK_SAVE_REASON_ID, } from './constants/economy';
import { EMPTY_DAILY_LOGIN, claim as claimDailyLogin, claimableToday, continuesStreak, nextStreakDay, repair as repairDailyLogin, repairOffer, } from './meta/daily-login';
import { EMPTY_QUESTS, applyEvent as applyQuestEvent, claimQuest, claimable as claimableQuests, } from './meta/quests';
import { counterOf, EMPTY_MILESTONES, LEGACY_QUEST_TIER, applyEvent as applyMilestoneEvent, badgePoints, claimLevel as claimMilestoneLevel, claimable as claimableMilestones, seedMilestones, withTrackReach, trackLadder, } from './meta/milestones';
import { featureState, featureStates, introDue, isUnlocked } from './meta/feature-unlocks';
import { missionsView } from './meta/mission-view';
import { applyMissionEvent, claimMission as claimMissionSlot, claimSetBonus as claimMissionSetBonus, isLive as missionsLive, missionDef, newMissionSalt, rollMissions, } from './meta/missions';
import { EMPTY_WIN_STREAK, applyWinStreakEvent, canSaveStreak, streakSavePrice, streakStepReward, STREAK_STAKE_FROM, } from './meta/win-streak';
import { buildMilestonesView } from './meta/milestone-view';
import { chooseAvatar as chooseAvatarIn, avatarPrice, avatarTierName, avatarCollection, defaultAvatarState, isUnlockable, newAvatarIds, ownsAvatar, unlockAvatar, displayedAvatar, PROFILE_IN_PUZZLE_SOLVES, WELCOME_AVATAR_ID, } from './meta/avatars';
import { summarizeSave } from './meta/save-summary';
import { pickWinLine, } from './meta/milestone-awareness';
import { poweredPlateIds } from './model/power';
import { dailyDate, dailyEffectiveLevel, dailySeed, streakAfterClear } from './daily/daily-spec';
import { generateFreeLevel } from './generator/free-generator';
import { PowerupController } from './powerups/powerup-controller';
import { activateEffect, activationFor, applyEffect, applyGateEffect, chargesWhenSpent, gateTargetsFor, openUnpaidPowerups, POWERUP_EFFECTS, targetsFor, } from './powerups/powerup-effects';
import { RAINBOW_GATES_ID } from './powerups/effects/rainbow-gates';
import { SURGE_ID } from './powerups/effects/surge';
import { endRainbow, isRainbowActive, rainbowMovesOpen, wildSpentBy } from './model/rainbow';
import { endSurge, isSurgeActive, surgeMovesOpen, surgeSpentBy } from './model/surge';
import { boardExitOptions } from './model/board-exit-options';
import { scriptedLevel } from './levels/scripted-levels';
/** A save that has never been played: every track at puzzle 1, nothing paid. */
function freshTrackProgress() {
    return Object.fromEntries(TRACK_IDS.map((id) => [id, { ordinal: 1, maxPaidOrdinal: 0 }]));
}
import { rewardTierForScore } from './generator/reward-tier';
import { coinsForTrackPuzzle, REWARD_CURVE_BASE, REWARD_CURVE_PER_LEVEL, } from './generator/reward-curve';
import { activeModal } from './ui/active-modal';
import { MILESTONE_TOAST_MS } from './ui/MilestoneToast';
import { cardReadiness, isQualified, prevTrackOf, winMoveUp, } from './model/track-readiness';
import { pickHint } from './powerups/powerup-hint';
import { isPowerupUnlocked, powerupDefinition, powerupRegistry } from './powerups/powerup-registry';
/**
 * The longest the win sequence will wait for the last block's slide-out before
 * starting the confetti regardless.
 *
 * A CEILING, not the beat. The beat is `board.whenRemoveFxSettled()` — the
 * board saying the block is through its gate and the gate has finished
 * reacting. This constant exists only so a flourish can never hold the popup
 * hostage, which is the same rule that keeps `playRemoveFx` fire-and-forget
 * everywhere else.
 *
 * WP-36.5 originally used a fixed 250ms timer here, mirroring `EXIT_MS` in
 * `free-slide-player`, and that was the bug: the two clocks only agreed while
 * nothing else was competing for the main thread, and the work this very method
 * triggers — banking the reward, saving, re-rendering the HUD, building level
 * N+1 — is exactly such a competitor. On a player's recording the timer expired
 * with the slide-out not yet drawn a single frame.
 *
 * Sized off the longest run the board can queue (`GATE_SPARK_MS`, 420ms) with
 * generous room for a device that is genuinely struggling; it is a tripwire, not
 * a schedule, and it should not normally be what ends the wait.
 */
const EXIT_FX_TIMEOUT_MS = 1200;
function defaultReducedMotion() {
    return (typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
}
/**
 * How long a music track ramps up over (Module 64, WP-64.A5).
 *
 * Longer than `STOP_FADE_MS` (150 ms) on purpose: the outgoing track's fade and
 * the incoming track's ramp overlap, so a symmetrical pair would leave an
 * audible dip in the middle. 600 ms is short enough that a player changing
 * level does not wait for the music to arrive, and long enough that the arrival
 * is not a click.
 */
const MUSIC_FADE_IN_MS = 600;
/**
 * Module 147 (D1) — the last Beginner puzzle that still shows the Top Bar labels
 * to a new player who has not used both buttons. Puzzle 10 is also where the
 * power-ups unlock, the point where the game stops treating them as new.
 */
export const TOP_BAR_LABEL_LAST_PUZZLE = 10;
export class BlockJamPlugin {
    /**
     * Module 50 (WP-50.25) — the highest level that has ever paid out, as a
     * high-water mark. A level credits coins only when its number is **above**
     * this; clearing it again is free to do and pays nothing.
     *
     * A mark rather than a set of cleared levels, and that is not a shortcut:
     * level select never offers a level above `_maxReachedLevel`, and
     * `_maxReachedLevel` only advances through `_nextLevel`, which only runs on a
     * clear. So the levels a player has been paid for are exactly `1..mark` —
     * there is no hole for a set to remember, and one integer cannot desynchronise
     * from itself the way a list can.
     *
     * Starts at 0, not 1: level 1 has not been paid for on a fresh save.
     *
     * Why this exists at all: `_levelCompleteGranted` is per-attempt and is reset
     * by restart, next-level AND jump-to-level, so before this a player could
     * replay level 1 — the same seeded three-move board, forever — and be paid in
     * full each time. Every number in Increment H is defeated by that on day one.
     */
    _maxPaidOrdinal(track = this._track) {
        return this._trackProgress[track]?.maxPaidOrdinal ?? 0;
    }
    constructor(_boardOverride, _initialStateFactory, _reducedMotionFn = defaultReducedMotion, 
    // Generation config injected via the constructor seam (no globals). Defaults
    // to the in-bundle parity config that mirrors `config/generation.json`.
    _generationConfig = DEFAULT_GENERATION_CONFIG, 
    /**
     * Block RENDERING parameters (WP-36.2), same seam and same rule: injected,
     * never global. `undefined` leaves `FreeSlidePlayer` on its own
     * `DEFAULT_BLOCK_VISUAL_CONFIG`, which is the parity mirror of
     * `config/block-visuals.json` — so an un-configured host renders exactly as
     * it did before this parameter existed.
     */
    _visualConfig, 
    /**
     * Show the dev timing overlay (WP-36.11). Injected, never read from a global
     * or an env var, so the GAME holds no policy about what counts as "dev" —
     * the composition root decides and this stays testable. Off by default, so
     * an un-configured host is exactly as it was before this existed.
     */
    _showPerfOverlay = false, 
    /**
     * Board CHROME parameters (Module 38, WP-38.3) — surface/grid/frame/canvas.
     * Same seam, same rule: injected, never global. `undefined` leaves
     * `FreeSlidePlayer` on its own `DEFAULT_BOARD_VISUAL_CONFIG`, the parity
     * mirror of `config/board-visuals.json`, so an un-configured host renders
     * exactly as it did before this parameter existed.
     */
    _boardVisualConfig, 
    /**
     * URLs of shared application art (Module 44, WP-44.1) — today, the tutorial
     * hand. Same seam and same rule as the configs above: injected, never
     * global, and `undefined` degrades rather than breaks (the ghost hand falls
     * back to its ring, halo and trail).
     */
    _assets, 
    /** Module 83 (WP-83.5b) — see `BlockJamManifestOptions.solver`. */
    _solver) {
        this._boardOverride = _boardOverride;
        this._initialStateFactory = _initialStateFactory;
        this._reducedMotionFn = _reducedMotionFn;
        this._generationConfig = _generationConfig;
        this._visualConfig = _visualConfig;
        this._showPerfOverlay = _showPerfOverlay;
        this._boardVisualConfig = _boardVisualConfig;
        this._assets = _assets;
        this._solver = _solver;
        this._context = null;
        this._enabled = true;
        this._sessionStartMs = 0;
        this._state = null;
        this._board = null;
        /**
         * Board snapshots for undo — the free model replaces the legacy undo stack.
         * The move count travels WITH the snapshot: undo returns the player to the
         * position they were in, and that includes the counter (it read one HIGHER
         * after an undo when only the blocks were restored).
         */
        this._history = [];
        /** Unlock popups already shown, persisted with the save (WP-35.16). */
        this._seenUnlocks = [];
        /**
         * WP-43.5 — tutorial moments already shown, persisted with the save. Replaces
         * WP-41.8's single `_seenTutorial` boolean; legacy saves migrate on load.
         */
        this._seenTutorials = [];
        /**
         * WP-48.10 — the goal tooltip has already introduced itself to this player.
         *
         * Its own flag rather than an entry in `_seenTutorials`: that set drives
         * `pendingTutorial()`, which draws a ghost hand on the board, and putting a
         * non-gesture moment in it would make the two able to suppress each other.
         */
        this._seenGoalIntro = false;
        /**
         * WP-52.1 — the language step has already introduced itself to this player.
         *
         * Its own persisted flag, beside `_seenGoalIntro` and for the same reason: it
         * is a coach step, not a board gesture, so it must not join `_seenTutorials`
         * (which drives `pendingTutorial()` and would let the two suppress each other).
         *
         * ## Why a flag at all, when the step already had two conditions
         *
         * It had two, and neither could answer "has this player seen it" — reported
         * 2026-08-14 as the step appearing only sometimes:
         *
         *  - `moveCount === 0`. One real move spent it forever. Not a rare path: the
         *    drag step before it ends on the first PRESS (WP-41.8), so a player whose
         *    first gesture is an actual move burns both steps in one motion
         *    (`Bugs/Language Tutorial Missing_2.mp4`, MOVES 1/6).
         *  - `languageWasDetectedThisBoot()`. Boot-scoped by construction and correct
         *    for what it was built for — it answers "did THIS boot pick the language",
         *    which is true only on the very first boot after storage is empty. Any
         *    reload before the player reaches the step makes it false for the rest of
         *    that player's life, and on a portal a reload is every bounced visit
         *    (`Bugs/Language Tutorial Missing_1.mp4`, MOVES 0/6 and no step).
         *
         * Neither is wrong about the thing it measures; both were being asked the
         * wrong question. This is the right one, and it is the same shape the goal
         * step next to it has used since WP-48.10.
         */
        this._seenLanguageIntro = false;
        /** Module 77 (WP-77.10) — the powered-gate explainer, once ever (see the save field). */
        this._seenPoweredHelp = false;
        /** Module 80 (WP-80.7) — see `BlockJamSaveData.explainedPowerups`. */
        this._explainedPowerups = [];
        /** Module 144 (WP-144.1) — see `BlockJamSaveData.powerupConfirms`. */
        this._powerupConfirms = {};
        /**
         * Module 144 (WP-144.3) — the confirm card's board preview while it is up:
         * the gates ringed pink, and the ones that carry a first-use badge.
         */
        this._confirmPreview = null;
        /** Module 101 (WP-101.1) — see `BlockJamSaveData.howToSeen`. */
        this._howToSeen = [];
        /** Module 131 (WP-131.7) — see `BlockJamSaveData.seenFeatureIntros`. */
        this._seenFeatureIntros = [];
        /** Module 101 (WP-101.1) — see `BlockJamSaveData.howToRewarded`. */
        this._howToRewarded = false;
        /** Module 118 (WP-118.2) — see `BlockJamSaveData.dailyLogin`. */
        this._dailyLogin = EMPTY_DAILY_LOGIN;
        /** Module 118 (WP-118.2) — see `BlockJamSaveData.quests`. */
        this._quests = EMPTY_QUESTS;
        /** Module 132 (WP-132.1) — see `BlockJamSaveData.milestones`. */
        this._milestones = EMPTY_MILESTONES;
        /**
         * Module 132 — rungs reached since the HUD last took them (the toast and
         * the win popup line, WP-132.6). In memory only: a reached rung is still
         * claimable after a reload; only the moment is lost.
         */
        this._milestonesEarned = [];
        /** Clock times of recent milestone claims (the 15-per-minute guard). Not saved. */
        this._milestoneClaimTimes = [];
        /**
         * Module 133 (WP-133.1) — see `BlockJamSaveData.missions`. Null until the
         * first draw (Missions unlocked), which makes the salt.
         */
        this._missions = null;
        /** Module 134 (WP-134.1) — see `meta/win-streak.ts`. */
        this._winStreak = EMPTY_WIN_STREAK;
        /** Module 134 — what this win did to the streak, for the win popup; null if it didn't count. */
        this._streakWin = null;
        /** Module 134 — the latest "ended" or "kept" line; a new `seq` shows it. */
        this._streakNotice = null;
        this._streakNoticeSeq = 0;
        /** Module 133 — mission slots finished since the HUD last took them (the toast). Not saved. */
        this._missionsCompleted = [];
        /**
         * WP-132.6 — the counters when the last win line was built (or the save
         * loaded): "this puzzle" for the line is everything since. Not saved.
         */
        this._lineBase = {};
        /** WP-132.6 — the win popup's milestone line for the latest win. */
        this._winLine = null;
        /**
         * Module 133 (WP-133.4, D1a) — the mission this win finished, for the win
         * popup's line (it takes the milestone line's place). Not saved.
         */
        this._missionWinSlot = null;
        /** Module 133 (C1) — the mid-puzzle "Mission done!" toast. `delayMs` lets a badge toast go first. Not saved. */
        this._missionToast = null;
        /** WP-132.6 — the mid-puzzle toast: the rungs one move reached, merged. Not saved. */
        this._milestoneToast = null;
        /** WP-132.11 — the title last logged (or at load), for `blockjam_title_reached`. */
        this._lastTitle = null;
        /** WP-132.7 — the full-screen reveal owed for a Gold or Platinum tier. Not saved. */
        this._milestoneReveal = null;
        /** Whether the CURRENT arm is that power-up's first, so its banner explains it. */
        this._armExplains = false;
        /** Module 80 (WP-80.12) — see `BlockJamSaveData.poweredBoardsSeen`. */
        this._poweredBoardsSeen = 0;
        /** Module 150 (WP-150.1) — the HUD says a popup covers the board (`board-cover.ts`). */
        this._boardCoveredByHud = false;
        /** The board last counted into `_poweredBoardsSeen`, so a re-render never counts twice. */
        this._poweredCountedKey = null;
        /** Module 79 (WP-79.3) — the first-visit home tour has been shown (once ever). */
        this._seenHomeTour = false;
        /**
         * Module 116 (WP-116.2) — see `BlockJamSaveData.homeOpened`. `true` until a
         * load proves otherwise, so a plugin that never loaded a save behaves as
         * before Module 116.
         */
        this._homeOpened = true;
        /**
         * Module 116 — the save this session loaded had never opened home, so the
         * player came in through a first visit. Latched at load: it is what makes
         * the home tour and the win-popup language line theirs, and opening home
         * mid-session must not take either away.
         */
        this._bootedIntoPuzzle = false;
        /** Module 147 (D5) — the one-time Home coach has been shown. Saved. */
        this._seenHomeCoach = false;
        /** Module 147 (D1) — the player has opened Settings at least once. Saved. */
        this._settingsOpened = false;
        /** Module 126 (WP-126.2) — the avatar shown and the ones got. Saved. */
        this._avatar = defaultAvatarState();
        /** Module 152 (WP-152.3) — the coin avatar the player is saving for. Saved. */
        this._avatarGoal = null;
        /** Module 152 (WP-152.3) — the newest avatar wave the player has been shown. Saved. */
        this._lastSeenAvatarWave = 1;
        /**
         * Module 126 (WP-126.5, D11) — signed in this session and the account's save
         * is still to be loaded (`host.account.loadAccountSave`), at the next safe
         * moment: the home screen, or between puzzles. In memory only.
         */
        this._accountLoadOwed = false;
        /** Module 126 (D11) — the account load was handed over; the page may reload. */
        this._accountHandedOver = false;
        /** Module 126 (D14) — the Welcome gift was granted (once ever). Saved. */
        this._welcomeGift = false;
        /** Module 126 (D7) — the one-time sign-in nudge was shown. Saved. */
        this._seenSignInNudge = false;
        /**
         * Module 126 (WP-126.7, Playgama rule 1.2.2: never prompt sign-in at launch)
         * — a puzzle was started this session. The nudge waits for it, so opening
         * the game never prompts; it shows on the next home visit after play.
         */
        this._puzzleStartedThisSession = false;
        /** Module 126 (D14) — the gift card on screen, or null. */
        this._welcomeGiftCard = null;
        /** Module 116 (D5) — the win-popup language line was answered or retired. Saved. */
        this._languageLineAnswered = false;
        /** Module 116 (D5) — the line has been on screen this session (for `ignored`). */
        this._languageLineShown = false;
        /**
         * Module 79 (WP-79.3) — the tour is owed THIS SESSION. Latched at load from
         * `homeTourEligible` and cleared only when the tour ends, never recomputed:
         * the tour marks itself seen the moment it shows, and a recomputed gate would
         * then unmount it on the very next render.
         */
        this._homeTourOwed = false;
        /** Module 79 (WP-79.3) — the host paused the game; the tour's clock stops. */
        this._hostPaused = false;
        /** Module 77 (WP-77.10) — `track:ordinal` of the board the plate lesson ran on this session. */
        this._poweredLessonShownOn = null;
        /**
         * WP-41.8/43.5 — the tutorial on screen right now (the hand is tracing), or
         * null. Holds the trigger rather than a boolean so the caption can be the one
         * belonging to THIS moment.
         */
        this._tutorial = null;
        /**
         * WP-45.4 — the caption actually showing, which is the trigger's own only when
         * its resolver demonstrated what the trigger promises. A resolver may fall
         * back to a different move and must then be able to change the words with it,
         * so the resolved key is held here rather than read back off `_tutorial`.
         */
        this._tutorialCaptionKey = null;
        /**
         * WP-50.9 (AUD-013) — the end of the board the caption must NOT sit on,
         * derived from the demonstrated move. See `_maybeShowTutorial` for the rule.
         */
        this._tutorialCaptionSide = null;
        /**
         * The power-up button the LIVE tutorial moment is about, or null when the
         * moment is about the board (2026-08-14). See `TutorialDemo`'s `button`
         * variant for why a power-up lesson cannot be a ghost hand on the canvas.
         *
         * Distinct from `_guidedPowerupId`, which outlives this: guided is "this is
         * new, it is yours" and persists until the power-up is used; this is the
         * spotlight, and ends the moment the player acts or dismisses it.
         */
        this._tutorialPowerupId = null;
        /** Unlock events queued for the current level, first one on screen. */
        this._unlockQueue = [];
        /**
         * Power-up caps/charges/unlocks (WP-35.17). Built in `initialize` because it
         * needs the context's wallet + analytics; null until then, so every caller
         * treats "no context yet" as "no power-ups", never as "free power-ups".
         */
        this._powerups = null;
        /** The armed power-up waiting for a target, or null (WP-35.18). */
        this._armedPowerupId = null;
        /**
         * The power-up button the onboarding highlight points at (WP-35.22) — the
         * "practice" half for a power-up unlock, since no block carries one. Cleared
         * the moment that power-up is actually used.
         */
        this._guidedPowerupId = null;
        /**
         * Module 73 (WP-73.7, decision D4) — the "try it free" glow's memory for the
         * CURRENT puzzle, keyed by `_freeHintKey` so a new puzzle starts clean while
         * a Restart (same puzzle) keeps its counters. The rules live in the pure
         * `powerups/powerup-hint.ts`; this only supplies facts and remembers answers.
         */
        this._freeHintKey = '';
        this._freeHintUndos = 0;
        this._freeHintRestarted = false;
        this._freeHintId = null;
        /** Shown and cleared on this puzzle — never a second time (rule 5). */
        this._freeHintDone = false;
        /** The generated level's own difficulty score, for reward scaling (WP-35.22). */
        this._levelScore = null;
        this._selectedBlockId = null;
        this._inputLocked = false;
        this._levelCompleteGranted = false;
        /**
         * The `level_complete` cue has sounded for this level (WP-37.8). A separate
         * flag from `_levelCompleteGranted` because the two no longer happen at the
         * same moment: the reward banks at win DETECTION so backgrounding the app
         * mid-confetti cannot cost the player coins, while the cue waits for the
         * confetti it belongs to (Module 37 §5).
         */
        this._levelCompleteCuePlayed = false;
        /**
         * Module 65 — the track the player is in, and where they are in it.
         *
         * `_ordinal` is the puzzle number the player sees; the EFFECTIVE level the
         * generator was asked for lives on the board as `puzzle.levelNumber`, and
         * every existing rule that keys off a level number (bands, unlocks, reward
         * tier, par) still reads that one and is unaware tracks exist.
         */
        this._track = 'beginner';
        this._ordinal = 1;
        /**
         * True when the save named no track, i.e. the player has never chosen one.
         *
         * The plugin still builds a Beginner board so the renderer has something real
         * to show; this flag is what the shell reads to put the Blockway home screen
         * in front of it. Keeping the board underneath means choosing a track is a
         * state change rather than a boot.
         */
        this._needsTrackSelection = false;
        /**
         * Module 67 (WP-67.C3) — the game is in the Daily Challenge, a MODE beside
         * the three tracks (never a fourth TrackId — the config schema pins the
         * tuple). While true: no power-ups (`powerups` returns []), no lessons, no
         * Restart, `_adoptLevel` budgets on the daily's own multiplier, and the win
         * pays through `_onDailyComplete` instead of the track ladder.
         */
        this._dailyMode = false;
        /**
         * Whether `_state` currently holds the DAILY board. Outlives `_dailyMode`
         * (Back clears the mode but not the board), and it is what stops
         * `selectTrack`'s same-track resume from resuming a daily board into a
         * track — the one caller for whom "same track" and "same board" diverge.
         */
        this._boardIsDaily = false;
        this._trackProgress = freshTrackProgress();
        /**
         * Module 87 — per-track state: the track's generator (its anti-repetition
         * walk), the boards prepared ahead of the player, and the board the player is
         * on there. Kept across Home and track switches so neither throws away work
         * nor rebuilds a board the player was already on.
         */
        this._ahead = new Map();
        /** Module 87 — every route the worker found, persisted (see `RouteCache`). */
        this._routeCache = new RouteCache();
        /**
         * The board the player is on, exactly as it was built — what Restart re-adopts.
         *
         * Restart used to RE-GENERATE from the effective level, which is only the same
         * board while a track is ramping (variant 0). In the endless phase the board is
         * that level at `variant = k - rampLength`, built against the track's own
         * anti-repetition window, so restarting puzzle 63 handed back level 23's
         * variant-0 board: a different puzzle with a different move budget. Keeping the
         * built board is the only answer that cannot drift from what was served,
         * whatever path produced it. Null until a board is adopted (a factory board
         * never is), in which case Restart falls back to regenerating.
         */
        this._currentBuilt = null;
        /** Module 103 (WP-103.3) — the latest 3·2·1 warning; `seq` replays the HUD burst. */
        this._movesAlert = null;
        this._coinsEarned = 0;
        /** Module 114 — `blockjam_first_move` is logged once per page life. */
        this._firstMoveLogged = false;
        /** Module 116 — `BOARD_SHOWN` goes out once per page life. */
        this._boardShownLogged = false;
        /** Module 116 (D7) — milestones already reported for this save. Saved. */
        this._earlyMilestones = [];
        /**
         * Module 50 (WP-50.25) — this clear paid nothing because the level had already
         * been paid for. Distinct from `_coinsEarned === 0`, which is also what a
         * board that has not been won yet looks like; the win popup needs to tell the
         * player which of the two it is rather than showing them a silent zero.
         */
        this._replayNoReward = false;
        /**
         * Module 41 (WP-41.11) — where the "double your coins" offer on the win popup
         * has got to for THIS level.
         *
         * `offer` → the button is shown. `pending` → the ad was requested and both
         * popup buttons are disabled, so a second tap cannot chain a second ad.
         * `granted` → paid, and the button is gone: one ad, one reward, no chaining.
         *
         * `unavailable` (Module 51, WP-51.4) → the ad did not happen: unfilled,
         * blocked, or refused by the host. It costs the player nothing and the offer
         * comes back a few seconds later, which is what `offer` alone used to do
         * immediately — and *that* is why this state exists. The button re-rendered
         * identical and unchanged, so a failed ad and a tap that never registered
         * looked exactly the same. The product owner reported the result as "the
         * button shows, tapping it does nothing", which is precisely what a silent
         * failure looks like from the outside. Saying so is not an error popup: the
         * platform's rule is that a failed ad must not punish the player, not that
         * the game must pretend it did not happen.
         *
         * Reset wherever `_coinsEarned` is, since the two describe the same payout.
         */
        this._doubleCoins = 'offer';
        /**
         * Module 96 (WP-96.3) — this win's one ad moment, from the host, asked once
         * when the clear is logged. `null`: not asked (a host that does not pace
         * wins, or the Daily), which keeps the old rule of offering whenever the host
         * has rewarded ads. Reset wherever `_doubleCoins` is.
         */
        this._winAdMoment = null;
        /**
         * Module 141 — the ad-break gift card while it shows (`seq` counts gifts, so
         * a second one restarts the card), and the timer that takes it away at the
         * Rule 9 cap. The unsubscribe from the host's ad-break thanks.
         */
        this._adGift = null;
        this._adGiftSeq = 0;
        this._adGiftTimer = null;
        this._unsubscribeAdBreakThanks = null;
        /** Module 137 — the unsubscribe from the host's coin-pack grants. */
        this._unsubscribeCoinPackGrants = null;
        /**
         * Module 137 — a bought pack's coins wait for the player to be back. The
         * store's checkout is drawn by the HOST page over the game, and the grant
         * can land while it still covers it (QA tool, 2026-10-30: the coins flew
         * behind the "Payment ✓" screen). The first pointer or key event inside the
         * game means the checkout has gone; until then nothing moves on the pill.
         */
        this._coinPackHold = null;
        /** WP-51.4 — puts the offer back after `unavailable` has been read. */
        this._doubleCoinsRetryTimer = null;
        /**
         * The minimal ads-only Shop's "watch an ad for coins" offer is in flight, so
         * the row shows as busy and cannot be tapped twice. No per-item id needed —
         * the Shop offers exactly one row.
         *
         * (Module 50, WP-50.29 deleted `_topUpOfferId`/`_topUpPendingId`, the
         * per-power-up pair this was modelled on.)
         */
        this._shopTopUpPending = false;
        /**
         * Module 52 (WP-52.6) — the last Shop ad request produced no ad.
         *
         * Cleared by the next request, so it describes one attempt and never lingers.
         * Not persisted: "there was no ad thirty seconds ago" is not a fact worth
         * carrying into the next session, and a stale one would be worse than none.
         */
        this._shopTopUpUnavailable = false;
        /**
         * Module 123 — when the home Free Coins button is ready again (epoch ms, the
         * context clock). 0 = ready. Set only by a REWARDED home ad; saved.
         */
        this._freeCoinsReadyAt = 0;
        /**
         * Module 123 — the home button's own "No ad now", shown for
         * `AD_UNAVAILABLE_MS` and then cleared — Double Coins' pattern. The Shop
         * keeps `_shopTopUpUnavailable`, unchanged.
         */
        this._freeCoinsUnavailable = false;
        this._freeCoinsRetryTimer = null;
        /** Module 50 (WP-50.28a) — an extra-moves ad is in flight. */
        this._extraMovesPending = false;
        /** Module 80 (WP-80.18) — a skip ad is in flight. */
        /** Module 81 (WP-81.4) — a Hint-button ad is in flight. */
        this._revealPending = false;
        /**
         * Module 110 (D8) — SOLVE IT's ad itself is on screen. Narrower than
         * `_revealPending` (which also covers the spend beat before it): only the ad
         * blocks the Top Bar, so the paid coins can be seen leaving first.
         */
        this._revealAdInFlight = false;
        /**
         * Module 81 (WP-81.6) — the low-moves highlight on the Hint button, while it
         * is up: the attempt and move count it lit on (the next move or a restart
         * ends it, read lazily rather than hooked) and whether the tip rides it.
         */
        this._revealLit = null;
        /** The attempt the last highlight lit on: at most one per attempt. */
        this._revealLitAttempt = -1;
        /** The "Stuck?" tip: still up, and the timer that takes it away. */
        this._revealTipUp = false;
        this._revealTipTimer = null;
        /**
         * Module 81 — the Hint button's notice strip: why a tap was refused, or
         * (WP-81.8, coin path) the "Use – 20 🪙" confirmation / "not enough coins".
         */
        this._revealNotice = null;
        this._revealNoticeTimer = null;
        /** Module 83 (WP-83.8) — the confirmation card is up (price per host). */
        this._revealConfirm = null;
        /** Module 83 (WP-83.9) — the Reveal page is up: the starting layout, the route, the budget. */
        this._revealPage = null;
        /** Module 83 — this clear paid nothing because the solution was revealed. */
        this._revealedNoReward = false;
        /**
         * Module 83 — the puzzles (`track:ordinal`) whose solution was paid for in
         * THIS SESSION. Deliberately not in the save (user, 2026-10-02): "once per
         * puzzle" is once per session — a player who watched, still could not
         * finish, and comes back another day may pay and watch again; within the
         * session every later tap re-opens the page free.
         */
        this._revealedThisSession = new Set();
        /** Module 83 — the renderer class, once `start()` has imported it (PixiJS stays lazy). */
        this._boardClass = null;
        /** Module 80 (WP-80.16) — when the current attempt began, for `time_spent_s`. */
        this._attemptStartedMs = Date.now();
        /**
         * Module 73 (WP-73.8e) — bumped whenever the attempt a reward would apply to
         * ends (a new board adopted, a Restart). A "+5 moves" ad that settles after
         * the player restarted or moved on belonged to an attempt that no longer
         * exists: its grant is dropped, not carried into the fresh one.
         */
        this._attemptSeq = 0;
        /**
         * Module 45 (WP-45.3) — the power-up whose "not unlocked yet" notice is on
         * screen, or null.
         *
         * A locked button is tappable from this WP (CrazyGames QA BUG-002: it must
         * "give meaningful unlock feedback"), and this is the whole of that feedback.
         * It is deliberately NOT a popup: the answer is one sentence, the player has
         * lost nothing, and interrupting a puzzle to say "not yet" would be a worse
         * experience than the greyed-out button it replaces.
         */
        this._refusalNotice = null;
        /**
         * Module 76 — replays the Top Bar coin badge's attention animation: a shake
         * when a tap was short of coins, a "−N" float when a paid use was charged.
         * `id` only ever grows, so each change replays exactly once.
         */
        this._coinsCue = null;
        /**
         * Module 112 (D6/P1) — a free use was just spent: the HUD flies that power-up's
         * ticket from its corner into its button. Set ONLY where a free use is
         * consumed (`usePowerup` returning `free`), which is the tap for Hammer and
         * Colour Bomb and the using exit for Wild Gates and Master Key — the same
         * moment a paid use's coins leave. Replays when `id` changes.
         */
        this._freeUseCue = null;
        /**
         * Module 110 (WP-110.1) — transactions the Top Bar has not shown yet. Every
         * credit and debit is recorded here by `_wallet` (see `recording-wallet.ts`);
         * the HUD releases them when nothing covers the pill.
         */
        this._coinMotion = new CoinMotionQueue();
        this._recordingWallet = null;
        /**
         * Module 76 (D2) — each board mode switched on with a FREE use, and how many
         * free uses are left after it, so its "active" strip can say "Free use · n
         * left". Absent after a paid activation, and cleared on every level start.
         * Module 91 — keyed by id: Wild Gates and Master Key can be open together,
         * each covered (or not) by its own free use.
         */
        this._activeFreeUses = new Map();
        /**
         * The timer that takes {@link _refusalNotice} away again.
         *
         * Held as a field solely so it can be cancelled. A notice that outlives its
         * level reappears over the next board, and one that outlives the plugin fires
         * `_renderHud()` against a torn-down React root — which is why every level
         * transition and `destroy()` clear it.
         */
        this._lockedNoticeTimer = null;
        this._isWon = false;
        /**
         * Module 77 (D6) — the win popup's "Try <next track> →" offer, decided once
         * in `_showWin` (which also counts it against the per-track cap of two), and
         * dropped whenever the board changes.
         */
        this._moveUpOffer = null;
        /**
         * Module 78 (WP-78.3) — the track just entered from the move-up, while its
         * entry card is on screen; null otherwise. Everything that would land on the
         * new board's first frame (lesson, unlock card, explainer, try-it glow) waits
         * for it, so the player registers the new track before being taught in it.
         */
        this._trackEntry = null;
        /**
         * The win celebration is running (WP-36.5): the board is cleared and the
         * reward is already banked, but the popup is deliberately held back until the
         * slide-out and the confetti have played. Separate from `_isWon` because
         * `_isWon` is what RENDERS the popup — collapsing the two is exactly the bug
         * this WP fixes.
         */
        this._isCelebrating = false;
        /** The pending "exit FX has finished, start the confetti" beat, if any. */
        this._celebrationTimer = null;
        /**
         * Module 87 — the win window is open for look-ahead work only once the last
         * slide-out has played (or a skip / reduced motion reached the popup): the
         * WP-36.11 rule that a build must not land on the animation the player is
         * watching.
         */
        this._winWindowOpen = false;
        /** Module 87 — a pointer is down on the game: a drag may be in progress. */
        this._pointerDown = false;
        this._lastPointerUpMs = 0;
        this._gapTimer = null;
        this._pointerListeners = null;
        /**
         * Module 83 (WP-83.5b) — a board whose route is still being searched in the
         * host's worker. While set, the HUD shows "Preparing puzzle…" and the entry
         * that started it finishes when the route arrives — unless `token` has moved
         * on, in which case that entry was superseded (Home, another track) and its
         * result is dropped.
         */
        this._preparing = null;
        this._prepareToken = 0;
        /**
         * Module 102 — the board lost its WebGL context and could not rebuild one
         * by itself. While set, the board region shows "Tap to reload the board".
         */
        this._boardRenderLost = false;
        /**
         * Module 95 — today's Daily, built and its route requested while the home
         * screen shows, so the card's tap finds it ready. `prepared` is exactly what
         * `enterDaily` adopts: a tap during the search waits on THIS search, never a
         * second one. `settled` turns true when the route is in (or the search died,
         * `failed`, and the next entry builds afresh).
         */
        this._dailyPrep = null;
        this._dailyPrepCancel = null;
        /** Bumped by teardown so a route landing on a destroyed plugin is dropped. */
        this._dailyPrepToken = 0;
        /** Cancels the in-flight idle callback, whichever API scheduled it. */
        this._aheadCancel = null;
        /** Dev-only timing overlay (WP-36.11), or null when it was not enabled. */
        this._perf = null;
        this._isStuck = false;
        this._isInfoOpen = false;
        this._hintedBlockId = null;
        // HUD / input
        this._hudRoot = null;
        this._hudEl = null;
        /** Module 120 (WP-120.2) — the debounced screen-resize watcher that re-sizes the board's backing store. */
        this._resolutionWatch = null;
        /** Module 122 — stops the board-top measurement (`_watchBoardTop`). */
        this._boardTopWatch = null;
        this._canvasEl = null;
        this._containerEl = null;
        /** Logical canvas size — kept for the a11y/layout code that reads it. */
        this._canvasLogical = 0;
        // Drag-to-slide state
        this._pointerDownHandler = null;
        this._pointerMoveHandler = null;
        this._pointerUpHandler = null;
        /**
         * Module 77 (D4) — at most ONE power-up lesson per puzzle. Expert unlocks all
         * five at puzzle 1; the rest wait for the next puzzles. Keyed on track +
         * ordinal so a restart of the same puzzle does not chain the next lesson on.
         */
        this._powerupLessonShownOn = null;
        // ─── Background music (WP-37.13) ──────────────────────────────────────────
        //
        // The only id on the `music` channel, and therefore the only thing the
        // `musicVolume` slider has ever controlled (locked decision 6: Block Jam
        // only — the Lobby and Water Sort stay silent).
        //
        // Two flags rather than one, because "playing" and "should be playing" come
        // apart every time the player backgrounds the app: `_musicPlaying` guards the
        // adapter (a second `play()` starts a second Howl the single `stop()` cannot
        // reach), while `_musicWanted` records that the game is still on screen and
        // the track is owed a restart when the tab comes back. Leaving the game
        // clears `_musicWanted`; pausing does not.
        /** True between a `play()` and its `stop()` — the guard against a double start. */
        this._musicPlaying = false;
        /** True while the game is on screen: pause/hide suspend the track, exit ends it. */
        this._musicWanted = false;
        /**
         * Which track was last asked for (Module 64, WP-64.A6).
         *
         * "Asked for", not "sounding": under Module 57 a track may still be OWED,
         * queued behind the autoplay gate or a platform mute. That is exactly why the
         * id has to be remembered rather than re-derived — `_stopMusic()` must cancel
         * the id it actually queued, and `stop()` is what drops a pending entry from
         * the service's queue. Stopping a different id would leave the old track to
         * start later, over a level it no longer belongs to.
         */
        this._musicTrackId = null;
        this._visibilityHandler = null;
        this._moveCommitTimer = null;
        this._pageExitHandler = null;
        this._generator = new FreeLevelGenerator(_generationConfig);
        // Pre-assign so move() works even when start() hasn't been called (e.g. tests).
        if (_boardOverride) {
            this._board = _boardOverride;
        }
    }
    /**
     * Build the board for a level. **The expensive half**, and the only half —
     * split out in WP-36.11 so it can also run at idle, ahead of the click that
     * needs it. Touches no plugin state beyond the generator's own anti-repetition
     * history, so calling it early cannot disturb the level being played.
     */
    _buildLevel(levelNumber, difficulty) {
        // Module 63 (WP-63.E1) — the one scripted-level lookup. A band override is
        // the Studio's, and a hand-authored board has no band to override, so the
        // generator answers that call as before.
        if (!difficulty) {
            const scripted = scriptedLevel(levelNumber, this._generationConfig);
            if (scripted)
                return scripted;
        }
        return this._generator.generate(levelNumber, {
            ...(difficulty ? { difficulty } : {}),
            solution: this._solutionMode(),
        });
    }
    /**
     * Module 83 (WP-83.5b) — with a host solver, generation takes the shipped
     * table route or stores none, and `_withRoute` fills the rest in off-thread.
     * Without one, generation searches on the main thread as before.
     */
    _solutionMode() {
        return this._solver ? 'table-only' : true;
    }
    /** The level with its route, or a promise of it when the worker has to search. */
    _withRoute(level) {
        if (level.metrics.solution !== null || !this._solver)
            return level;
        // Module 87 (WP-87.2b) — a board whose route was found before (this
        // session, or any earlier one: the cache rides in the save) is never
        // searched again. `get` replays the route against this exact board.
        const cached = this._routeCache.get(level.state);
        if (cached) {
            return { ...level, metrics: { ...level.metrics, solution: cached, solutionSource: 'cache' } };
        }
        const maxNodes = this._generationConfig.moveBudget.solution.maxNodes;
        const searched = (moves) => {
            if (moves)
                this._routeCache.put(level.state, moves);
            return {
                ...level,
                metrics: { ...level.metrics, solution: moves, solutionSource: moves ? 'search' : null },
            };
        };
        return this._solver(solveRequestFor(level.state, maxNodes)).then(searched, () => 
        // The worker failed: search here, exactly as a host without one would.
        searched(solveRequest(solveRequestFor(level.state, maxNodes))));
    }
    _prepareOrdinal(ordinal) {
        return this._withRoute(this._buildOrdinal(ordinal));
    }
    /**
     * Run `commit` with the prepared level: at once when it is ready, else after
     * the route arrives, with the HUD showing the preparing state meanwhile.
     */
    _whenPrepared(prepared, commit) {
        // Any entry supersedes a wait still in flight: its result is dropped.
        const token = ++this._prepareToken;
        if (!(prepared instanceof Promise)) {
            this._preparing = null;
            commit(prepared);
            return;
        }
        this._preparing = { token };
        this._renderHud();
        void prepared.then((level) => {
            if (this._preparing?.token !== token)
                return;
            this._preparing = null;
            commit(level);
        });
    }
    /** Module 83 — true while a board's route is being searched off-thread. */
    get preparing() {
        return this._preparing !== null;
    }
    /** Leaving for Home while a route is still in flight: that entry is over. */
    _dropPreparing() {
        this._prepareToken += 1;
        this._preparing = null;
    }
    // ─── Daily prewarm (Module 95) ──────────────────────────────────────────────
    /**
     * Module 95 (D1) — today's Daily is prepared while the home screen shows and
     * today's attempt is still open. Only with a host solver: without one the
     * route search runs on the main thread, and a multi-second freeze on the home
     * screen is worse than the wait it would save.
     */
    _dailyPrewarmWanted() {
        if (!this._context || !this._solver || !this._needsTrackSelection || this._dailyMode) {
            return false;
        }
        const date = dailyDate(this._context.clock);
        return this._daily?.date !== date || this._daily.state === 'in-progress';
    }
    /**
     * Module 95 (D2) — the host's solver is ONE worker answering in order, so a
     * Daily search sent behind the look-ahead's waits for all of them (11–16 s in
     * the endless phase, WP-95.1). While the Daily's route is owed on the home
     * screen, the look-ahead does not send anything.
     */
    _dailyPrewarmPending() {
        if (!this._context || !this._dailyPrewarmWanted())
            return false;
        const prep = this._dailyPrep;
        return !prep || prep.date !== dailyDate(this._context.clock) || !prep.settled;
    }
    /** Module 95 — build today's Daily at the next idle moment, unless it already is. */
    _prewarmDaily() {
        if (this._dailyPrepCancel || !this._context || !this._dailyPrewarmWanted())
            return;
        const prep = this._dailyPrep;
        if (prep && !prep.failed && prep.date === dailyDate(this._context.clock))
            return;
        this._dailyPrepCancel = this._onIdle(() => {
            this._dailyPrepCancel = null;
            // The player left the home screen (or the plugin went) while we waited.
            if (!this._context || !this._dailyPrewarmWanted())
                return;
            this._dailyPrepared(dailyDate(this._context.clock));
        });
    }
    /**
     * Module 95 (D4) — today's Daily: the prewarmed one when its date matches,
     * else built now. Generation is deterministic in (date, config), so either way
     * it is the same board; the move budget is NOT set here but at adopt time,
     * where `_dailyMode` decides it.
     */
    _dailyPrepared(date) {
        const existing = this._dailyPrep;
        if (existing && existing.date === date && !existing.failed)
            return existing.prepared;
        this._dailyPrepCancel?.();
        this._dailyPrepCancel = null;
        const level = dailyEffectiveLevel(date);
        const prepared = this._withRoute(generateFreeLevel(level, {
            config: this._generationConfig,
            seed: dailySeed(date),
            solution: this._solutionMode(),
        }));
        const slot = { date, prepared, settled: !(prepared instanceof Promise), failed: false };
        this._dailyPrep = slot;
        if (prepared instanceof Promise) {
            const token = this._dailyPrepToken;
            const settle = (built) => {
                if (token !== this._dailyPrepToken || this._dailyPrep !== slot)
                    return;
                slot.settled = true;
                if (built)
                    slot.prepared = built;
                else
                    slot.failed = true;
                // D5 — the route is in `_routeCache` now; one commit carries it into the
                // save, so a reload before the tap does not search again. Never in daily
                // mode: `enterDaily` commits on adopt, and a commit before that would
                // fold the outgoing board into today's record.
                if (built?.metrics.solutionSource === 'search' && !this._dailyMode && this._state) {
                    this._commitState();
                }
                // D2 — the hold on the look-ahead lifts.
                this._pumpLookahead();
            };
            prepared.then(settle, () => settle(null));
        }
        return prepared;
    }
    /** Run `run` at the next idle moment (a 0 ms timeout where idle callbacks don't exist). */
    _onIdle(run) {
        const idle = globalThis
            .requestIdleCallback;
        if (typeof idle === 'function') {
            const handle = idle(run, { timeout: 1000 });
            return () => {
                globalThis.cancelIdleCallback?.(handle);
            };
        }
        const handle = setTimeout(run, 0);
        return () => clearTimeout(handle);
    }
    /**
     * Adopt a built board as the current level: the cheap half, and the one that
     * must run on the click. Everything here is "entering a level", which is why
     * it must NOT run at pre-generation time.
     */
    _resumeCaps() {
        const cfg = this._generationConfig.moveBudget;
        return {
            maxAds: cfg.maxAdsPerLevel,
            maxBought: RESCUES_PER_ATTEMPT,
            adGrantMax: Math.max(0, ...cfg.adGrants),
            boughtGrant: EXTRA_MOVES_GRANT,
        };
    }
    /**
     * Module 129 (WP-129.3) — adopt a TRACK board, resuming the attempt saved for
     * it (`TrackProgress.board`) when the save fits the rebuilt puzzle. Boot,
     * track entry and the way back from the Daily use this; Restart and the next
     * puzzle use `_adoptLevel` (a fresh attempt).
     */
    _adoptTrackLevel(level, levelNumber) {
        const pristine = this._adoptLevel(level, levelNumber);
        const resumed = resumeTrackBoard(this._trackProgress[this._track]?.board, this._ordinal, pristine, this._resumeCaps());
        if (!resumed)
            return pristine;
        this._powerups?.restoreUsedThisLevel(resumed.powerupsUsed);
        return resumed.state;
    }
    _adoptLevel(level, levelNumber) {
        this._attemptSeq += 1;
        // Module 50 (WP-50.27) — a rainbow left open when the player restarts, jumps
        // or moves on is closed by the level change, so it is reported here rather
        // than being silently lost. `ended: 'level'` versus `'exit'` is what
        // separates "this power-up bought the player a way out" from "they paid for
        // it and never cashed it in", which is the measurement that decides whether
        // Rainbow is priced right relative to the Hammer.
        //
        // Read BEFORE the new state is built and returned — `this._state` is still
        // the outgoing board at this point, and nothing below reads it.
        //
        // Module 90 — Wild Gates is paid only on the exit that uses it, so one still
        // open here was never paid for: it is CANCELLED, not completed, and costs
        // nothing. Dropped with the outgoing state, which also releases the hold.
        // Module 91 — Master Key too (it was "completed, ended: level" while it was
        // paid on Use). Every open, unpaid mode is reported, each with its own id.
        if (this._state) {
            for (const id of openUnpaidPowerups(this._state)) {
                this._logOpenCancelled(this._state, id, 'level');
            }
        }
        // The generator already scored this board while choosing it (WP-35.10's
        // in-loop proxy), so reward scaling costs nothing at the win moment — the
        // exact score would be a ~350ms solve, which is not something to spend while
        // the player waits for a popup. `targetScore` is the curve's designed
        // difficulty for the rung and covers the fallback board, which has no proxy.
        this._levelScore = level.metrics.proxyScore ?? level.metrics.targetScore;
        this._currentBuilt = level;
        // Module 87 — the board this track is on, for a switch back; and a new
        // board closes the win window and gives the look-ahead its study window.
        this._winWindowOpen = false;
        if (!this._boardIsDaily)
            this._aheadFor(this._track).current = { ordinal: this._ordinal, built: level };
        this._pumpLookahead();
        this._history = [];
        // Module 65 (WP-65.C1) — derived from the BOARD, not from the level number.
        //
        // An Expert-first player's first board is level 61; under the level-keyed
        // rule they crossed none of the unlock levels and would have been handed the
        // L-shape, powered gates and four power-ups with no lesson for any of them,
        // ever. See `pendingUnlocks`.
        //
        // Capped at ONE per puzzle. Cumulative power-up unlocks mean a track that
        // starts high owes several lessons at once, and showing them back to back
        // teaches nothing — the remainder is simply still pending on the next board,
        // because nothing is marked seen until it is dismissed.
        // Module 67 (WP-67.C3) — the daily is no place for lessons: its board can
        // carry shapes and gates a Beginner-track player has never met, and an
        // unlock popup marking things "seen" off a one-day board would spend the
        // lesson their own track still owes them.
        this._unlockQueue = this._dailyMode
            ? []
            : pendingUnlocks(this._generationConfig, levelNumber, this._seenUnlocks, level.state, this._ordinal, this._track).slice(0, 1);
        // Module 68 (WP-68.2) — a track's first board is the player's BASELINE, so
        // what it shows is marked seen rather than merely left unshown. Without
        // this the shapes suppressed at puzzle 1 would simply queue up and fire on
        // puzzles 2, 3, 4 … for content that had been on screen since the start.
        // Empty on every other puzzle (and in daily mode, which owns no lessons at
        // all — WP-67.C3), so there is no `if` here to keep in step with that one.
        if (!this._dailyMode) {
            this._seenUnlocks = markUnlocksSeen(this._seenUnlocks, boardBaselineUnlocks(this._generationConfig, levelNumber, level.state, this._ordinal));
        }
        // A new board disarms whatever was armed — the target ids belonged to blocks
        // that no longer exist (WP-35.18).
        this._armedPowerupId = null;
        this._confirmPreview = null;
        this._guidedPowerupId = null;
        // Entering a level resets its power-up counters — including on restart, which
        // hands the uses back along with the board. Farming them that way costs the
        // player every move they had made, so the cap still bounds a single attempt,
        // which is what it is for. WP-65.F1 — the ordinal is what the unlock rule
        // reads; every caller sets `_ordinal` before it builds the board.
        this._powerups?.startLevel(levelNumber, this._ordinal, this._unlockTrack());
        this._activeFreeUses.clear();
        // Module 50 (WP-50.28a) — the move budget is per ATTEMPT, and this is the one
        // place every attempt passes through: first load, restart, next level and
        // jump-to-level all reach `_adoptLevel`. Deliberately NOT added to the three
        // sites that reset `_levelCompleteGranted` — that list exists because those
        // flags live on the plugin, while this lives on the state the board is built
        // from, so there is nothing to keep in step and no fourth site to forget.
        return {
            ...level.state,
            moveBudget: moveBudgetFor(this._generationConfig, {
                proxyPar: level.metrics.proxyPar,
                clearingDepth: level.metrics.clearingDepth,
                // Module 83 (WP-83.2) — the budget is never below the stored solution.
                solutionLength: level.metrics.solution?.length ?? null,
                blockCount: level.metrics.blockCount,
                dependencies: level.metrics.dependencies,
                usedFallback: level.metrics.usedFallback,
                difficulty: level.state.puzzle.difficulty,
                // Module 65 (WP-65.A3) — the track, without which the seam exists and
                // is never used: the budget silently stayed on the band multiplier and
                // Beginner never got the extra rope it was tuned for. Unit tests called
                // `moveBudgetFor` directly and could not see it; `pnpm smoke`'s
                // win-on-last-move planner disagreed with the game by one move, which is
                // exactly what that check is for.
                // Module 67 — the daily has no track; `intermediate`'s multiplier is
                // the deliberate middle (its 25–55 band is Intermediate's territory).
                track: this._dailyMode ? 'intermediate' : this._track,
                // Module 69 — the opening-puzzle grace, which is relief for a TRACK's
                // opening. `_ordinal` is set by every caller before the board is built
                // (see `_powerups.startLevel` just above, which already relies on it).
                // Withheld in daily mode on purpose: `_ordinal` there is the date's
                // effective level, not a position in anything, and a daily is nobody's
                // first board.
                ...(this._dailyMode ? {} : { ordinal: this._ordinal }),
                // Module 86 (L1) — the grace is for a player meeting this track cold.
                // One who has qualified on the track before it is not.
                entryGraceEligible: this._entryGraceEligible(),
                // Module 146 — the Daily keeps its own multiplier (1.2) now that
                // Intermediate's, which it used to ride, has tightened.
                daily: this._dailyMode,
            }),
            movesAdsUsed: 0,
            movesBought: 0,
        };
    }
    /**
     * Module 86 (L1) — false once the player has qualified on the track BEFORE
     * this one (cumulative, so Intermediate 35 also counts as past Beginner).
     * Beginner has no track before it, so its opening always keeps the grace.
     * Read when a board's budget is set; progress on the previous track cannot
     * change while this one is being played.
     */
    _entryGraceEligible() {
        const prev = prevTrackOf(this._track);
        return prev === null || !isQualified(this._generationConfig, prev, this._trackProgress);
    }
    /**
     * Module 65 — the scripted-level lookup in `_buildOrdinal` is keyed on the
     * EFFECTIVE level, which is why `scriptedLevelsFor` had to become
     * config-derived: the hand-authored powered-gates board belongs to the powered
     * unlock wherever that is, and under a cycling endless phase the same
     * effective level comes round again. Module 67 — every board built for a
     * track clears `_boardIsDaily`; only `enterDaily` sets it. Module 83
     * (WP-83.5b) — the "build and adopt" helper that used to live here became
     * `_prepareOrdinal` + `_whenPrepared`, because adopting may now wait on the
     * host's worker for the board's route.
     */
    /**
     * The ordinal of THIS track that lands on `level`, for a caller that only has
     * a level number — an injected fixture board, or the dev/QA jump.
     *
     * Floored at 1 but NOT capped at the ramp length: a fixture is allowed to
     * hand back any board it likes, and the position is a best effort at
     * describing it. WP-65.F1 — the cap had to go once unlocks moved onto the
     * ordinal: a fixture at "level 50" on Beginner used to clamp to puzzle 25 and
     * silently lock the very power-up the fixture was built to exercise. Past
     * the ramp the ordinal keeps counting, which is exactly what it does in play.
     */
    _ordinalForLevel(level) {
        const track = this._generationConfig.tracks.find((t) => t.id === this._track);
        if (!track)
            return 1;
        return Math.max(1, level - track.firstLevel + 1);
    }
    _buildOrdinal(ordinal) {
        const position = trackPositionFor(this._generationConfig, this._track, ordinal);
        // The scripted teaching board belongs to the RAMP's powered-unlock puzzle
        // only. The endless phase cycles through the ceiling band, so the unlock
        // level comes round again every lap — and a teaching board is a lesson, not
        // a puzzle. This was a session-scoped "shown once" Set, which a reload
        // emptied: Beginner puzzle 58 (effective level 22 again) served the two-block
        // tutorial board. Keyed on the position instead, it is a pure function of
        // (track, ordinal) and survives any reload.
        const scripted = !position.holding &&
            position.effectiveLevel === this._generationConfig.unlocks.poweredGatesUnlockLevel
            ? scriptedLevel(position.effectiveLevel, this._generationConfig)
            : null;
        // Module 87 — every build of this track's boards is reported to its walk,
        // queued or inline, scripted or generated: play skips the generator for a
        // scripted board too, and the look-ahead must walk exactly as play does.
        const ahead = this._aheadFor(this._track);
        ahead.noteBuilt(ordinal);
        if (scripted)
            return scripted;
        return ahead.generator.generate(ordinal, { solution: this._solutionMode() });
    }
    _generateLevel(levelNumber, difficulty) {
        // A board built outside the track walk (a factory fallback): whatever this
        // track had queued was built against a future that just changed.
        this._aheadFor(this._track).clear();
        return this._adoptLevel(this._buildLevel(levelNumber, difficulty), levelNumber);
    }
    // ─── Look-ahead (Module 87; replaces WP-36.11's single pre-built board) ─────
    /** This track's walk, queue and current board, created on first use. */
    _aheadFor(track) {
        let ahead = this._ahead.get(track);
        if (!ahead) {
            ahead = new TrackLookahead(new TrackLevelGenerator(track, this._generationConfig));
            this._ahead.set(track, ahead);
        }
        return ahead;
    }
    /** Module 87 — the board for `ordinal`: prepared ahead when it was, else built now. */
    _takeOrPrepare(ordinal) {
        const queued = this._aheadFor(this._track).take(ordinal);
        if (queued)
            return { prepared: queued, ready: true };
        return { prepared: this._prepareOrdinal(ordinal), ready: false };
    }
    /**
     * How idle the player is right now, most idle first (see `puzzle-lookahead`).
     * Anything that is not a track board in front of a player who is not touching
     * it is `busy`.
     */
    _idleWindow() {
        if (!this._context || this._hostPaused || this._preparing)
            return 'busy';
        if (this._dailyMode || this._boardIsDaily)
            return 'busy';
        if (this._isWon || (this._isCelebrating && this._winWindowOpen))
            return 'win';
        if (this._isCelebrating || this._pointerDown)
            return 'busy';
        if (this._needsTrackSelection)
            return 'study';
        if ((this._state?.moveCount ?? 0) === 0)
            return 'study';
        return Date.now() - this._lastPointerUpMs >= LOOKAHEAD_GAP_MS ? 'gap' : 'busy';
    }
    /**
     * Build the next board of this track's walk at the next idle moment, if the
     * current window wants one, and chain until it does not. One board per idle
     * callback: generation is the main-thread cost (up to ~250ms here, more on a
     * phone), so it is never batched. The route search starts right after, in
     * the host's worker.
     */
    _pumpLookahead() {
        // Module 95 — on the home screen, today's Daily is scheduled before any
        // look-ahead board (D2); everywhere else this is a no-op.
        this._prewarmDaily();
        if (this._aheadCancel || !this._context)
            return;
        if (!this._lookaheadWanted()) {
            this._armGapTimer();
            return;
        }
        const run = () => {
            this._aheadCancel = null;
            // Destroyed, or the window closed (a drag began), while we waited for idle.
            if (!this._context || !this._lookaheadWanted()) {
                this._armGapTimer();
                return;
            }
            const ahead = this._aheadFor(this._track);
            const ordinal = ahead.nextFor(this._ordinal);
            const startedMs = performance.now();
            // Deliberately NOT wrapped in try/catch: generation is deterministic, so
            // anything that throws here would throw on the click too.
            const prepared = this._prepareOrdinal(ordinal);
            ahead.put(ordinal, prepared);
            const record = (level) => {
                this._perf?.recordGeneration(performance.now() - startedMs, {
                    level: level.state.puzzle.levelNumber,
                    blocks: level.metrics.blockCount,
                    pregenerated: true,
                });
            };
            if (prepared instanceof Promise)
                void prepared.then(record, () => { });
            else
                record(prepared);
            this._pumpLookahead();
        };
        this._aheadCancel = this._onIdle(run);
    }
    _lookaheadWanted() {
        // Module 95 (D2) — today's Daily goes to the worker first.
        if (this._dailyPrewarmPending())
            return false;
        const target = lookaheadTarget(this._idleWindow(), this._ordinal);
        return this._aheadFor(this._track).nextFor(this._ordinal) <= target;
    }
    /**
     * While the player is between drags, the `gap` window opens LOOKAHEAD_GAP_MS
     * after the last pointer-up — nothing else would wake the pump at that moment.
     */
    _armGapTimer() {
        if (this._gapTimer !== null || this._pointerDown || !this._context)
            return;
        if ((this._state?.moveCount ?? 0) === 0 || this._isCelebrating || this._isWon)
            return;
        const wait = this._lastPointerUpMs + LOOKAHEAD_GAP_MS - Date.now();
        if (wait <= 0)
            return;
        this._gapTimer = setTimeout(() => {
            this._gapTimer = null;
            this._pumpLookahead();
        }, wait);
    }
    /** Stop pending look-ahead work. The queues themselves are kept. */
    _cancelLookahead() {
        this._aheadCancel?.();
        this._aheadCancel = null;
        if (this._gapTimer !== null) {
            clearTimeout(this._gapTimer);
            this._gapTimer = null;
        }
    }
    /** The slide-out has played (or was skipped): the win window opens. */
    _openWinWindow() {
        this._winWindowOpen = true;
        this._pumpLookahead();
    }
    _bindPointerTracking(el) {
        this._pointerListeners?.();
        // A host (or a test) may hand a container that is not a real element; the
        // look-ahead then simply never sees a drag, and `study`/`win` still work.
        if (typeof el?.addEventListener !== 'function' || typeof window === 'undefined')
            return;
        const down = () => {
            this._pointerDown = true;
            if (this._gapTimer !== null) {
                clearTimeout(this._gapTimer);
                this._gapTimer = null;
            }
        };
        const up = () => {
            if (!this._pointerDown)
                return;
            this._pointerDown = false;
            this._lastPointerUpMs = Date.now();
            this._armGapTimer();
        };
        el.addEventListener('pointerdown', down, true);
        window.addEventListener('pointerup', up, true);
        window.addEventListener('pointercancel', up, true);
        this._pointerListeners = () => {
            el.removeEventListener('pointerdown', down, true);
            window.removeEventListener('pointerup', up, true);
            window.removeEventListener('pointercancel', up, true);
        };
    }
    /** True while the board for the next puzzle is prepared and waiting. Test seam. */
    get hasPregeneratedLevel() {
        return this._aheadFor(this._track).has(this._ordinal + 1);
    }
    /** Module 87 — the ordinals prepared ahead on the current track. Test seam. */
    get lookaheadOrdinals() {
        return this._aheadFor(this._track).queued;
    }
    async initialize(context) {
        this._context = context;
        // Module 110 (D9) — the one wallet this plugin earns and spends through.
        this._recordingWallet = recordingWallet(context.economy, this._coinMotion, (change) => {
            // Module 114 — coins only: gems are hidden in Blockway and never move.
            if (change.currency !== 'coins')
                return;
            // Module 132 — the coins-spent milestone. Rides the caller's commit:
            // every debit site saves right after its own state change.
            if (change.kind === 'debit') {
                this._milestoneEvent({ type: 'coinsSpent', amount: change.amount }, false);
            }
            context.analytics.logEvent(ANALYTICS_EVENTS.COINS_CHANGED, {
                direction: change.kind === 'credit' ? 'earn' : 'spend',
                source: change.id,
                amount: change.amount,
            });
        });
        this._powerups = new PowerupController(this._generationConfig, {
            wallet: this._recordingWallet,
            analytics: context.analytics,
            // Module 90 — Wild Gates' price is held while it is open and unpaid.
            pendingCharge: () => (this._state ? openUnpaidPowerups(this._state) : []),
        });
        this._enabled = context.remoteConfig.get(REMOTE_CONFIG_KEYS.ENABLED, true);
        // Module 96 (WP-96.5) — thank the player for each ad break they sat through.
        this._unsubscribeAdBreakThanks =
            context.host?.adBreakThanks?.subscribe(() => this._onAdBreakWatched()) ?? null;
        // Module 137 — the HOST credits a bought pack (outside this plugin's
        // wallet), so the flight is queued here, right after its credit, from the
        // Shop's coin pile. No coins_changed here: the host already logged it.
        this._unsubscribeCoinPackGrants =
            context.host?.coinPack?.subscribeGrants((coins) => {
                this._holdCoinPackFlight();
                this._coinMotion.push({
                    kind: 'credit',
                    currency: 'coins',
                    amount: coins,
                    id: IAP_COIN_PACK_SOURCE_ID,
                });
            }) ?? null;
        const saveModule = context.save.getModule(SAVE_KEYS.BLOCKJAM_PROGRESS);
        const saved = saveModule.load();
        // Always generate a fresh board at the saved level number — never restore mid-game state
        // to avoid "duplicate board" artifacts from stale serialized block positions.
        // Module 65 (WP-65.B1) — a track and a position in it, not a level number.
        //
        // WP-65.G2 (user decision 2026-09-03) — every entry lands on the home
        // screen, not only a save that names no track. Auto-resume made "do I see
        // the home screen?" depend on where the player last exited, which read as
        // random; the saved track still drives the card's "Puzzle N" and the board
        // built below (so the renderer has something real behind the scrim), and
        // resuming is the one tap on that card.
        //
        // Module 116 (D1, user decision 2026-10-12) — EXCEPT a player who has never
        // opened home: they open straight into their Beginner puzzle, because ~43%
        // of first-time players left during the home tour before ever seeing a
        // board. No save at all is such a player; a save without the field
        // predates Module 116 and so belongs to someone who has seen home.
        this._trackProgress = saved?.tracks ?? freshTrackProgress();
        this._homeOpened = saved ? (saved.homeOpened ?? true) : false;
        this._bootedIntoPuzzle = !this._homeOpened;
        this._seenHomeCoach = saved?.seenHomeCoach ?? false;
        this._settingsOpened = saved?.settingsOpened ?? false;
        this._avatar = saved?.avatar ?? defaultAvatarState();
        this._avatarGoal = saved?.avatarGoal ?? null;
        this._lastSeenAvatarWave = saved?.lastSeenAvatarWave ?? 1;
        this._welcomeGift = saved?.welcomeGift ?? false;
        this._seenSignInNudge = saved?.seenSignInNudge ?? false;
        this._languageLineAnswered = saved?.languageLineAnswered ?? false;
        this._languageLineShown = false;
        this._earlyMilestones = saved?.earlyMilestones ?? [];
        this._freeCoinsReadyAt = saved?.freeCoinsReadyAt ?? 0;
        this._needsTrackSelection = this._homeOpened;
        this._track = saved?.currentTrack ?? 'beginner';
        // Module 87 — a boot starts every track's walk afresh (as a reload always
        // has); the routes found in earlier sessions come back from the save.
        this._cancelLookahead();
        this._ahead.clear();
        this._dailyPrep = null;
        this._routeCache = new RouteCache(saved?.routeCache);
        this._ordinal = this._trackProgress[this._track]?.ordinal ?? 1;
        // undefined difficulty derives it from levelNumber (correct for a resumed save at
        // any level) — passing 'Easy' here would have silently overridden a genuine
        // resume at a higher level, since the generator can no longer tell that apart
        // from an explicit 'Easy' request (see WP-BJ.SLOW).
        this._seenUnlocks = saved?.seenUnlocks ?? [];
        // WP-43.5 — one-way migration of WP-41.8's boolean. A legacy `true` means the
        // level-1 hand, and nothing else, has been shown.
        this._seenTutorials = migrateSeenTutorials(saved);
        // WP-48.10 — absent on every save written before Module 48, which is the
        // right default: the permanent goal line those players used to read on every
        // level was deleted in the same module, so they have not seen the chip that
        // replaced it either and should get the one-time introduction.
        this._seenGoalIntro = saved?.seenGoalIntro ?? false;
        // WP-52.1 — absent on every save written before Module 52. `false` is the
        // right default rather than merely the compatible one: the step it gates was
        // reachable so unreliably (see the field's own doc) that an existing save
        // saying nothing is far more likely to be a player who never saw it than one
        // who did. Showing it once more costs a dismissed pill; defaulting the other
        // way would permanently withhold it from exactly the players who were hit by
        // the bug this fixes.
        this._seenLanguageIntro = saved?.seenLanguageIntro ?? false;
        this._seenPoweredHelp = saved?.seenPoweredHelp ?? false;
        this._explainedPowerups = saved?.explainedPowerups ?? [];
        this._howToSeen = saved?.howToSeen ?? [];
        this._seenFeatureIntros = saved?.seenFeatureIntros ?? [];
        this._howToRewarded = saved?.howToRewarded ?? false;
        this._poweredBoardsSeen = saved?.poweredBoardsSeen ?? 0;
        this._seenHomeTour = saved?.seenHomeTour ?? false;
        this._homeTourOwed = homeTourEligible({
            seenHomeTour: this._seenHomeTour,
            bootedIntoPuzzle: this._bootedIntoPuzzle,
        });
        // Module 67 (WP-67.C3) — already repaired by the validator; a record for a
        // PAST date is kept as-is (its streak pair is tomorrow's input) and goes
        // stale naturally the moment `enterDaily` compares dates.
        this._daily = saved?.daily;
        // Module 118 (WP-118.2) — repaired by the validator; absent = never claimed / no progress.
        this._dailyLogin = saved?.dailyLogin ?? EMPTY_DAILY_LOGIN;
        this._quests = saved?.quests ?? EMPTY_QUESTS;
        // WP-50.25 — absent on every save written before Module 50. Defaulting to 0
        // means an existing player's already-cleared levels pay once more, each, on
        // their way back up. That is the right default and not merely the easy one:
        // there are no existing players (the game has never launched), and the
        // alternative — assuming `levelId - 1` levels were paid for — would silently
        // withhold a reward from anyone whose save this cannot actually speak for.
        // Module 63 (WP-63.E5) — the free-use grant is once EVER: what a player has
        // consumed comes back from the save, never from a fresh counter. WP-65.F2 —
        // per track, so it is the current track's record.
        this._powerups.restoreFreeUses(this._trackProgress[this._track]?.powerupFreeUsed);
        // Module 144 (WP-144.1) — a save with no confirm counts belongs to a player
        // from before the field: whoever already spent free uses has met the card,
        // so each id starts from the most free uses used on any track. In memory
        // only — the next ordinary `_commitState()` writes it.
        this._powerupConfirms = saved?.powerupConfirms ?? this._seedPowerupConfirms();
        // Module 118 — the day-7 tickets: player-wide, so not part of the per-track restore.
        this._powerups.restoreBonusFreeUses(saved?.bonusFreeUses);
        // Module 132 (WP-132.1) — a save from before milestones starts them from
        // what it already proves. In memory only — the next `_commitState()` writes it.
        this._milestones = saved?.milestones ?? this._seedMilestones(saved);
        // Module 131 (WP-131.10) — each track's Reach badge starts from the furthest
        // puzzle that track proves (a save from before the per-track badges). A
        // track never played proves nothing: its default "puzzle 1" is not a start.
        this._milestones = withTrackReach(this._milestones, Object.fromEntries(TRACK_IDS.map((id) => {
            const p = this._trackProgress[id];
            const played = (p?.ordinal ?? 1) > 1 || this._maxPaidOrdinal(id) > 0;
            return [id, played ? Math.max(p?.ordinal ?? 0, this._maxPaidOrdinal(id)) : 0];
        })));
        this._lineBase = { ...this._milestones.counters };
        this._lastTitle = buildMilestonesView(this._milestones, this._quests).title;
        // Module 133 (WP-133.1) — today's missions: the saved set, or a new day's
        // draw once Missions are unlocked. In memory only — the next commit writes it.
        this._missions = saved?.missions ?? null;
        this._winStreak = saved?.winStreak ?? EMPTY_WIN_STREAK;
        this._rollMissions();
        // A save written by the single-axis model carries only a level number, which
        // is all we use: boards are regenerated, never restored (locked decision 4),
        // so an old save resumes on the new model instead of crashing on stale block
        // shapes.
        const fromFactory = this._initialStateFactory?.();
        if (fromFactory) {
            this._state = fromFactory;
        }
        else {
            // Module 83 (WP-83.5b) — boot may await the worker's route (an endless or
            // out-of-table resume board); `initialize` is async, so no HUD state.
            this._boardIsDaily = false;
            const built = await this._prepareOrdinal(this._ordinal);
            this._state = this._adoptTrackLevel(built, built.state.puzzle.levelNumber);
        }
        if (fromFactory) {
            // A factory board was never adopted, so there is no built board for
            // Restart to re-adopt — it regenerates, as before.
            this._currentBuilt = null;
            // Module 65 — a factory board OVERRIDES the saved position.
            //
            // The existing comment below already said a factory "is free to hand back
            // a different level than the save named", and before tracks that was
            // harmless: `_nextLevel` derived the next level from the board it was
            // holding, so the save was simply ignored. Now the ordinal drives the
            // advance, and leaving it on the saved value makes a fixture board
            // announce level 1 while the plugin thinks it is on puzzle 8 — which is
            // how `bj-pregeneration`'s shared save state became visible.
            //
            // Following the board is the honest reading: whoever injected it decided
            // where the game is.
            this._ordinal = this._ordinalForLevel(fromFactory.puzzle.levelNumber);
        }
        // Idempotent, and it takes the level from the BOARD — the factory path (tests,
        // fixtures) never runs `_generateLevel`, and a factory is free to hand back a
        // different level than the save named.
        // Module 129 — `startLevel` clears the attempt's uses; a resumed board's come back.
        const resumedUses = this._powerups.usedThisLevel();
        this._powerups.startLevel(this._state.puzzle.levelNumber, this._ordinal, this._unlockTrack());
        this._powerups.restoreUsedThisLevel(resumedUses);
        context.analytics.logEvent(ANALYTICS_EVENTS.PLUGIN_INITIALIZED, { enabled: this._enabled });
        context.analytics.logEvent(ANALYTICS_EVENTS.LEVEL_STARTED, {
            // Still the EFFECTIVE level: every consumer of this event (the embed's ad
            // pacing, the analytics schema) was built against a difficulty coordinate,
            // and the ordinal is a display number. `track` and `puzzle` are additive.
            level: this._state.puzzle.levelNumber,
            track: this._track,
            puzzle: this._ordinal,
            // Module 73 (WP-73.8b) — how the puzzle was entered, and whether it
            // opens on a lesson. The embed places a midgame ad only on `entry:
            // 'next'` with no lesson; boot and track entry are never natural breaks.
            // Module 116 — `boot` is a board parked behind home (nobody plays it
            // yet); `firstVisit` is a real start, straight onto the board.
            entry: this._needsTrackSelection ? 'boot' : 'firstVisit',
            lesson: this._lessonPending(),
        });
        // Module 95 — boot lands on the home screen. A generated boot board already
        // scheduled this from `_adoptLevel`'s pump; a factory board did not.
        // (A first-visit boot is not on home, so this is a no-op until home opens.)
        this._prewarmDaily();
    }
    /**
     * Module 73 (WP-73.8b) — does the current board open on a lesson (an unlock
     * popup queued, or a coached tutorial moment owed)? The same two checks
     * `_maybeShowTutorial` makes, asked without acting on them.
     */
    _lessonPending() {
        if (this._dailyMode || !this._state)
            return false;
        if (this._unlockQueue.length > 0)
            return true;
        return (pendingTutorial(this._generationConfig, this._state, this._seenTutorials, this._ordinal, this._tutorialOpts()) !== null);
    }
    /**
     * Module 77 (D4) — the track the power-up unlock rule is asked about
     * (`unlockPuzzleFor`). None in the Daily, which has no power-ups and
     * borrows a track only for its difficulty.
     */
    _unlockTrack() {
        return this._dailyMode ? undefined : this._track;
    }
    _boardKey() {
        return `${this._dailyMode ? 'daily' : this._track}:${this._ordinal}`;
    }
    /**
     * Module 77 (WP-77.10, user 2026-09-28) — the explainer opens by itself once
     * ever, on the SECOND powered board the player meets: the first carries the
     * plate lesson, and two teaching layers on one board is what this avoids.
     * "Second" = a powered board after the plate lesson has been seen, and not
     * the board that lesson ran on this session (so a restart there is not it).
     */
    _owesPoweredHelp() {
        if (this._seenPoweredHelp)
            return false;
        if ((this._state?.puzzle.plates?.length ?? 0) === 0)
            return false;
        if (!this._seenTutorials.includes('poweredPlate'))
            return false;
        if (this._poweredLessonShownOn === this._boardKey())
            return false;
        return this._tutorial === null;
    }
    /** Module 77 (D6) — everything the HUD shows about track readiness. */
    _guidanceProps() {
        const cfg = this._generationConfig;
        const progress = this._trackProgress;
        const trackReadiness = Object.fromEntries(TRACK_IDS.map((id) => [id, cardReadiness(cfg, id, progress)]));
        return {
            trackReadiness,
            moveUpOffer: this._moveUpOffer,
            onAcceptMoveUp: () => this.acceptMoveUp(),
            trackEntry: this._trackEntry,
            onDismissTrackEntry: () => this.dismissTrackEntry(),
        };
    }
    _tutorialOpts() {
        return {
            track: this._unlockTrack(),
            powerupLessons: this._powerupLessonShownOn !== `${this._track}:${this._ordinal}`,
        };
    }
    async start(containerEl) {
        // Module 83 (WP-83.11) — the QA jump, promised with the budget audit:
        // `blockwayQA.jump('expert', 7)` from the console. Play-only, no save.
        const qa = globalThis.blockwayQA ?? {};
        globalThis.blockwayQA = {
            ...qa,
            jump: (track, n) => {
                if (this._track !== track)
                    this.selectTrack(track);
                this._trackProgress[track] = {
                    ...this._trackProgress[track],
                    ordinal: Math.max(this._trackProgress[track]?.ordinal ?? 1, n),
                };
                this.jumpToPuzzle(n);
            },
            // The Reveal page on the current board, unpaid — for QA captures only
            // (anything a console can do, a save edit could do already).
            revealPage: () => {
                const route = this._revealRoute();
                const payment = this._revealPayment();
                if (route && payment)
                    this._openRevealPage(route, payment, false);
            },
        };
        if (!this._context)
            return;
        this._sessionStartMs = this._context.clock.now();
        if (!this._enabled) {
            this._context.analytics.logEvent(ANALYTICS_EVENTS.PLUGIN_STARTED, { enabled: false });
            return;
        }
        if (this._boardOverride) {
            this._board = this._boardOverride;
        }
        else {
            // Lazy so PixiJS is only pulled in when a board is actually shown.
            const { FreeSlidePlayer } = await import('./renderer/free-slide-player');
            // Module 83 — kept for the Reveal page's second board (same dynamic import).
            this._boardClass = FreeSlidePlayer;
            if (!this._context)
                return; // destroyed mid-import
            this._board = new FreeSlidePlayer(this._state, {
                reducedMotion: this._reducedMotionFn(),
                // Omitted (undefined) when no host config was injected, so the board
                // keeps its own shipped default rather than being handed a null.
                ...(this._visualConfig ? { visuals: this._visualConfig } : {}),
                ...(this._boardVisualConfig ? { boardVisuals: this._boardVisualConfig } : {}),
                // WP-44.1 — the shared tutorial-hand image. Omitted (not passed as
                // undefined) when the host injected nothing, matching the two above.
                ...(this._assets?.tutorialHandUrl ? { tutorialHandUrl: this._assets.tutorialHandUrl } : {}),
                onMove: (info) => this.handleBoardMove(info),
                onGuidedMoved: () => this._renderHud(),
                onTargetPicked: (blockId) => this.handleTargetPicked(blockId),
                onGateTargetPicked: (gateKey) => this.handleGateTargetPicked(gateKey),
                onTargetCancelled: () => this.handleTargetCancelled(),
                // WP-37.8 — the interaction beats the cues ride. Each one forwards to a
                // public handler so the wiring is testable without a canvas, exactly as
                // `handleBoardMove` has been since WP-35.16.5.
                onBlockSelected: (blockId) => this.handleBlockSelected(blockId),
                onBlockReselected: (blockId) => this.handleBlockReselected(blockId),
                onBlockHovered: (blockId) => this.handleBlockHovered(blockId),
                onSelectionCleared: () => this.handleSelectionCleared(),
                onBlockExited: (blockId) => this.handleBlockExited(blockId),
                onMoveBlocked: () => this.handleMoveBlocked(),
                onGhostHandDismissed: () => this.handleGhostHandDismissed(),
                // Module 63 (WP-63.C4) — the powered circuit's four animation beats.
                onGateOpened: () => this.handleGateOpened(),
                onGateClosed: () => this.handleGateClosed(),
                onPowerRefused: () => this.handlePowerRefused(),
                onTracePulsed: () => this.handleTracePulsed(),
                // Module 102 — the board could not get its WebGL context back.
                onRenderLost: (lost) => this.handleBoardRenderLost(lost),
            });
        }
        this._containerEl = containerEl;
        this._bindPointerTracking(containerEl);
        const gridSize = this._state?.puzzle.gridSize ?? 5;
        this._canvasLogical = gridSize * CELL_SIZE + 2 * FRAME_BORDER;
        // WP-65.B3 — the ordinal, for the same reason the music uses it: each track
        // gets its own art sequence from Puzzle 1.
        this._applyBgTheme(containerEl, this._ordinal);
        this._applyPowerupBarReserve();
        // Before the canvas mounts, so the very first board is laid out against the
        // reserve rather than resized into it (WP-45.7 learned the same lesson about
        // publishing geometry only on later pushes).
        await this._board.mount(containerEl);
        if (!this._context) {
            // destroyed while mounting canvas
            this._board.destroy();
            this._board = null;
            return;
        }
        if (this._state)
            this._board.setBoard(this._state);
        // WP-45.7 — the mount path sets the board directly rather than through
        // `_renderBoard`, so publishing only there left the very first level (the
        // one every player sees) with no geometry published at all. Found by
        // measuring a tablet viewport and getting the fallback.
        this._publishBoardGeometry();
        this._watchBoardResolution();
        this._watchBoardTop(containerEl);
        if (typeof window !== 'undefined' && wantsLayoutReadout(window.location.search)) {
            const stop = mountLayoutReadout();
            const prev = this._boardTopWatch;
            this._boardTopWatch = () => {
                prev?.();
                stop();
            };
        }
        // Module 66 — the home screen's ground is the bare pool background, so the
        // parked board must be hidden the moment it exists (before the HUD's first
        // paint), not on the first later re-render.
        this._syncBoardParked();
        // Mount React HUD overlay — appended to document.body so its React root
        // is independent of the outer app React tree (avoids React 19 DOM conflicts).
        if (typeof document !== 'undefined') {
            this._hudEl = document.createElement('div');
            this._hudEl.className = 'bj-hud-root';
            document.body.appendChild(this._hudEl);
            // Module 120 — Block Jam takes the UI scale (`--bj-s`, ui.css) only while
            // its HUD is on the page. An attribute, not `:root:has(.bj-hud-root)`: a
            // root `:has()` made every style recalculation dearer (resize to upright
            // measured ~100ms slower).
            document.documentElement.setAttribute('data-bj-scaled', '1');
            // Lazy import to avoid pulling react-dom into test bundles
            const { mountHud } = await import('./ui/mount-hud');
            if (!this._context) {
                // destroyed while loading HUD module
                if (this._hudEl.parentElement)
                    this._hudEl.parentElement.removeChild(this._hudEl);
                this._hudEl = null;
                this._board?.destroy();
                this._board = null;
                return;
            }
            // Module 126 (D14) — the HUD's first draw goes through here, not
            // `_renderHud`, and a quiet home screen may never redraw: so a player who
            // arrives signed in (after the sign-in reload) gets the gift on this draw.
            this._maybeWelcomeGift();
            this._hudRoot = mountHud(this._hudEl, this._getHudProps());
            if (this._showPerfOverlay) {
                this._perf = new PerfOverlay();
                this._perf.mount(document.body);
            }
        }
        // WP-41.8 — after the board and HUD are both up, so the hand and its caption
        // appear together, and after `setBoard`, which clears any ghost.
        //
        // WP-65.G2 — everything below that ANNOUNCES A BOARD is gated on the home
        // screen not being up, and the home screen is up on every entry except a
        // first visit (Module 116), which opens straight onto the board and so gets
        // the tutorial, the level cue and the puzzle music right here. The
        // tutorial hand, the level cue and the music all belong to entering a
        // track, and `selectTrack()` fires each of them; playing them here would
        // put a ghost hand and a score under a chooser nobody has answered yet —
        // which is also the Back path's contract, since D13 ends the music on the
        // way back to this screen.
        if (!this._needsTrackSelection)
            this._maybeShowTutorial();
        // Module 116 — a first visit's board is playable from here.
        if (!this._needsTrackSelection)
            this._noteBoardShown();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.PLUGIN_STARTED, { enabled: true });
        this._context.analytics.logEvent(ANALYTICS_EVENTS.SESSION_STARTED, {});
        // WP-37.8 — the level is on screen. Deliberately here and not in
        // `initialize()`, which runs while the Lobby is still showing: a cue that
        // announces a board must not play before the board exists.
        if (!this._needsTrackSelection)
            this._playCue(BlockJamAudioId.LEVEL_START);
        // WP-37.13 — and the music, on the same beat and for the same reason. This
        // is also what makes the track LAZY (T3): its file is fetched by the first
        // `play()`, so the Lobby never pays for it. The visibility watch stays
        // unconditional: it guards backgrounding for the whole plugin lifetime,
        // home screen included.
        this._watchVisibility();
        // Module 129 (WP-129.3) — a pending move save is written before the page goes.
        this._watchPageExit();
        // Module 72 (WP-72.B3) — unconditional now: on the home screen this starts
        // the home track (`_trackForCurrentLevel` picks it), where it used to leave
        // the screen silent.
        this._startMusic();
    }
    /**
     * Module 102 — the board reports that it could not bring its picture back
     * (`true`), or that it has (`false`). Public, like the other board
     * callbacks, so it is testable without a canvas.
     */
    handleBoardRenderLost(lost) {
        if (this._boardRenderLost === lost)
            return;
        this._boardRenderLost = lost;
        if (this._hudRoot)
            this._renderHud();
    }
    /**
     * Module 126 (WP-126.3) — the Profile panel's numbers. Track puzzles solved
     * count the same way milestones seed them (paid wins minus skipped and
     * revealed), so the Profile and the badges never disagree.
     */
    _profileStats() {
        const perTrack = Object.fromEntries(TRACK_IDS.map((id) => {
            const p = this._trackProgress[id];
            return [id, Math.max(0, this._maxPaidOrdinal(id) - (p?.skipped ?? 0) - (p?.revealed ?? 0))];
        }));
        return {
            totalSolved: Object.values(perTrack).reduce((a, b) => a + b, 0),
            perTrack,
            dailyStreak: this._daily?.streak ?? 0,
            dailiesSolved: this._milestones.counters.dailies_cleared ?? 0,
            // Module 152 (WP-152.3) — "Avatars n / m" (m = released so far).
            avatars: avatarCollection(this._avatar, this._avatarWaveLive()),
        };
    }
    /**
     * Module 126 (WP-126.3) — one sign-in attempt from a player's tap, with its
     * analytics. The account view never rejects and settles within its guard
     * (WP-126.1); a cancel or failure changes nothing on screen (D3). The reload
     * that loads the account's save is WP-126.5's.
     */
    async _signIn(from) {
        const signIn = this._context?.host?.account?.signIn;
        if (!signIn)
            return;
        const analytics = this._context?.analytics;
        analytics?.logEvent(ANALYTICS_EVENTS.SIGN_IN_STARTED, { from });
        const result = await signIn();
        analytics?.logEvent(result === 'signed_in'
            ? ANALYTICS_EVENTS.SIGN_IN_COMPLETED
            : result === 'cancelled'
                ? ANALYTICS_EVENTS.SIGN_IN_CANCELLED
                : ANALYTICS_EVENTS.SIGN_IN_FAILED, { from });
        // D11 — a signed-in tap owes the account's save: loaded at a safe moment.
        if (result === 'signed_in' && this._context?.host?.account?.loadAccountSave) {
            this._accountLoadOwed = true;
            // Already on home: hand over now, not on whenever the HUD next draws.
            if (this._needsTrackSelection)
                this._loadAccountNow();
        }
        if (this._hudRoot)
            this._renderHud();
    }
    /**
     * Module 126 (WP-126.5) — commit this device's state through the one writer,
     * then let the host load the account's save (it copies this save first, and
     * may reload the page). Once per sign-in.
     */
    _loadAccountNow() {
        this._accountLoadOwed = false;
        this._accountHandedOver = true;
        this._commitState();
        this._context?.host?.account?.loadAccountSave?.();
    }
    /**
     * Module 126 (WP-126.6, D14) — the Welcome gift: +250 coins and Nimbus, once
     * ever per save, to a player SIGNED IN on a host with in-game sign-in.
     *
     * Never while the save under the session may still change: not while a
     * sign-in's account load is owed or under way (the page is about to reload
     * into the account's store, and a gift written now would land in the guest
     * save), and not while the keep-which-progress choice is open. So it lands
     * in the save that is actually kept. The flag is set BEFORE the credit (the
     * How to Play rule): a credit that throws is lost, never paid twice.
     */
    _maybeWelcomeGift() {
        if (this._welcomeGift || this._accountLoadOwed || this._accountHandedOver)
            return;
        const account = this._context?.host?.account;
        if (!this._context || !this._state || !account?.signIn)
            return;
        const signedIn = account.current();
        if (!signedIn || account.pendingChoice?.())
            return;
        this._welcomeGift = true;
        if (!this._avatar.owned.includes(WELCOME_AVATAR_ID)) {
            this._avatar = {
                chosen: WELCOME_AVATAR_ID,
                owned: [...this._avatar.owned, WELCOME_AVATAR_ID],
            };
        }
        let paid = false;
        try {
            this._wallet.credit('coins', WELCOME_GIFT_COINS, WELCOME_GIFT_SOURCE_ID);
            paid = true;
        }
        catch {
            // Refused locally: keep the flag and the avatar, never loop.
        }
        this._commitState();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.WELCOME_GIFT_GRANTED, {
            coins: paid ? WELCOME_GIFT_COINS : 0,
        });
        this._welcomeGiftCard = { seq: (this._welcomeGiftCard?.seq ?? 0) + 1, name: signedIn.username };
    }
    /** Module 126 (D7) — a guest at 10 solves, on a host with sign-in, who has never seen it. */
    _signInNudgeOwed() {
        const account = this._context?.host?.account;
        return (this._puzzleStartedThisSession &&
            !this._seenSignInNudge &&
            Boolean(account?.signIn) &&
            (account?.current() ?? null) === null &&
            this._profileStats().totalSolved >= PROFILE_IN_PUZZLE_SOLVES);
    }
    /**
     * Module 126 (WP-126.5) — the keep-which-progress choice for the HUD, when the
     * host has one: each side summarized from its raw save.
     */
    _saveChoiceView() {
        const choice = this._context?.host?.account?.pendingChoice?.() ?? null;
        if (!choice)
            return undefined;
        return {
            device: { ...summarizeSave(choice.device.progress), coins: choice.device.coins },
            account: { ...summarizeSave(choice.account.progress), coins: choice.account.coins },
            onChoose: (side) => {
                choice.choose(side);
                if (this._hudRoot)
                    this._renderHud();
            },
        };
    }
    /** Module 126 (WP-126.2) — the player's avatar state, as saved. */
    get avatar() {
        return this._avatar;
    }
    /** Module 152 (WP-152.3) — the coin avatar the player is saving for, or null. */
    get avatarGoal() {
        return this._avatarGoal;
    }
    /** Module 152 (WP-152.3) — released coin avatars of a wave the player hasn't been shown. */
    get newAvatarIds() {
        return newAvatarIds(this._lastSeenAvatarWave, this._avatarWaveLive());
    }
    /**
     * Module 152 (WP-152.3) — the released wave, from the registry. A method so a
     * test can stand in a later wave without a registry edit.
     */
    _avatarWaveLive() {
        return ECONOMY.avatars.waveLive;
    }
    /**
     * Module 152 (WP-152.3) — the player has now been shown the newest wave (the
     * home card, or the picker with its NEW chips). Once per wave: one commit,
     * one event; nothing when there is nothing new.
     */
    markAvatarWaveSeen(from) {
        const wave = this._avatarWaveLive();
        if (wave <= this._lastSeenAvatarWave)
            return;
        this._lastSeenAvatarWave = wave;
        this._commitState();
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.AVATAR_WAVE_SEEN, { wave, from });
    }
    /**
     * Module 152 (WP-152.3, V8) — the one home card for a new wave: owed while a
     * newer wave is unseen, and only once this session has been into a puzzle,
     * so it never greets a player at launch (Playgama 1.2.2's spirit) and never
     * opens at a puzzle's start (Economy rule 9: the HUD shows it on home only).
     */
    _avatarWaveCardOwed() {
        if (!this._puzzleStartedThisSession)
            return null;
        const ids = this.newAvatarIds;
        return ids.length > 0 ? { count: ids.length, ids } : null;
    }
    /** Module 152 (WP-152.3) — the goal for the HUD: home chip and win-popup line. */
    _avatarGoalView() {
        const id = this._avatarGoal;
        if (!id || !isUnlockable(this._avatar, id, this._avatarWaveLive()))
            return null;
        return { id, price: avatarPrice(id) };
    }
    /**
     * Module 152 (WP-152.3) — "Set as my goal" (or, with null, clear it). Only an
     * avatar that could be unlocked now (released, for coins, not owned) can be a
     * goal; anything else changes nothing. One commit; never in a puzzle's way
     * (the goal shows on home and as one line on the win popup only).
     */
    setAvatarGoal(id) {
        if (id !== null && !isUnlockable(this._avatar, id, this._avatarWaveLive()))
            return false;
        if (id === this._avatarGoal)
            return true;
        this._avatarGoal = id;
        this._commitState();
        if (id !== null) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.AVATAR_GOAL_SET, {
                avatar: id,
                price: avatarPrice(id),
                coins: this._wallet.getBalance('coins'),
            });
        }
        if (this._hudRoot)
            this._renderHud();
        return true;
    }
    /**
     * Module 126 (WP-126.2) — the picker's tap. Saves at once through the one
     * writer (`_commitState`, module rule 3) and redraws. Returns false, writing
     * nothing, for an avatar this player can't choose (unknown here, not owned).
     */
    chooseAvatar(id) {
        const next = chooseAvatarIn(this._avatar, id);
        if (!next)
            return false;
        if (next === this._avatar)
            return true;
        this._avatar = next;
        this._commitState();
        if (this._hudRoot)
            this._renderHud();
        return true;
    }
    /**
     * Module 152 (WP-152.2) — unlock a coin avatar: one wallet spend at the
     * registry price under `blockjam_avatar_<id>`, then owned AND worn, in ONE
     * commit (Economy rule 3: a reload resumes exactly — either the coins are
     * gone and the avatar is owned, or neither).
     *
     * Idempotent per id: an avatar already owned is never charged again (a
     * double tap's second call answers `'owned'` before the wallet is touched),
     * and the server's `avatarsBought` stamp refuses a second web debit too.
     * Unknown, not-for-coins and not-yet-released ids spend nothing.
     */
    buyAvatar(id, from = 'picker') {
        if (!this._context)
            return 'unavailable';
        if (ownsAvatar(this._avatar, id))
            return 'owned';
        const next = isUnlockable(this._avatar, id, this._avatarWaveLive())
            ? unlockAvatar(this._avatar, id, this._avatarWaveLive())
            : null;
        if (!next)
            return 'unavailable';
        const price = avatarPrice(id);
        if (this._wallet.getBalance('coins') < price)
            return 'short';
        const debit = this._wallet.debit('coins', price, avatarReasonId(id));
        if (!debit.ok)
            return 'short';
        this._avatar = next;
        // WP-152.3 — a goal reached is a goal done.
        if (this._avatarGoal === id)
            this._avatarGoal = null;
        // "Unlock your first avatar" (a one-off badge); rides the commit below.
        this._milestoneEvent({ type: 'avatarUnlocked' }, false);
        this._commitState();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.AVATAR_PURCHASED, {
            avatar: id,
            tier: avatarTierName(id) ?? 'unknown',
            price,
            from,
        });
        if (this._hudRoot)
            this._renderHud();
        return 'ok';
    }
    /**
     * Module 102 — "Tap to reload the board". A fresh renderer first: the puzzle
     * lives on the board object, so this costs the player nothing. If the
     * browser still refuses WebGL, only a page reload can get it back — the save
     * (`_commitState`, board state included) is already written, so that is what
     * the tap does next, rather than leaving the player in front of a notice
     * that can never clear.
     */
    reloadBoard() {
        if (this._board?.retryRender?.())
            return;
        if (typeof window !== 'undefined')
            window.location.reload();
    }
    pause() {
        this._inputLocked = false;
        if (!this._hostPaused) {
            this._hostPaused = true;
            if (this._homeTourOwed)
                this._renderHud();
            // Module 150 — an ad or a platform pause covers the board.
            this._syncBoardParked();
        }
        // WP-37.13 — music IS meant to come back: `_musicWanted`
        // stays true so `resume()` can restart it.
        this._stopMusic();
        if (!this._context)
            return;
        this._context.analytics.logEvent(ANALYTICS_EVENTS.PLUGIN_PAUSED, {});
    }
    resume() {
        if (this._hostPaused) {
            this._hostPaused = false;
            if (this._homeTourOwed)
                this._renderHud();
            this._syncBoardParked();
        }
        if (!this._context)
            return;
        // Only what `pause()` suspended (WP-37.13). A `resume()` on a game that was
        // never started, or one already on its way out, must not start a track over
        // a board nobody is looking at.
        if (this._musicWanted)
            this._startMusic();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.PLUGIN_RESUMED, {});
    }
    async destroy() {
        // Module 129 — a move made in the last two seconds is still saved.
        this._flushMoveCommit();
        this._unwatchPageExit();
        this._resolutionWatch?.();
        this._resolutionWatch = null;
        this._boardTopWatch?.();
        this._boardTopWatch = null;
        // Module 110 — nothing unshown outlives the HUD that would have shown it.
        this._coinMotion.clear();
        // Module 96 — no thanks into a torn-down plugin, and no gift-card timer left.
        this._unsubscribeAdBreakThanks?.();
        this._unsubscribeAdBreakThanks = null;
        this._unsubscribeCoinPackGrants?.();
        this._unsubscribeCoinPackGrants = null;
        // Unhooks without rendering a torn-down HUD.
        this._coinPackHold?.();
        this._coinPackHold = null;
        if (this._adGiftTimer !== null)
            clearTimeout(this._adGiftTimer);
        this._adGiftTimer = null;
        // Kill the win sequence first (WP-36.5): the pending timer must not fire
        // against a torn-down plugin, and `_isCelebrating = false` is what stops the
        // in-flight `playWinFx` promise from showing a popup on a dead HUD.
        this._cancelCelebration();
        // Same reasoning for a level still being built at idle: it may not outlive
        // the plugin.
        this._cancelLookahead();
        // …and today's Daily being prepared (Module 95): the idle build is
        // cancelled, and a route landing later is dropped by the token.
        this._dailyPrepCancel?.();
        this._dailyPrepCancel = null;
        this._dailyPrepToken += 1;
        this._pointerListeners?.();
        this._pointerListeners = null;
        // …and the locked-power-up notice (WP-45.3), for exactly the same reason:
        // its timer calls `_renderHud()`, and the HUD root is unmounted below.
        this._clearLockedNotice();
        // …and the "no ad available" retry (WP-51.4), which is the same shape again.
        this._clearDoubleCoinsRetry();
        // …and the home Free Coins "No ad now" (Module 123), the same shape.
        this._clearFreeCoinsRetry();
        // …and the Hint button's tip and notice timers (Module 81).
        this._clearRevealTimers();
        // …and the track entry card (WP-78.3), whose timer re-renders the HUD.
        this._trackEntry = null;
        // …and the music (WP-37.13), which is the loop with the longest reach: it
        // outlives the board unless something stops it. `_musicWanted` goes false
        // first, so a `visibilitychange` racing the teardown cannot restart it.
        this._endMusic();
        // Remove drag listeners
        if (this._canvasEl) {
            if (this._pointerDownHandler)
                this._canvasEl.removeEventListener('pointerdown', this._pointerDownHandler);
            if (this._pointerMoveHandler)
                this._canvasEl.removeEventListener('pointermove', this._pointerMoveHandler);
            if (this._pointerUpHandler) {
                this._canvasEl.removeEventListener('pointerup', this._pointerUpHandler);
                this._canvasEl.removeEventListener('pointercancel', this._pointerUpHandler);
            }
            this._pointerDownHandler = null;
            this._pointerMoveHandler = null;
            this._pointerUpHandler = null;
            this._canvasEl = null;
        }
        // Unmount HUD
        if (this._hudRoot) {
            // Through `disposeHud` so `mount-hud` stops tracking it — see the
            // `liveRoots` note there. The root is captured first because the import
            // resolves after `this._hudRoot` has been nulled.
            const root = this._hudRoot;
            void import('./ui/mount-hud').then(({ disposeHud }) => {
                disposeHud(root);
            });
            this._hudRoot = null;
        }
        if (this._hudEl?.parentElement) {
            this._hudEl.parentElement.removeChild(this._hudEl);
            this._hudEl = null;
        }
        if (typeof document !== 'undefined')
            document.documentElement.removeAttribute('data-bj-scaled');
        // Takes its own rAF loop down with it (WP-36.11).
        this._perf?.destroy();
        this._perf = null;
        // WP-44.2 — hand back the layout signal. WP-41.15 mirrored
        // `data-powerup-bar` onto `<html>` to reach the HUD's sibling root, and
        // `<html>` outlives this plugin: nothing else ever removed it, so the
        // attribute stayed stamped on the document after the player left for the
        // lobby. It was harmless while every selector using it also needed a
        // Block Jam element that had gone with the game — but it is Block Jam state
        // left on the platform's root node, and since WP-44.2 it is left there by
        // every session rather than only by ones past level 20.
        this._clearPowerupBarReserve();
        if (!this._context)
            return;
        const duration = this._context.clock.now() - this._sessionStartMs;
        this._context.analytics.logEvent(ANALYTICS_EVENTS.SESSION_ENDED, { duration });
        this._context.analytics.logEvent(ANALYTICS_EVENTS.PLUGIN_DESTROYED, {});
        // Persist progress so re-entry resumes at the current level.
        this._commitState();
        this._board?.destroy();
        this._board = null;
        this._state = null;
        this._context = null;
    }
    // ─── Game Loop ────────────────────────────────────────────────────────────
    handleTap(canvasX, canvasY) {
        if (this._inputLocked)
            return;
        if (canvasX < SWIPE_GUARD_PX)
            return;
        const block = this._hitTestBlock(canvasX, canvasY);
        if (block) {
            this._selectedBlockId = this._selectedBlockId === block.id ? null : block.id;
            this._renderBoard();
            return;
        }
        if (this._selectedBlockId) {
            const selected = this._state?.blocks.find((b) => b.id === this._selectedBlockId);
            if (!selected)
                return;
            const direction = this._slideDirection(selected, canvasX, canvasY);
            if (direction)
                void this.move(this._selectedBlockId, direction);
        }
    }
    /**
     * Apply one move programmatically (hints, tests, power-ups later). Player input
     * does NOT come through here — the board owns the drag and reports it through
     * `_onBoardMove` — but both paths end in the same `_afterMove` bookkeeping.
     */
    async move(blockId, direction) {
        if (!this._state || this._inputLocked)
            return;
        // Module 76 (D2) — a move while a paid confirm is up cancels the offer, the
        // same way a board tap does. A drag cannot get here (the board is in
        // no-target targeting); this covers the programmatic path.
        if (this._armedPowerupId && activationFor(this._armedPowerupId) === 'immediate') {
            this.cancelArm();
        }
        // WP-50.27 — the same options the board resolves a drag with. Without this
        // the programmatic path would refuse an exit the player can see happening
        // under an open rainbow, which is AUD-010's shape all over again: two
        // callers composing the same primitives with different arguments.
        // Module 63 — derived in ONE place now: rainbow, surge and the power set.
        // Module 74 — kept, because `surgeSpentBy` must be asked with the SAME
        // options the exit was decided with.
        // Module 75 (D4) — and the plates, so power is judged where the mover
        // stands at each step, as the drag and the solver judge it. With only the
        // pre-move set, a block starting on its own plate elsewhere read its gate
        // as lit all the way there.
        const exitOpts = boardExitOptions(this._state);
        const before = this._state.blocks;
        const result = applyFreeMove(before, { blockId, direction }, this._state.puzzle.exits, this._state.puzzle.gridSize, exitOpts, this._state.puzzle.plates);
        if (!result) {
            this._playCue(BlockJamAudioId.INVALID_MOVE);
            return;
        }
        this._pushHistory();
        this._state = {
            ...this._state,
            blocks: result.blocks,
            moveCount: this._state.moveCount + 1,
        };
        this._noteGatesPowered(before);
        // After the move count is taken, so `moves_open` counts this move.
        // Module 90 (D3) — only an exit that USED Wild Gates ends it: the same
        // predicate, with the same options, the board asks at a drag's exit.
        const exited = result.exitedId ? before.find((b) => b.id === result.exitedId) : undefined;
        if (exited && wildSpentBy(result.exitedGate, exited, exitOpts))
            this._endRainbowOnExit();
        // The power set the exit was decided with, mover included (`surgeSpentBy`).
        this._endSurgeIfSpent(result.exitedGate !== null &&
            surgeSpentBy(result.exitedGate, { ...exitOpts, poweredPlateIds: result.powered }));
        this._board?.setBoard(this._state);
        // WP-37.8 — the exit cue for THIS path only. A drag exits through the board,
        // which fires it on the gate flash (`handleBlockExited`); a programmatic move
        // never reaches that code, and `setBoard` shows the result outright, so the
        // cue is sounded here rather than in the shared `_afterMove` — putting it
        // there would double-fire on every drag.
        if (result.exitedId)
            this._playCue(BlockJamAudioId.BLOCK_EXIT);
        this._afterMove(result.exitedId ? [result.exitedId] : []);
        await Promise.resolve();
    }
    // ─── Interaction cues (WP-37.8, re-cut in WP-D.1) ─────────────────────────
    //
    // The board reports the beat; the plugin decides what it sounds like. All of
    // them are public for the same reason `handleBoardMove` is: they are the
    // testable seam, and a canvas is not needed to exercise them.
    //
    // What WP-D.1 changed is WHICH beats there are. They were pointer events with
    // audio names on them — a grab sounded a select, a release sounded a deselect
    // — so clicking a block and letting go played select-then-deselect while the
    // block sat there, still selected. They are now selection-STATE transitions,
    // and a release is not one of them.
    /** The selection changed to a block. Dragging itself stays silent, by
     *  explicit product decision (Audio_Rules.md rule 1). */
    handleBlockSelected(_blockId) {
        this._playCue(BlockJamAudioId.BLOCK_SELECT);
    }
    /**
     * The press landed on the block already selected. Nothing changed, so this is
     * NOT a select — it is the quieter acknowledgement, and the reason the board
     * asks whether the selection moved before it overwrites it.
     */
    handleBlockReselected(_blockId) {
        this._playCue(BlockJamAudioId.BLOCK_RESELECT);
    }
    /** The mouse entered a block. Desktop only — the board never reports a
     *  `touch`/`pen` pointer, so this cannot fire under a finger. */
    handleBlockHovered(_blockId) {
        this._playCue(BlockJamAudioId.BLOCK_HOVER);
    }
    /**
     * The selection genuinely became nothing — a tap on empty board while a block
     * was selected. That is the ONLY deselect the game has: a release leaves the
     * block selected, and the two other ways a selection ends (it exits through a
     * gate; the level is restarted or swapped) already sound their own beat, so
     * adding this on top would break "one cue per action".
     */
    handleSelectionCleared() {
        this._playCue(BlockJamAudioId.BLOCK_DESELECT);
    }
    /** A block cleared its gate, on the flash beat (Module 37 §5). */
    handleBlockExited(_blockId) {
        this._playCue(BlockJamAudioId.BLOCK_EXIT);
    }
    /** The dragged block is being shoved into a wall or another block. */
    handleMoveBlocked() {
        this._playCue(BlockJamAudioId.INVALID_MOVE);
    }
    // ─── Powered gates (Module 63, WP-63.C4) ──────────────────────────────────
    // Each one is the beat the board reports from its own paint — the trace
    // starting to light or drain, the shutter's shudder, the pulse's first frame.
    // Nothing here reads the model: the board is the authority on WHEN, exactly
    // as it is for the exit flash.
    /** A plate was covered: the trace is lighting and the shutter retracting. */
    handleGateOpened() {
        this._playCue(BlockJamAudioId.GATE_OPEN);
    }
    /** A plate was uncovered: the trace is draining and the shutter dropping. */
    handleGateClosed() {
        this._playCue(BlockJamAudioId.GATE_CLOSE);
    }
    /** A block was shoved into a shut powered gate — the shutter's clunk. */
    handlePowerRefused() {
        this._playCue(BlockJamAudioId.GATE_BLOCKED);
    }
    /** The refusal's pulse began running from the plate toward the gate. */
    handleTracePulsed() {
        this._playCue(BlockJamAudioId.TRACE_PULSE);
    }
    /**
     * Every cue in the game goes through here. One line today, but it is the only
     * place that knows audio is optional — `_context` is null before `initialize`
     * and after `destroy`, and a cue firing against a torn-down plugin is a real
     * possibility given how many of these ride timers and animation callbacks.
     */
    _playCue(id) {
        this._context?.audio.play(id);
    }
    /**
     * The track this level should be playing, or `null` to leave the choice alone.
     *
     * The composition root owns the rotation (`getLevelMusic`); a context without
     * one — tests, standalone, any game with no pool — falls back to the first
     * declared track, which is exactly the single id that played before Module 64.
     */
    _trackForCurrentLevel() {
        // Module 72 (WP-72.B3) — the home screen has its own track, outside the
        // per-puzzle rotation. Every home ⇄ track transition already goes through
        // `_syncMusicToLevel`, so this one branch is the whole switch.
        if (this._needsTrackSelection)
            return BlockJamAudioId.HOME_MUSIC;
        // WP-65.B3 — the ORDINAL, not the effective level. Module 64 picks a track
        // deterministically from an index at stride 1, so the music changes with
        // every board exactly as the background does. Keyed on the effective level
        // it would repeat whenever the endless phase cycled back to a level, and
        // two different puzzles would share a track for no reason a player could
        // see.
        return this._context?.getLevelMusic?.('blockjam', this._ordinal) ?? BlockJamAudioId.BG_MUSIC_1;
    }
    /**
     * Move the rotation on when the level's track differs from the sounding one.
     *
     * Called on the same beat as `_applyBgTheme`, because music and background
     * rotate together and a player who sees a new board while hearing the old
     * track has been shown the seam.
     *
     * A no-op when the track is unchanged. At Block Jam's shipped stride of 1
     * that is only a re-entry into the SAME level (restart, jump-to-current), but
     * the guard is what makes those free — and it is what a larger stride, which
     * the config still allows, would lean on for four levels in five.
     */
    _syncMusicToLevel() {
        if (!this._musicWanted)
            return;
        const next = this._trackForCurrentLevel();
        if (next === this._musicTrackId)
            return;
        // Stop the OLD id explicitly (it may be sounding or merely owed), then ask
        // for the new one. `_stopMusic()` clears `_musicPlaying`, so `_startMusic()`
        // below is not blocked by its own guard — which is the whole reason a switch
        // cannot be expressed as two independent calls from outside this class.
        this._stopMusic();
        this._startMusic(next);
    }
    _startMusic(trackId = this._trackForCurrentLevel()) {
        if (!this._context || this._musicPlaying)
            return;
        this._musicWanted = true;
        this._musicTrackId = trackId;
        // Module 57 — this may not start the track, and that is fine now.
        //
        // `AudioService` OWES a music track it cannot start yet, and starts it when
        // whatever was in the way lifts: the first gesture, the platform un-muting,
        // an ad ending, the slider coming up off zero. So `_musicPlaying` below
        // means "we have asked for music and not cancelled it", which is exactly
        // what the guard above needs and what `_stopMusic()` cancels.
        //
        // Before that it meant less than it looked like. `play()` returns void, so
        // this could not tell a refusal from a start, and the service DROPPED the
        // track whenever a hold was on — a player who had CrazyGames muted at load
        // got no music for the session, and this flag then blocked every retry.
        // WP-64.A5 — a music track fades in. It is the counterpart of the fade
        // `stop()` has always applied, and without it a rotation switch would be a
        // fade-out followed by a hard start.
        this._context.audio.play(trackId, { loop: true, fadeInMs: MUSIC_FADE_IN_MS });
        this._musicPlaying = true;
    }
    /** Suspend the track, leaving it owed a restart. Safe when nothing is playing. */
    _stopMusic() {
        if (!this._musicPlaying)
            return;
        this._musicPlaying = false;
        // The id that was ASKED FOR, never a re-derived one: after a level change
        // the rotation would name a different track, and stopping that one would
        // leave the real track playing with nothing tracking it.
        this._context?.audio.stop(this._musicTrackId ?? BlockJamAudioId.BG_MUSIC_1);
    }
    /**
     * Module 72 (WP-72.B3) — back on the home screen: switch to the home track.
     * Re-arms first, so a player who returned from a track the music had been
     * ended on (an exit path) still hears the home screen.
     */
    _musicToHome() {
        this._musicWanted = true;
        this._syncMusicToLevel();
    }
    /** Stop for good — the game is over or gone, so nothing may restart it. */
    _endMusic() {
        this._musicWanted = false;
        this._stopMusic();
        // Cleared last: `_stopMusic()` reads it, and a stale id surviving teardown
        // would make the next `start()` believe the rotation was already on that
        // track and skip its first switch.
        this._musicTrackId = null;
        this._unwatchVisibility();
    }
    /**
     * Backgrounding the app must not leave music playing (T2). Nothing in the
     * app wires `visibilitychange` to `pause()` — the plugin loader only pauses
     * on deactivate — so the game listens for itself.
     */
    _watchVisibility() {
        if (typeof document === 'undefined' || this._visibilityHandler)
            return;
        this._visibilityHandler = () => {
            if (document.hidden) {
                this._stopMusic();
            }
            else if (this._musicWanted) {
                this._startMusic();
            }
        };
        document.addEventListener('visibilitychange', this._visibilityHandler);
    }
    _unwatchVisibility() {
        if (!this._visibilityHandler || typeof document === 'undefined')
            return;
        document.removeEventListener('visibilitychange', this._visibilityHandler);
        this._visibilityHandler = null;
    }
    _scheduleMoveCommit() {
        if (this._moveCommitTimer !== null)
            return;
        this._moveCommitTimer = setTimeout(() => {
            this._moveCommitTimer = null;
            this._commitState();
        }, BlockJamPlugin.MOVE_COMMIT_MS);
    }
    _flushMoveCommit() {
        if (this._moveCommitTimer === null)
            return;
        this._commitState();
    }
    _watchPageExit() {
        if (typeof document === 'undefined' || this._pageExitHandler)
            return;
        const handler = () => {
            if (document.visibilityState === 'hidden')
                this._flushMoveCommit();
        };
        const onPageHide = () => this._flushMoveCommit();
        document.addEventListener('visibilitychange', handler);
        if (typeof window !== 'undefined')
            window.addEventListener('pagehide', onPageHide);
        this._pageExitHandler = () => {
            document.removeEventListener('visibilitychange', handler);
            if (typeof window !== 'undefined')
                window.removeEventListener('pagehide', onPageHide);
        };
    }
    _unwatchPageExit() {
        this._pageExitHandler?.();
        this._pageExitHandler = null;
    }
    /** A move the player made by dragging on the board (WP-35.16.5). */
    handleBoardMove(info) {
        if (!this._state)
            return;
        this._pushHistory();
        const before = this._state.blocks;
        // The board is the authority on what the drag did; the plugin mirrors it.
        this._state = {
            ...this._state,
            blocks: info.blocks,
            moveCount: this._state.moveCount + 1,
        };
        this._noteGatesPowered(before);
        // WP-50.27 — a block passing a gate spends the rainbow. After the move count
        // is taken, so `moves_open` includes the move that ended it; before
        // `_afterMove`, so the win/stuck/budget checks below see the settled state.
        // Module 90 (D3) — and only an exit that USED it: a block leaving through
        // its own colour leaves Wild Gates open and unpaid.
        if (info.wildSpent === true)
            this._endRainbowOnExit();
        // Module 74 (D3) — Surge lasts until a block leaves through a gate only it
        // was holding open (`model/surge.ts`). The renderer decided that at the
        // exit instant, with the power set of that instant, and has already
        // dropped its own flag; this is the plugin mirroring the board, as with
        // the rainbow.
        this._endSurgeIfSpent(info.surgeSpent === true);
        this._afterMove(info.exitedIds);
    }
    /**
     * A block just left through a gate that only Surge was holding open, so the
     * surge is spent (Module 74, D3). `moves_open` is how long the player took to
     * cash it in: the number that says whether "until used" is what made Surge
     * worth 40 coins.
     */
    _endSurgeIfSpent(spent) {
        if (!spent || !this._state || !isSurgeActive(this._state))
            return false;
        const movesOpen = surgeMovesOpen(this._state);
        this._state = endSurge(this._state);
        // Module 91 (D2) — THIS is Master Key's charge, as `_endRainbowOnExit` is
        // Wild Gates': the exit that used it. Closed first so its own hold is
        // released before the controller checks the balance; before `_afterMove`
        // on both move paths, so a winning last block is paid for before
        // level-complete commits. A failure is our bug (the price was held), so
        // the player keeps the exit and is never charged later for it.
        this._chargeSpentMode(SURGE_ID);
        this.completePowerup(SURGE_ID, { moves_open: movesOpen, ended: 'exit' });
        return true;
    }
    /**
     * Module 90/91 — charge an open, pay-when-used mode at the exit that used it:
     * free use first, then coins. Never blocks the exit; a refusal is logged.
     */
    _chargeSpentMode(id) {
        if (!this._state)
            return;
        this._activeFreeUses.delete(id);
        const charged = this._chargeCounted(id);
        if (!charged.ok) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_CHARGE_FAILED, {
                powerup_id: id,
                level_id: this._state.puzzle.levelNumber,
                refusal: charged.error,
            });
        }
    }
    /**
     * Module 144 — the one place a power-up's card stage moves: a real SPEND (the
     * exit that uses Wild Gates or Master Key, the block a Hammer or Colour Bomb
     * is aimed at). Counted before the charge so a free use's on-the-spot commit
     * (`usePowerup`) carries it; a paid one rides the next commit. Taken back if
     * the charge fails.
     */
    _chargeCounted(id) {
        const before = this._powerupConfirms[id] ?? 0;
        this._powerupConfirms = { ...this._powerupConfirms, [id]: before + 1 };
        const charged = this.usePowerup(id);
        if (!charged.ok)
            this._powerupConfirms = { ...this._powerupConfirms, [id]: before };
        return charged;
    }
    /** Snapshot the current position for undo, bounded by `MAX_UNDOS`. */
    _pushHistory() {
        if (!this._state)
            return;
        this._history.push({
            blocks: this._state.blocks.map((b) => ({ ...b })),
            moveCount: this._state.moveCount,
        });
        if (this._history.length > MAX_UNDOS)
            this._history.shift();
    }
    /** Everything that happens after a move, whichever path produced it. */
    _afterMove(exitedIds) {
        if (!this._state)
            return;
        // Module 129 (WP-129.3) — every move is saved (resume exactly). A commit
        // made below for a win or out-of-moves writes it sooner.
        this._scheduleMoveCommit();
        // Module 114 — the first committed move of this page life: the funnel's
        // answer to "could they play inside 30 seconds?" (the embed adds the clock).
        if (!this._firstMoveLogged) {
            this._firstMoveLogged = true;
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.FIRST_MOVE, {
                track: this._dailyMode ? 'daily' : this._track,
                puzzle: this._dailyMode ? 0 : this._ordinal,
            });
        }
        // Module 116 — the first move of THIS attempt (`page_hidden.moved`). At most
        // once per attempt in practice; an undo back to 0 and a new move repeats it,
        // which only says "moved" again.
        if (this._state.moveCount === 1) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.ATTEMPT_MOVED, {
                track: this._dailyMode ? 'daily' : this._track,
                puzzle: this._dailyMode ? 0 : this._ordinal,
            });
        }
        this._hintedBlockId = null;
        this._selectedBlockId = null;
        // WP-37.8 — `block_move` is the SETTLE, the block landing on its new cells.
        // A move that ended with the block leaving the board never settles anywhere,
        // and its exit cue has already played on the gate flash; sounding both would
        // put a thud under every clearance.
        //
        // WP-A.2 audited this against the movement rule and left it alone: a settle
        // describes a landing, not a motion, and `_afterMove` is unreachable without
        // a committed move, so it cannot fire on a touch the way the bed used to.
        if (exitedIds.length === 0)
            this._playCue(BlockJamAudioId.BLOCK_MOVE);
        // Module 132 — blocks out through a gate. Memory only: the move commit saves it.
        else
            this._milestoneEvent({ type: 'blocksCleared', count: exitedIds.length }, false);
        if (this._state.blocks.length === 0) {
            // Reward, analytics and the save fire at DETECTION time; only the POPUP
            // waits for the celebration (WP-36.5, locked decision 2). A player who
            // backgrounds the app mid-confetti must not lose the coins.
            this.onLevelComplete();
            this._startWinSequence();
        }
        else if (hasNoFreeMoves(this._state)) {
            // Module 50 (WP-50.28a) — BEFORE the budget, not after. A board with no
            // legal move left is not a board more moves can rescue, so offering "+2
            // moves — watch ad" there would be selling something that cannot work.
            this._isStuck = true;
        }
        else if (isOutOfMoves(this._state)) {
            // The budget check is deliberately the LAST of the three: a move that
            // clears the final block and spends the last of the budget in the same
            // gesture is a win. `blocks.length === 0` is evaluated first and wins
            // outright, which is what makes that true by construction rather than by
            // an ordering someone has to remember.
            //
            // There is no `_isOutOfMoves` flag: the popup is DERIVED from the state
            // (`isOutOfMoves`), so entering a level, restarting and being granted ad
            // moves all clear it for free. A flag would need resetting at four sites
            // and would be the fourth one somebody forgets.
            //
            // Module 53 (WP-53.1) — that derivation is what shipped the reported bug,
            // and the ordering above is NOT what fixed it. This branch was never even
            // reached on a winning last move; the popup mounted anyway, because the
            // derived expression did not know a celebration was running. The
            // derivation stays, for the reasons above; the ranking it depends on now
            // lives in `ui/active-modal.ts` where it can be enumerated.
            //
            // The event fires on `moveCount === moveBudget` rather than `>=`, so it
            // fires exactly once per wall reached — including the second time, after
            // an ad has moved the wall.
            if (this._state.moveCount === this._state.moveBudget) {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.MOVE_BUDGET_EXHAUSTED, {
                    level: this._state.puzzle.levelNumber,
                    budget: this._state.moveBudget,
                    adsUsed: this._state.movesAdsUsed ?? 0,
                    // Module 130 (D8) — how close: does the redesign lift the rescue rate?
                    blocksLeft: this._state.blocks.length,
                });
                // Module 118 (WP-118.7) — how short, measured off the main thread.
                this._measureMovesShort();
                // Module 80 (WP-80.16) — counted per PUZZLE, across restarts and
                // sessions: this is what decides when help is offered.
                this._bumpAttempt('outOfMoves');
                this._commitState();
            }
        }
        // Module 103 (WP-103.3) — after the win / stuck / out-of-moves ranking, so a
        // move that settles any of those can never also warn.
        this._warnIfMovesLow();
        const badgeSeq = this._milestoneToast?.seq;
        this._queueMilestoneToast();
        this._queueMissionToast(this._milestoneToast?.seq !== badgeSeq);
        this._renderHud();
    }
    /**
     * Module 103 (WP-103.3) — the 3·2·1 warning, decided ONCE per committed move.
     *
     * Fires when the move just made left 3, 2 or 1 moves (and the config's
     * `warnAtRemaining` agrees), on a board that is still being played: not won
     * (the last block left — a winning last move is a win, never a warning), not
     * stuck, not out of moves. Only `_afterMove` calls it, so Undo, Restart and an
     * ad grant — none of which commit a move — can never sound it.
     *
     * The cue plays here (the state transition, Audio_Rules Rule 1b); the HUD gets
     * a sequence number so its burst replays once per warning.
     */
    _warnIfMovesLow() {
        if (!this._state || this._state.blocks.length === 0 || this._isStuck)
            return;
        if (isOutOfMoves(this._state))
            return;
        const left = this.movesRemaining;
        if (left === null || left < 1 || left > 3)
            return;
        if (left > this._generationConfig.moveBudget.warnAtRemaining)
            return;
        this._movesAlert = { left: left, seq: (this._movesAlert?.seq ?? 0) + 1 };
        this._playCue(left === 3
            ? BlockJamAudioId.MOVES_LOW_3
            : left === 2
                ? BlockJamAudioId.MOVES_LOW_2
                : BlockJamAudioId.MOVES_LOW_1);
    }
    // ─── Win sequence (WP-36.5) ───────────────────────────────────────────────
    /**
     * Run exit-slide-out → confetti → popup, then set `_isWon` so the popup
     * renders. Under `prefers-reduced-motion` it skips straight to the popup: a
     * player who asked for less motion should not be made to wait through 1.4s of
     * it (locked decision 2).
     *
     * ## The slide-out is WAITED FOR, not timed
     *
     * The first beat asks the board when the last block has actually finished
     * going through its gate, rather than assuming it takes `EXIT_MS`. The
     * assumption was wrong on exactly the frames that mattered: `checkExit` queues
     * the FX and then, in the same synchronous task, this method runs — banking
     * the reward, saving, re-rendering the HUD, and (until this change) building
     * level N+1. Measured off a player's recording that was ~200ms of blocked main
     * thread against a 250ms timer, so the timer expired with the slide-out not
     * yet drawn once. The board went from "block at the gate" straight to "block
     * gone, popup up", which is the bug in one sentence.
     *
     * `EXIT_FX_TIMEOUT_MS` bounds the wait so a flourish still cannot hold the
     * popup hostage.
     *
     * @param nextLevelNumber the level to pre-build during the celebration.
     *   Deliberately started only once the slide-out has PLAYED, not at win
     *   detection: generation is 70ms typically and up to 1.6s on a throttled
     *   device (WP-36.10), and inside the exit window that cost lands squarely on
     *   the 250ms of animation the player is meant to be watching. The confetti
     *   window that follows is 1.2s of budget nobody is using, which is what
     *   WP-36.11 was actually reaching for.
     */
    _startWinSequence() {
        if (this._isWon || this._isCelebrating)
            return;
        // Whichever route reaches the popup schedules the build, and only the first
        // one does — a tap that skips the celebration must still get an instant
        // **Next**, and it arrives here before `celebrate` ever runs.
        // Module 87 — closed until the slide-out has played (or a skip / reduced
        // motion reaches the popup), whichever route gets there first.
        this._winWindowOpen = false;
        if (this._reducedMotionFn()) {
            // No animation to protect, so the idle build starts immediately — the
            // popup is already on screen and **Next** is one tap away.
            this._openWinWindow();
            this._showWin();
            return;
        }
        this._isCelebrating = true;
        // One timer, so a skip or a teardown has exactly one thing to cancel. It is
        // the CEILING on the wait now rather than the wait itself: whichever of the
        // board and the timer arrives first calls `celebrate`, which is idempotent.
        let celebrated = false;
        const celebrate = () => {
            if (celebrated)
                return;
            celebrated = true;
            if (this._celebrationTimer !== null) {
                clearTimeout(this._celebrationTimer);
                this._celebrationTimer = null;
            }
            if (!this._isCelebrating)
                return; // skipped or destroyed while waiting
            // The slide-out is done and the popup is still ~1.2s away — the window
            // WP-36.11 wants for level N+1.
            this._openWinWindow();
            const burst = this._board?.playWinFx() ?? Promise.resolve();
            // WP-37.8 — with the burst, not with the win detection that banked the
            // coins a moment ago. This is the beat the cue was written for.
            this._playLevelCompleteCue();
            void burst.then(() => {
                // False if a tap already skipped ahead, or the plugin was destroyed —
                // the board resolves the promise on teardown rather than hanging.
                if (this._isCelebrating)
                    this._showWin();
            });
        };
        this._celebrationTimer = setTimeout(celebrate, EXIT_FX_TIMEOUT_MS);
        void (this._board?.whenRemoveFxSettled() ?? Promise.resolve()).then(celebrate);
    }
    /** True while the celebration is playing and the popup is being held back. */
    get isCelebrating() {
        return this._isCelebrating;
    }
    /** True once the win popup is up. */
    get isWon() {
        return this._isWon;
    }
    /** A tap anywhere during the celebration jumps straight to the popup. */
    skipCelebration() {
        if (!this._isCelebrating)
            return;
        this._showWin();
    }
    /** Cancel whatever is still playing and put the popup on screen. */
    _showWin() {
        this._cancelCelebration();
        // A skip lands here ahead of `celebrate`; this is the build it would
        // otherwise have missed. A no-op on the ordinary path, which already ran it.
        this._openWinWindow();
        // The two routes that reach the popup without ever playing the confetti:
        // reduced motion, and a tap that skipped ahead of the burst. Either way the
        // level IS complete and must sound like it — `_playLevelCompleteCue` is
        // once-per-level, so the ordinary path does not double up here.
        this._playLevelCompleteCue();
        this._decideMoveUpOffer();
        this._isWon = true;
        this._renderHud();
    }
    /**
     * Module 77 (D6, user 2026-09-28) — the win popup's "Try <next track> →"
     * button, greyed until this track's threshold (`winMoveUp`). An ACTIVE one
     * counts as shown: counted per track and persisted through `_commitState`
     * (capped at the validator's 10), for the analytics `offer` number.
     */
    _decideMoveUpOffer() {
        this._moveUpOffer = null;
        if (this._dailyMode)
            return;
        const button = winMoveUp(this._generationConfig, this._track, this._ordinal, this._trackProgress);
        if (!button)
            return;
        if (!button.active) {
            this._moveUpOffer = { ...button, offer: 0 };
            return;
        }
        const offer = Math.min(10, (this._trackProgress[this._track]?.moveUpOffers ?? 0) + 1);
        this._moveUpOffer = { ...button, offer };
        this._trackProgress[this._track] = {
            ...this._trackProgress[this._track],
            moveUpOffers: offer,
        };
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.TRACK_SUGGESTION_SHOWN, {
            track: this._track,
            offer,
        });
        this._commitState();
    }
    /** Module 77 (D6) — "Try <next track> →": enter it as a home-card tap would. */
    acceptMoveUp() {
        const offer = this._moveUpOffer;
        if (!offer?.active || !this._isWon)
            return;
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.TRACK_SUGGESTION_ACCEPTED, {
            track: this._track,
            offer: offer.offer,
        });
        // The cleared puzzle stays cleared: returning to this track resumes on
        // the next one, exactly as Next would have left it.
        const current = this._trackProgress[this._track];
        this._trackProgress[this._track] = {
            ...current,
            ordinal: Math.max(current?.ordinal ?? 1, this._ordinal + 1),
        };
        // WP-78.3 — raised BEFORE the switch, because `selectTrack` ends by asking
        // for the new board's lesson and that ask must see the card is up.
        this._trackEntry = offer.next;
        this.selectTrack(offer.next);
    }
    /** Module 78 (WP-78.3) — the entry card goes on its button; what it held back follows. */
    dismissTrackEntry() {
        if (this._trackEntry === null)
            return;
        this._trackEntry = null;
        this._maybeShowTutorial();
        this._renderHud();
    }
    /** The win cue, at most once per level however the popup was reached. */
    _playLevelCompleteCue() {
        if (this._levelCompleteCuePlayed)
            return;
        this._levelCompleteCuePlayed = true;
        this._playCue(BlockJamAudioId.LEVEL_COMPLETE);
    }
    /**
     * Stop the celebration without showing anything — the shared teardown for a
     * skip, a new level and `destroy()`. Safe to call when nothing is running.
     */
    _cancelCelebration() {
        if (this._celebrationTimer !== null) {
            clearTimeout(this._celebrationTimer);
            this._celebrationTimer = null;
        }
        this._isCelebrating = false;
        this._board?.stopWinFx();
    }
    undo() {
        // Nothing to undo once the level is over — including during the
        // celebration, when the board is already cleared and the coins are banked.
        if (!this._state || this._isWon || this._isCelebrating)
            return;
        const previous = this._history.pop();
        // Nothing popped = nothing undone. The cue fires below, past this guard, so
        // a tap on a spent Undo stays silent (WP-37.8 T4).
        if (!previous)
            return;
        this._playCue(BlockJamAudioId.UNDO);
        // Module 73 — an undo is one of the "looks stuck" signals (rule 3).
        this._syncFreeHintPuzzle();
        this._freeHintUndos += 1;
        // Module 50 (WP-50.28a) — `moveCount` is deliberately NOT restored. Undo
        // returns the board, never the move.
        //
        // This is the decision that makes the budget mean anything. With `MAX_UNDOS
        // = 30` and a refund, a player simply undoes back under the limit forever:
        // they never run out, the offer never appears, and the whole increment is
        // decorative. It also turns Undo from a free rewind into a real choice.
        //
        // `_pushHistory` still snapshots `moveCount` — the field is left in place
        // because it is what a future "how many moves ago was this" would need, but
        // nothing may read it back. A test pins that.
        this._state = { ...this._state, blocks: previous.blocks };
        this._scheduleMoveCommit();
        this._isStuck = false;
        // An undo cannot take the player back under the budget (moves only go up),
        // so the wall stays up if it was up. What it CAN do is clear the stuck flag
        // above, which is why that one is reset and this one is not.
        this._hintedBlockId = null;
        // `_renderBoard` → `setBoard` drops the board's targeting; keep the plugin's
        // armed flag in step or the HUD would keep showing an arm that is gone.
        this._armedPowerupId = null;
        this._confirmPreview = null;
        this._renderBoard();
        this._renderHud();
    }
    getEnabled() {
        return this._enabled;
    }
    getSelectedBlockId() {
        return this._selectedBlockId;
    }
    getBoardState() {
        return this._state;
    }
    isInputLocked() {
        return this._inputLocked;
    }
    lockInput() {
        this._inputLocked = true;
    }
    unlockInput() {
        this._inputLocked = false;
    }
    // ─── Power-ups (WP-35.17) ─────────────────────────────────────────────────
    /**
     * Live state of every power-up for the current level — the bar's data source
     * (WP-35.18). Empty before `initialize`, never "everything unlocked".
     */
    /**
     * The board the plugin currently holds (Module 63). Read-only, for tests and
     * diagnostics that need the plugin's OWN truth rather than the renderer's
     * last `setBoard` — the two are meant to agree, and this is how a test says
     * so.
     */
    get boardState() {
        return this._state;
    }
    get powerups() {
        // Module 67 (WP-67.C3) — NO power-ups in the Daily (user decision
        // 2026-09-05): "same board for everyone" means same tools, and the
        // per-track free-use bookkeeping has no home in a mode with no track.
        // Empty here is the single switch — the bar renders off this list and
        // `_applyPowerupBarReserve` sizes the layout off its length, so both
        // follow for free.
        if (this._dailyMode)
            return [];
        // Module 70 — the BOARD goes in, so a power-up that can do nothing on this
        // one (Surge with no shut powered gate) dims instead of sitting bright and
        // swallowing the tap. `_state` is null only before the first board exists,
        // and the controller treats that exactly as it did before this module.
        return this._powerups?.statuses(this._state ?? undefined) ?? [];
    }
    /**
     * Commit a use: unlock/cap/funds are enforced here and the charge is taken.
     * The caller (the arm/target flow, then each effect's WP) applies the effect
     * only when this returns `ok`, and calls `completePowerup` when it finishes.
     */
    usePowerup(id) {
        if (!this._powerups)
            return R.err('unknown');
        const result = this._powerups.use(id);
        if (result.ok) {
            // The guided highlight has done its job the moment the power-up it points
            // at is actually used (WP-35.22) — same rule as the guided BLOCK clearing
            // once it is moved.
            if (this._guidedPowerupId === id)
                this._guidedPowerupId = null;
            // Module 118 — quests (`use_powerup`), free or paid alike.
            this._questEvent({ type: 'powerupUsed' });
            this._milestoneEvent({ type: 'powerupUsed' });
            // Module 63 (WP-63.E5) — a consumed FREE use is written to the save on
            // the spot, not on the next move: "once ever" has to survive a reload
            // between the press and the move, or a page refresh is a refund. Every
            // use goes through here, so the one writer stays the one writer.
            if (result.value.free) {
                this._commitState();
                // Module 112 — the free use is SEEN being spent: its ticket flies.
                this._freeUseCue = { id: (this._freeUseCue?.id ?? 0) + 1, powerupId: id };
            }
            // Module 76 (D2) — a paid use is SEEN leaving the wallet. Module 110: the
            // "−25" float now comes from the coin-motion queue (`_wallet` recorded the
            // debit), so it plays when the pill can be seen — never on a free use,
            // which recorded nothing.
            this._renderHud();
        }
        return result;
    }
    /** The power-up button the onboarding highlight points at, if any. */
    get guidedPowerupId() {
        return this._guidedPowerupId;
    }
    /** The effect finished — see `PowerupController.complete`. */
    completePowerup(id, details) {
        this._powerups?.complete(id, details);
    }
    /** The power-up the player has armed and is choosing a target for, if any. */
    get armedPowerupId() {
        return this._armedPowerupId;
    }
    /** Module 80 (WP-80.7) — whether the current arm is this power-up's first. */
    get armExplains() {
        return this._armedPowerupId !== null && this._armExplains;
    }
    /**
     * Arm a power-up (WP-35.18): the board dims everything that is not a valid
     * target and waits for one tap. **Nothing is charged here** — the locked UX is
     * arm → show cost → tap target → charge, and "tap empty area = cancel, no
     * charge", so arming has to be free or a cancel would cost the player money.
     *
     * Refuses (and stays unarmed) for the same reasons a use would be refused, so
     * the player is told before choosing a target rather than after. Tapping the
     * already-armed power-up disarms it — the button doubles as its own cancel.
     */
    /**
     * Module 80 (WP-80.7) — a TARGETED power-up's first arm explains it. Its
     * banner otherwise says only what to tap ("Tap a block to smash it"), which
     * tells a player who has never used it what to do but not what it is for.
     * Immediate power-ups need nothing: their confirm strip already leads with
     * the name and description. Marked at the arm, not at the use, so a player
     * who arms and cancels has still been told. One write, through
     * `_commitState()` (module rule 3).
     */
    _noteFirstArm(id) {
        this._armExplains = !this._explainedPowerups.includes(id);
        if (!this._armExplains)
            return;
        this._explainedPowerups = [...this._explainedPowerups, id];
        this._commitState();
    }
    armPowerup(id) {
        if (this._armedPowerupId === id) {
            this.cancelArm('button_tap');
            return R.ok(undefined);
        }
        // Module 90 (D4) — the button of an open, unpaid mode is its cancel. Ahead of
        // every refusal: the player is asking to undo, not to buy.
        if (this._state && chargesWhenSpent(id) && openUnpaidPowerups(this._state).includes(id)) {
            this.cancelOpenPowerup(id);
            return R.ok(undefined);
        }
        if (!this._powerups || !this._state)
            return R.err('unknown');
        const status = this._powerups.status(id, this._state);
        if (!status)
            return R.err('unknown');
        if (status.refusal) {
            // WP-41.12 gave `insufficientFunds` a way out here — a rewarded ad worth
            // coins. Module 50 (WP-50.29) removed it: an ad at the moment a player
            // wants a power-up sells them the wrong thing, and the power-up prices
            // (WP-50.26) are now set so that playing on is the way out. The refusal is
            // the refusal.
            //
            // WP-45.3 — "not unlocked yet" is the one refusal the player can act on,
            // by playing on. Before this, a locked button was `disabled` and the tap
            // did not happen at all, so the row said what was locked but never why or
            // for how long. The arm is still refused; the only thing that changes is
            // that the refusal now speaks.
            //
            // Module 70 — and so does "nothing here for it to do", for the same
            // reason: it is a refusal the player can act on, by playing on to a board
            // that has one. The button is already dimmed by then; this is what tells
            // them the dimming is about the PUZZLE and not about them.
            //
            // Module 76 (D3) — every refusal speaks now: short of coins and used this
            // puzzle were the two a player could tap and get nothing back from. The
            // short-of-coins strip offers "Get coins", which opens the Shop the Top
            // Bar's "+" already opens. That reverses WP-50.29 above by user decision
            // (2026-09-25): it does not play an ad at the player, it tells them where
            // coins come from, at the moment they want them.
            if (status.refusal !== 'unknown')
                this._showRefusalNotice(status, status.refusal);
            return R.err(status.refusal);
        }
        // A real arm supersedes whatever the last refusal was saying.
        this._clearLockedNotice();
        // …and it ends the lesson that asked for it (2026-08-14). The player has
        // done the thing the spotlight was pointing at, so the spotlight comes down
        // here rather than waiting for a dismissal — `ArmBanner` is already on screen
        // with the rest of the instruction. Placed after every refusal above, so a
        // refused tap leaves the lesson standing.
        if (this._tutorialPowerupId === id)
            this.handleGhostHandDismissed();
        // WP-50.27 — the second commit shape. An `'immediate'` power-up has nothing
        // to aim at: pressing the button IS the commit, so it charges and switches
        // the board's mode here and never enters targeting. Branched on the
        // EFFECT's declaration, never on the id (the registry's whole rule).
        //
        // Module 76 (D2) — a PAID immediate power-up no longer commits on the first
        // tap: it arms, and the strip asks "Use – 25 🪙". Free uses still commit at
        // once (trying one stays one tap, and the lessons at the unlock puzzle
        // always have their free uses). The effect is asked first, as the commit
        // asks it, so a confirm is never offered for something that would refuse.
        if (activationFor(id) === 'immediate') {
            // Module 90 (D2) — except a power-up paid when it is spent: it confirms
            // on EVERY tap, free uses included, because its first free use is a new
            // player's first meeting with it and the card is where it is explained.
            if (status.freeUsesLeft > 0 && !chargesWhenSpent(id))
                return this._activateImmediate(id);
            if (!activateEffect(id, this._state))
                return R.err('nothingToDo');
            this._switchArm(id);
            this._armedPowerupId = id;
            this._playCue(BlockJamAudioId.POWERUP_ARMED);
            // Targeting with NO targets: every tap on the board cancels, and no block
            // can be dragged while the confirm is up, so a move can never slip in
            // between the offer and the charge.
            this._board?.setTargeting([]);
            if (chargesWhenSpent(id))
                this._showConfirmPreview(id);
            this._renderHud();
            return R.ok(undefined);
        }
        // Module 67 (WP-67.B4) — the third commit shape: targeting aimed at GATES.
        // Same arm beat and same "nothing to aim at is a refusal" rule as blocks.
        if (activationFor(id) === 'target-gate') {
            const gateTargets = gateTargetsFor(id, this._state);
            if (gateTargets.length === 0)
                return R.err('capped');
            this._switchArm(id);
            this._armedPowerupId = id;
            this._noteFirstArm(id);
            this._playCue(BlockJamAudioId.POWERUP_ARMED);
            this._board?.setGateTargeting?.(gateTargets);
            this._renderHud();
            return R.ok(undefined);
        }
        // Nothing to aim at is a real refusal, not an empty armed state the player
        // has to tap their way out of.
        const targets = targetsFor(id, this._state);
        if (targets.length === 0)
            return R.err('capped');
        this._switchArm(id);
        this._armedPowerupId = id;
        this._noteFirstArm(id);
        // WP-37.10 — the arming beat, and only on a real arm: every refusal above
        // returns before this, and re-tapping the armed power-up disarms through
        // `cancelArm`, which is not an arming. Nothing has been charged yet, so this
        // cue must not sound like a spend.
        this._playCue(BlockJamAudioId.POWERUP_ARMED);
        this._board?.setTargeting(targets);
        // Module 144 — Hammer and Colour Bomb get the card too; the blocks it can be
        // aimed at pulse pink, loud while the player is still learning it.
        const stage = this.confirmStageFor(id);
        this._board?.setTargetPulse?.(true, stage !== 'regular');
        // A first card's hand taps a real target on the BOARD, not the card: the
        // instruction is not a button (user, 2026-10-23). The one nearest the
        // board's middle, so the hand never hangs off an edge.
        const handBlock = stage === 'first' ? this._middleBlock(targets) : undefined;
        this._confirmPreview = { id, keys: [], badges: [], ...(handBlock ? { handBlock } : {}) };
        this._renderHud();
        return R.ok(undefined);
    }
    /**
     * Charge for an `'immediate'` power-up and switch the board into its mode
     * (WP-50.27). The button press is the commit — there is no arm to cancel and
     * nothing to aim at.
     *
     * Order is the point: the effect is asked whether it has anything to do
     * BEFORE the charge, because everything after a successful `usePowerup` has
     * spent the player's coins. `activate` returning null on an already-open
     * rainbow is what makes a double-press free rather than a second purchase.
     */
    _activateImmediate(id) {
        if (!this._state)
            return R.err('unknown');
        const next = activateEffect(id, this._state);
        // Nothing to do. Module 70 gave this its own refusal rather than borrowing
        // `capped`: the two are different sentences to the player ("you have used
        // it up here" vs "there is nothing here to use it on"), and only one of them
        // is true of a fresh board. Unreachable in practice now — `armPowerup`
        // refuses `nothingToDo` before it ever gets here — and kept as the second
        // lock, because the two must never be able to disagree.
        if (!next)
            return R.err('nothingToDo');
        // Module 90 (D3) — a mode paid for when it is SPENT opens here for nothing.
        // The charge (free use first, then coins) is taken by the exit that uses
        // it (`_endRainbowOnExit`); until then the price is held and the button
        // cancels. Re-asked here because the balance can move between the confirm
        // card opening and the Use tap.
        if (chargesWhenSpent(id)) {
            const status = this._powerups?.status(id, this._state);
            if (!status)
                return R.err('unknown');
            if (status.refusal)
                return R.err(status.refusal);
            if (status.freeUsesLeft > 0)
                this._activeFreeUses.set(id, status.freeUsesLeft - 1);
            else
                this._activeFreeUses.delete(id);
            // The guided highlight comes down when the player USES the power-up it
            // points at (WP-35.22). That was the charge; for this shape the player has
            // used it the moment it opens, whether or not an exit ever pays for it.
            if (this._guidedPowerupId === id)
                this._guidedPowerupId = null;
            this._state = next;
            this._board?.setBoard(this._state);
            const opened = POWERUP_EFFECTS[id]?.impactAudioId;
            const onOpened = () => {
                if (opened)
                    this._playCue(opened);
            };
            // Module 140 (D4) — a key turns in each lock before its gate opens, so
            // the cue waits for the board to report the first turn (Audio_Rules Rule
            // 1.5). The board calls back exactly once, even when it draws nothing.
            if (POWERUP_EFFECTS[id]?.unlockFx && this._board?.playUnlockFx) {
                this._board.playUnlockFx(onOpened);
            }
            else
                onOpened();
            this._renderHud();
            return R.ok(undefined);
        }
        const charged = this.usePowerup(id);
        if (!charged.ok) {
            this._playCue(BlockJamAudioId.INVALID_MOVE);
            this._renderHud();
            return R.err(charged.error);
        }
        if (charged.value.free) {
            this._activeFreeUses.set(id, this.powerups.find((p) => p.id === id)?.freeUsesLeft ?? 0);
        }
        else {
            this._activeFreeUses.delete(id);
        }
        // NOT `_pushHistory()`. Undo restores blocks and would have to either
        // restore the rainbow (giving back a spent purchase every time) or drop it
        // silently at a moment the player did not choose. The rainbow is not a
        // board position, so it is not on the undo stack at all — see
        // `model/rainbow.ts`.
        this._state = next;
        this._board?.setBoard(this._state);
        // The gates turning rainbow IS the effect, so this is its impact beat
        // (Audio_Rules rule 1 — the cue rides the animation it belongs to). Read
        // from the effect's declaration like every other cue in this file.
        const impact = POWERUP_EFFECTS[id]?.impactAudioId;
        if (impact)
            this._playCue(impact);
        this._renderHud();
        return R.ok(undefined);
    }
    /**
     * A block went through a gate — close the rainbow if it was open (WP-50.27).
     *
     * Called from both move paths, after the state has taken the move, because
     * `moves_open` is measured against the move count the exit produced. Returns
     * whether it closed anything, so a caller can play the ending beat without
     * asking twice.
     */
    _endRainbowOnExit() {
        if (!this._state || !isRainbowActive(this._state))
            return false;
        const movesOpen = rainbowMovesOpen(this._state);
        this._state = endRainbow(this._state);
        // Module 90 (D3) — THIS is the charge: the exit that used Wild Gates.
        // Closed first so the hold is released before the controller checks the
        // balance. It runs before `_afterMove`, so a winning last block is charged
        // before level-complete commits. A failure is our bug (the price was held),
        // so the player keeps the exit and is never charged later for it.
        this._chargeSpentMode(RAINBOW_GATES_ID);
        // The renderer clears its own paint the instant the block leaves (that IS
        // the rule, and it has to be true before the next drag resolves), so this
        // is the plugin mirroring the board rather than instructing it — the same
        // relationship `handleBoardMove` already has with `info.blocks`. The
        // programmatic path has no renderer involvement and is re-synced by its own
        // `setBoard`.
        this.completePowerup(RAINBOW_GATES_ID, { moves_open: movesOpen, ended: 'exit' });
        return true;
    }
    /**
     * The power-up whose refusal notice is showing, and why, or null (WP-45.3,
     * generalised to every refusal by Module 76).
     */
    get refusalNotice() {
        return this._refusalNotice;
    }
    /** Module 76 — the Top Bar coin badge's current attention cue, or null. */
    /** Module 112 — the last free use spent, for the HUD's ticket flight. */
    get freeUseCue() {
        return this._freeUseCue;
    }
    get coinsCue() {
        return this._coinsCue;
    }
    /** Module 110 — the transactions the Top Bar has not shown yet. Test seam. */
    get coinMotion() {
        return this._coinMotion;
    }
    /**
     * Module 110 (D9) — every credit and debit goes through this wallet, which
     * records it for the Top Bar. Reads (`getBalance`) may use either: balances
     * are the same. Set with `_context`, so every caller's context guard covers it.
     */
    get _wallet() {
        if (!this._recordingWallet)
            throw new Error('BlockJamPlugin: wallet used before initialize()');
        return this._recordingWallet;
    }
    /**
     * Module 110 (D2) — the plugin's half of "something covers the coin pill": an
     * outcome popup or the win celebration (the arbiter counts an emptied board,
     * so the win credited at detection is covered from its first render). The
     * HUD adds its own dialogs. A covered reward that flies through the cover
     * (the win's, once its popup coin has counted) is the HUD's business.
     */
    get coinsCovered() {
        // Module 141 — the ad-break gift card covers the pill from the credit on.
        // The HUD's own `adGift` term arrives with the next render, too late: the
        // credit notifies the shown balance first, and it reads THIS live (found
        // in the built game: the +10 counted on the pill as the card popped in).
        if (this._adGift !== null)
            return true;
        if (this._needsTrackSelection)
            return false;
        return activeModal(this._modalView()) !== null;
    }
    /**
     * Module 110 — a rewarded ad is in flight: NOTHING moves on the Top Bar, not
     * even a reward that would otherwise fly through a popup's dim.
     */
    get coinsBlocked() {
        return (this._coinPackHold !== null ||
            this._doubleCoins === 'pending' ||
            this._shopTopUpPending ||
            this._extraMovesPending ||
            this._revealAdInFlight);
    }
    /** Module 137 — see `_coinPackHold`. Held once per grant; the first event releases it. */
    _holdCoinPackFlight() {
        if (this._coinPackHold || typeof window === 'undefined')
            return;
        const events = ['pointerdown', 'pointermove', 'keydown'];
        const off = () => {
            for (const e of events)
                window.removeEventListener(e, release, true);
        };
        const release = () => {
            off();
            this._coinPackHold = null;
            this._renderHud();
        };
        for (const e of events)
            window.addEventListener(e, release, true);
        this._coinPackHold = off;
    }
    /**
     * The refusal notice as the HUD draws it: the power-up's live status plus the
     * refusal recorded at the tap. Recorded rather than re-read, because the
     * status can move while the strip is up (coins arrive), and the strip answers
     * the tap the player made.
     */
    _refusalNoticeView() {
        const notice = this._refusalNotice;
        if (!notice)
            return null;
        const status = this.powerups.find((p) => p.id === notice.id);
        return status ? { status, refusal: notice.refusal } : null;
    }
    /**
     * Say why a tap was refused, and take it away again.
     *
     * Re-tapping the same button (or another refused one) restarts the timer
     * rather than stacking a second one — hence the unconditional clear first.
     */
    _showRefusalNotice(status, refusal) {
        const { id } = status;
        const unlockPuzzle = status.unlockPuzzle;
        if (this._lockedNoticeTimer !== null)
            clearTimeout(this._lockedNoticeTimer);
        this._refusalNotice = { id, refusal };
        // Module 70 — `powerup_locked_tapped` still means what the Analytics Master
        // Schema says it means, so a `nothingToDo` tap logs nothing rather than
        // arriving in that funnel as a fake lock. A cue of its own would be a new
        // event, and a new event is registered in the schema before it is emitted
        // (cross-cutting rule 4) — out of scope for this fix, and worth raising.
        if (refusal === 'locked') {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_LOCKED_TAPPED, {
                powerup_id: id,
                level_id: this._state?.puzzle.levelNumber ?? 0,
                // WP-65.F1 — a puzzle ordinal now; the key is kept for the schema.
                unlock_level: unlockPuzzle ?? 0,
            });
        }
        // Module 76 — short of coins: its own event (registered in the schema), and
        // the Top Bar coin badge shakes so the player sees WHAT they are short of.
        if (refusal === 'insufficientFunds') {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_UNAFFORDABLE_TAPPED, {
                powerup_id: id,
                level_id: this._state?.puzzle.levelNumber ?? 0,
                cost_coins: status.cost.coins,
                balance_coins: this._context?.economy.getBalance('coins') ?? 0,
            });
            this._coinsCue = { id: (this._coinsCue?.id ?? 0) + 1, shake: true };
        }
        // Module 76 — heard as well as read. The button is `silentTap`, so this is
        // the one sound the tap makes.
        this._playCue(BlockJamAudioId.INVALID_MOVE);
        // A "not enough coins" strip can carry a "Get coins" button, so it stays up
        // long enough to reach it.
        const noticeMs = refusal === 'insufficientFunds'
            ? BlockJamPlugin.ACTION_NOTICE_MS
            : BlockJamPlugin.LOCKED_NOTICE_MS;
        this._lockedNoticeTimer = setTimeout(() => {
            this._lockedNoticeTimer = null;
            this._refusalNotice = null;
            // Guarded: the timer is cancelled by `destroy()`, but a plugin torn down
            // between the callback firing and this line would render into nothing.
            if (this._hudRoot)
                this._renderHud();
        }, noticeMs);
        this._renderHud();
    }
    /**
     * Drop the notice and its timer. Idempotent, and renders only when there was
     * something to take away — several callers here already render afterwards.
     */
    _clearLockedNotice() {
        if (this._lockedNoticeTimer !== null) {
            clearTimeout(this._lockedNoticeTimer);
            this._lockedNoticeTimer = null;
        }
        if (this._refusalNotice === null)
            return;
        this._refusalNotice = null;
        if (this._hudRoot)
            this._renderHud();
    }
    /** Leave targeting without spending anything. */
    /**
     * Module 76 (D2) — the "Use – 25 🪙" button on an armed immediate power-up.
     * Leaves the armed state first, then commits through the one immediate path
     * (`_activateImmediate`), which re-asks the effect before it charges.
     */
    confirmPowerup() {
        const id = this._armedPowerupId;
        if (!id || activationFor(id) !== 'immediate')
            return R.err('unknown');
        this._armedPowerupId = null;
        this._board?.setTargeting(null);
        this._clearConfirmPreview();
        if (!chargesWhenSpent(id))
            return this._activateImmediate(id);
        // Module 144 — the Use press is an answer, logged with the card it answered.
        // It is NOT counted: a player who opens the mode and cancels it has spent
        // nothing and learned little (user, Bugs/Use Count.jpg — "regular" with
        // both free uses still unspent). The count moves in `_chargeSpentMode`.
        this._logConfirmAnswered(id, 'use');
        return this._activateImmediate(id);
    }
    /** The board reported a tap that picked no target — for a confirm card, that is an answer. */
    handleTargetCancelled() {
        this.cancelArm('board_tap');
    }
    /** Module 144 (WP-144.1) — which card the player gets for this power-up. */
    confirmStageFor(id) {
        return confirmStage(this._generationConfig, this._powerupConfirms[id] ?? 0);
    }
    /**
     * Module 144 — another power-up is being armed over the one whose card or
     * strip is up. The old card is answered (`switched`) and its board preview
     * goes with it: Master Key's rings stayed on the board under Colour Bomb's
     * strip (user, Bugs/Gate Glow.mp4), because only a card power-up draws a new
     * preview over the old one. Only on a SUCCESSFUL arm — a refused tap leaves
     * the old card up, preview and all.
     */
    _switchArm(id) {
        const prev = this._armedPowerupId;
        if (!prev || prev === id)
            return;
        if (activationFor(prev) === 'immediate' && chargesWhenSpent(prev)) {
            this._logConfirmAnswered(prev, 'switched');
        }
        this._clearConfirmPreview();
    }
    /**
     * Module 144 (D6/D7) — ring what the card's power-up would change; badge it on
     * a first card. Loud (breathing) while the player is still learning it.
     */
    _showConfirmPreview(id) {
        if (!this._state)
            return;
        const stage = this.confirmStageFor(id);
        const keys = confirmPreviewGates(id, this._state);
        this._confirmPreview = {
            id,
            keys,
            badges: stage === 'first' ? confirmBadgeGates(id, keys, this._state.puzzle.exits) : [],
        };
        this._board?.setGatePreview?.(keys, stage !== 'regular');
    }
    /** The block among `ids` whose centre is nearest the board's centre. */
    _middleBlock(ids) {
        if (!this._state)
            return undefined;
        const mid = this._state.puzzle.gridSize / 2;
        let best;
        for (const b of this._state.blocks) {
            if (!ids.includes(b.id))
                continue;
            const bb = boundingBox(b);
            const d = Math.hypot(b.position.col + bb.width / 2 - mid, b.position.row + bb.height / 2 - mid);
            if (!best || d < best.d)
                best = { id: b.id, d };
        }
        return best?.id;
    }
    _clearConfirmPreview() {
        if (!this._confirmPreview)
            return;
        const wasTarget = activationFor(this._confirmPreview.id) === 'target';
        this._confirmPreview = null;
        if (wasTarget)
            this._board?.setTargetPulse?.(false, false);
        else
            this._board?.setGatePreview?.(null, false);
    }
    /** Each id's starting count for a save that predates `powerupConfirms`. */
    _seedPowerupConfirms() {
        const seeded = {};
        for (const record of Object.values(this._trackProgress)) {
            for (const [id, n] of Object.entries(record?.powerupFreeUsed ?? {})) {
                if (n > (seeded[id] ?? 0))
                    seeded[id] = n;
            }
        }
        return seeded;
    }
    _logConfirmAnswered(id, answer) {
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_CONFIRM_ANSWERED, {
            powerup_id: id,
            level_id: this._state?.puzzle.levelNumber ?? 0,
            stage: this.confirmStageFor(id),
            answer,
        });
    }
    /**
     * Module 90 (D4) — close an open mode that has not been used. Nothing was
     * charged when it opened, so nothing is refunded and nothing is spent; the
     * free use (if one was going to cover it) is untouched too. Its button and
     * the ✕ on its strip both land here.
     *
     * Module 91 — takes the id: Wild Gates and Master Key can both be open, and
     * a cancel closes only the one asked for. With no id it closes the first
     * open one (the ✕ path before Module 91 passed none).
     */
    cancelOpenPowerup(id) {
        if (!this._state)
            return;
        const open = openUnpaidPowerups(this._state);
        const target = id ?? open[0];
        const close = target && open.includes(target) ? POWERUP_EFFECTS[target]?.close : undefined;
        if (!target || !close)
            return;
        this._logOpenCancelled(this._state, target, 'button');
        this._state = close(this._state);
        this._activeFreeUses.delete(target);
        this._board?.setBoard(this._state);
        this._renderHud();
    }
    _logOpenCancelled(state, id, reason) {
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_CANCELLED, {
            powerup_id: id,
            level_id: state.puzzle.levelNumber,
            moves_open: id === SURGE_ID ? surgeMovesOpen(state) : rainbowMovesOpen(state),
            reason,
        });
    }
    cancelArm(answer) {
        if (!this._armedPowerupId)
            return;
        // Module 144 (WP-144.1) — a cancel of a confirm card is an answer worth
        // logging; a targeting cancel (Hammer, Colour Bomb) and a cancel with no
        // reason (the programmatic move) are not. Writes no save either way.
        if (answer &&
            activationFor(this._armedPowerupId) === 'immediate' &&
            chargesWhenSpent(this._armedPowerupId)) {
            this._logConfirmAnswered(this._armedPowerupId, answer);
        }
        this._armedPowerupId = null;
        this._board?.setTargeting(null);
        this._board?.setGateTargeting?.(null);
        this._clearConfirmPreview();
        // WP-50.27 — no `setGatesRainbow(false)` here any more, and its absence is
        // load-bearing. The gates used to be painted while a power-up was ARMED, so
        // every disarm turned them off. They are now painted because the board is in
        // rainbow MODE, which arming has nothing to do with: arming a Hammer and
        // cancelling it while a paid rainbow is open would have switched the paint
        // off and left the exit rule on — the two-truths bug this WP exists to
        // remove, reintroduced from the other end.
        this._renderHud();
    }
    /**
     * The player tapped a valid target. This is the commit point: charge first,
     * and only apply the effect if the charge succeeded. A refusal here disarms
     * rather than leaving the board stuck in targeting with no way forward.
     */
    handleTargetPicked(blockId) {
        const id = this._armedPowerupId;
        if (!id || !this._state)
            return;
        const charged = this._chargeCounted(id);
        this._armedPowerupId = null;
        this._board?.setTargeting(null);
        this._clearConfirmPreview();
        // No `setGatesRainbow(false)` — see `cancelArm`. Using a Hammer must not
        // cancel a rainbow the player is part-way through spending.
        if (!charged.ok) {
            this._playCue(BlockJamAudioId.INVALID_MOVE);
            this._renderHud();
            return;
        }
        // All three effects are registered as of WP-35.21. `applyEffect` returning
        // null now means only "nothing to do" — a target that left the board between
        // arming and the tap, or a power-up added to config without an effect.
        const before = this._state.blocks;
        const result = applyEffect(id, this._state, blockId);
        if (result) {
            this._pushHistory();
            const removed = before.filter((b) => result.removedIds.includes(b.id));
            // Module 140 (D8) — the picked block leads: a `'bomb'` lands on the first.
            removed.sort((a, b) => Number(b.id === blockId) - Number(a.id === blockId));
            this._state = { ...this._state, blocks: result.blocks };
            // Board first, FX second: the FX draws from the snapshot, so the board is
            // already correct — and already winnable — while the shards are flying.
            this._board?.setBoard(this._state);
            // WP-37.5 gave each power-up its own impact cue (locked decision 8
            // reversing WP-35.19's shared `POWERUP_SMASH`); WP-37.10 moved it HERE,
            // next to the burst it belongs to rather than two statements ahead of it
            // (Module 37 §5). Still read from the effect's declaration and never
            // branched on the id — the same rule `fx` above follows.
            //
            // Rainbow Gates gets exactly one cue, at this impact, even though it is
            // the one power-up with a STATE (T3): the rainbow gates are what the
            // player sees WHILE choosing, and that phase already sounds —
            // `powerup_armed` opens it and this closes it. A second sustained cue
            // under the targeting would be describing a paint job.
            //
            // Module 139 (D7) — the board reports the impact beat and the cue rides
            // it. For the Hammer that is the frame the head lands, ~200ms after the
            // tap; for every other style it is the tap itself, as before. The board
            // calls it exactly once whatever happens to the strike, so the sound is
            // never lost; with no board at all there is nothing to wait for.
            const impact = POWERUP_EFFECTS[id]?.impactAudioId;
            const onImpact = () => {
                if (impact)
                    this._playCue(impact);
            };
            const fx = POWERUP_EFFECTS[id]?.fx ?? 'smash';
            if (this._board)
                this._board.playRemoveFx(removed, fx, undefined, onImpact);
            else
                onImpact();
            this._afterPowerup(result.removedIds);
        }
        this.completePowerup(id, { blocks_removed: result?.removedIds.length ?? 0 });
        this._renderHud();
    }
    /**
     * Module 67 (WP-67.B4) — the player tapped a valid GATE target. Mirrors
     * `handleTargetPicked` with `_activateImmediate`'s order: the effect is
     * asked whether it has anything to do BEFORE the charge, so a key that went
     * stale between arming and the tap costs nothing.
     *
     * Deliberately NOT `_pushHistory()`, and the state swap keeps the blocks
     * untouched: the widen is puzzle geometry, not a board position. Undo
     * restores `blocks` only, so a paid widen survives undo by construction —
     * the same argument `_activateImmediate` makes for the rainbow. Pinned by
     * `bj-gate-widener.test.ts`.
     */
    handleGateTargetPicked(gateKey) {
        const id = this._armedPowerupId;
        if (!id || !this._state)
            return;
        const next = applyGateEffect(id, this._state, gateKey);
        this._armedPowerupId = null;
        this._board?.setGateTargeting?.(null);
        if (!next) {
            // Nothing to do — a disarm, not a spend.
            this._renderHud();
            return;
        }
        const charged = this.usePowerup(id);
        if (!charged.ok) {
            this._playCue(BlockJamAudioId.INVALID_MOVE);
            this._renderHud();
            return;
        }
        this._state = next;
        this._board?.setBoard(this._state);
        // The gate growing IS the effect — its impact beat, read from the effect's
        // declaration like every other cue in this file.
        const impact = POWERUP_EFFECTS[id]?.impactAudioId;
        if (impact)
            this._playCue(impact);
        this.completePowerup(id, { gate: gateKey });
        this._renderHud();
    }
    /**
     * Win/stuck bookkeeping after an effect removed blocks. Deliberately NOT
     * `_afterMove`: a power-up is not a move (it must not bump the move count or
     * clear a hint), and locked decision 19 says removal can never make a
     * slide-out board unsolvable, so there is no solver re-validation here.
     */
    _afterPowerup(_removedIds) {
        if (!this._state)
            return;
        this._selectedBlockId = null;
        if (this._state.blocks.length === 0) {
            // Same order as `_afterMove` (WP-36.5): bank first, celebrate second.
            // The pre-generation is scheduled inside the win sequence, once the
            // removal FX has played — a power-up's smash burst deserves the same
            // protection from a 70–1600ms level build as a gate exit does.
            this.onLevelComplete();
            this._startWinSequence();
        }
        else if (hasNoFreeMoves(this._state)) {
            this._isStuck = true;
        }
    }
    // ─── Economy ──────────────────────────────────────────────────────────────
    onLevelComplete() {
        // Module 67 (WP-67.C4) — the daily pays its own once-per-day reward and
        // never touches the track ladder's maxPaidOrdinal.
        if (this._dailyMode) {
            this._onDailyComplete();
            return;
        }
        if (!this._context || this._levelCompleteGranted)
            return;
        this._levelCompleteGranted = true;
        const band = this._state?.puzzle.difficulty ?? 'Easy';
        // WP-35.22 — the tier the level's difficulty SCORE earned. Module 96: it no
        // longer chooses the amount; it is still reported below, so dashboards that
        // group clears by tier keep their meaning.
        const difficulty = rewardTierForScore(this._generationConfig, this._levelScore, band);
        const levelNumber = this._state?.puzzle.levelNumber ?? 0;
        // WP-50.25, re-keyed onto the track by WP-65.B2 — a puzzle pays ONCE, ever.
        const firstClear = this._ordinal > this._maxPaidOrdinal();
        // Reported, no longer paid differently: Module 96 retired the endless trickle.
        const endless = this._ordinal > rampLengthFor(this._generationConfig, this._track);
        // Module 96 (WP-96.1) — one continuous curve, the same on every track.
        // Module 97 — keyed on the track puzzle, not the board's level: the endless
        // phase cycles its difficulty down and back up, and pay must not follow it
        // down. See `reward-curve.ts`.
        const fullAmount = coinsForTrackPuzzle(this._generationConfig, this._track, this._ordinal, this._context.remoteConfig.get(REMOTE_CONFIG_KEYS.REWARD_BASE, REWARD_CURVE_BASE), this._context.remoteConfig.get(REMOTE_CONFIG_KEYS.REWARD_PER_LEVEL, REWARD_CURVE_PER_LEVEL));
        // Module 83 (WP-83.7) — a puzzle cleared after its solution was revealed
        // advances the track but pays nothing and is never counted as solved
        // (user decision 2026-10-02): the session's reveal set says so, the clear
        // is still "paid" so a later lap cannot collect, and `revealed` joins
        // `skipped` in `_puzzlesSolved`.
        const revealed = this._revealedHere();
        const amount = !firstClear || revealed ? 0 : fullAmount;
        this._coinsEarned = amount;
        this._replayNoReward = !firstClear && !revealed;
        this._revealedNoReward = revealed;
        if (firstClear) {
            const progress = this._trackProgress[this._track];
            this._trackProgress[this._track] = {
                ...progress,
                maxPaidOrdinal: this._ordinal,
                ...(revealed ? { revealed: (progress?.revealed ?? 0) + 1 } : {}),
            };
            if (amount > 0)
                this._wallet.credit('coins', amount, LEVEL_COMPLETE_SOURCE_ID);
        }
        // No cue here any more (WP-37.8). This runs at win DETECTION, ~250ms before
        // the confetti; `_playLevelCompleteCue` rides the burst instead.
        this._context.analytics.logEvent(ANALYTICS_EVENTS.LEVEL_COMPLETED, {
            // WP-41.10 — `level_id` was missing, even though the master schema has
            // listed it as required for this event since it was registered. It is
            // added here because something now READS it: the embed's ad controller
            // paces midgame ads off level number, and it learns the number the same
            // way any analytics consumer would. The game is not told why.
            level_id: levelNumber,
            difficulty,
            amount,
            // Both, so a payout can be traced back: which band the level was, and
            // which tier its score actually earned.
            band,
            difficulty_score: this._levelScore,
            // WP-50.25 — without this, a replay is indistinguishable from a first
            // clear that happened to pay 0, and every "coins per level" figure drawn
            // from this event would be an average over two different things.
            first_clear: firstClear,
            // Module 65 — without these, a trickle is indistinguishable from a full
            // clear that happened to be an Easy board, and every coins-per-clear
            // figure drawn from this event would average two different things.
            track: this._track,
            puzzle: this._ordinal,
            endless,
            // Module 79 (WP-79.11) — first-clears summed over every track, after this
            // clear. A fact about progress like the rest of this event; the embed
            // shell happens to submit it to a leaderboard, and the game is not told.
            puzzles_solved: this._puzzlesSolved(),
            // Module 132 (WP-132.8) — the Expert share of it, for `hard_puzzles_solved`.
            hard_puzzles_solved: this._solvedOn('expert'),
            revealed,
            // Module 114 (WP-114.6) — how many moves the win took (LEVEL_FAILED already carries it).
            move_count: this._state?.moveCount ?? 0,
        });
        // Module 116 (D7) — the first-visit funnel's wins.
        if (firstClear && this._track === 'beginner' && this._ordinal <= 3) {
            this._reachMilestone(`puzzle${this._ordinal}_won`);
        }
        // Module 134 — BEFORE the milestone win below: the win that unlocks the streak
        // (O3) doesn't count, so this reads the wins counter as it stood.
        this._winStreakEvent({ type: 'win', puzzle: this._streakPuzzle(), firstClear, revealed });
        // Module 118 — quests. A revealed puzzle is never counted as solved (Module 83).
        if (!revealed)
            this._questEvent({ type: 'win', movesLeft: this._movesLeftNow() }, false);
        // Module 132 — milestones, same rule.
        if (!revealed) {
            this._milestoneEvent({
                type: 'win',
                movesLeft: this._ownMovesLeft(),
                track: this._track,
                powerupFree: Object.keys(this._powerups?.usedThisLevel() ?? {}).length === 0,
                streakBest: this._winStreak.best,
            }, false);
        }
        this._buildWinLine();
        // Module 96 (WP-96.3) — AFTER the completion is logged: an embed host arms
        // its next-puzzle ad off that event, and its answer decides whether this
        // win may offer the doubling ad at all.
        this._winAdMoment = this._context.host?.winAdMoment?.() ?? null;
        this._commitState();
    }
    /** Module 134 — the puzzle as the streak names it. */
    _streakPuzzle() {
        return `${this._track}:${this._ordinal}`;
    }
    /**
     * Module 134 — one streak event, and the step reward it reached. The streak counts
     * only from its unlock (O3). The caller commits. A refused credit keeps the step
     * paid and never loops, as a mission's claim does.
     */
    _winStreakEvent(evt) {
        if (evt.type === 'win')
            this._streakWin = null;
        if (!isUnlocked('winStreak', counterOf(this._milestones, 'wins')))
            return;
        const before = this._winStreak.current;
        const bestBefore = this._winStreak.best;
        const update = applyWinStreakEvent(this._winStreak, evt);
        this._winStreak = update.state;
        const reward = update.reached === null ? null : streakStepReward(update.reached);
        if (evt.type === 'win' && update.state.current > before) {
            this._streakWin = {
                current: update.state.current,
                reached: update.reached,
                coins: reward?.coins ?? 0,
                tickets: reward?.tickets ?? 0,
            };
        }
        // D4 — the gentle line, only for a streak worth saving.
        if (update.broke >= STREAK_STAKE_FROM)
            this._noteStreak('ended', update.broke);
        // WP-134.5 — `best_win_streak` reads `best` off this event, so a new best between
        // steps must log too. One event per win: a step already carries the best.
        if (update.reached !== null)
            this._logStreak('step', update.reached);
        else if (update.state.best > bestBefore)
            this._logStreak('best', update.state.best);
        if (update.broke > 0)
            this._logStreak('broken', update.broke);
        if (!reward)
            return;
        try {
            if (reward.sourceId)
                this._wallet.credit('coins', reward.coins, reward.sourceId);
        }
        catch {
            // Refused locally (the daily cap, O4): the step stays paid.
        }
        if (reward.tickets > 0)
            this._powerups?.grantBonusFreeUses(reward.tickets);
    }
    /**
     * Module 134 (D3) — keep the streak at the wall: pay, then retry the same puzzle
     * with its rescues handed back. Out of coins, nothing changes and the attempt is
     * still in progress, so the Shop can open and the player can come back to this.
     */
    saveWinStreak() {
        const puzzle = this._streakPuzzle();
        if (!this._state || !this.isOutOfMoves || !this._streakAtStake())
            return 'unavailable';
        if (!canSaveStreak(this._winStreak, puzzle))
            return 'unavailable';
        const paid = this._wallet.debit('coins', streakSavePrice(this._winStreak.current), STREAK_SAVE_REASON_ID);
        if (!paid.ok)
            return 'short';
        this._winStreak = applyWinStreakEvent(this._winStreak, { type: 'saved', puzzle }).state;
        this._logStreak('saved', this._winStreak.current);
        this._restart({ streakSaved: true });
        this._noteStreak('kept', this._winStreak.current);
        this._commitState();
        this._renderHud();
        return 'ok';
    }
    _logStreak(stage, value) {
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.WIN_STREAK, {
            stage,
            value,
            best: this._winStreak.best,
        });
    }
    _noteStreak(kind, value) {
        this._streakNoticeSeq += 1;
        this._streakNotice = { seq: this._streakNoticeSeq, kind, value, best: this._winStreak.best };
    }
    /**
     * Module 134 — would ending this attempt break a streak worth keeping? Not in the
     * Daily, a replay, a revealed puzzle, or one whose first try is already spent:
     * those are outside the streak (`meta/win-streak.ts`), so nothing is warned or sold.
     */
    _streakAtStake() {
        return (this._state !== null &&
            !this._dailyMode &&
            this._winStreak.current >= STREAK_STAKE_FROM &&
            this._ordinal > this._maxPaidOrdinal() &&
            !this._revealedHere() &&
            this._winStreak.spentOn !== this._streakPuzzle());
    }
    /** Module 134 — the streak's screens, once it is open (null before its unlock). */
    _winStreakHud() {
        if (featureState('winStreak', counterOf(this._milestones, 'wins')).kind !== 'open')
            return null;
        const atStake = this._streakAtStake();
        const canSave = atStake && canSaveStreak(this._winStreak, this._streakPuzzle());
        const savePrice = canSave ? streakSavePrice(this._winStreak.current) : 0;
        return {
            current: this._winStreak.current,
            best: this._winStreak.best,
            atStake,
            savePrice,
            affordable: canSave && this._wallet.getBalance('coins') >= savePrice,
            win: this._dailyMode ? null : this._streakWin,
            notice: this._streakNotice,
        };
    }
    /** Module 79 — every puzzle ever first-cleared, across the three tracks. */
    _puzzlesSolved() {
        return TRACK_IDS.reduce((sum, id) => sum + this._solvedOn(id), 0);
    }
    /** First clears on one track. */
    _solvedOn(id) {
        // Module 80 (WP-80.18) — a skipped puzzle is passed, never solved: the
        // next clear lifts `maxPaidOrdinal` over it, so it is taken back out here.
        return Math.max(0, this._maxPaidOrdinal(id) -
            (this._trackProgress[id]?.skipped ?? 0) -
            // Module 83 — a revealed puzzle is never "solved" for a leaderboard.
            (this._trackProgress[id]?.revealed ?? 0));
    }
    onAppBackground() {
        this._commitState();
    }
    /** Module 141 — the gift card while it shows. Test seam. */
    get adGift() {
        return this._adGift;
    }
    /** Module 141 — the card finished (or was skipped to its end). */
    _endAdGift(seq) {
        if (this._adGift?.seq !== seq)
            return;
        if (this._adGiftTimer !== null)
            clearTimeout(this._adGiftTimer);
        this._adGiftTimer = null;
        this._adGift = null;
        this._renderHud();
    }
    /**
     * Module 96 (WP-96.5) — the host reports an ad break the player sat through.
     * The host names the amount; the game credits it under its own reason id
     * and says thanks, the Shop top-up's split.
     */
    _onAdBreakWatched() {
        const coins = this._context?.host?.adBreakThanks?.coins ?? 0;
        if (!this._context || coins <= 0)
            return;
        this._wallet.credit('coins', coins, REWARD_AD_BREAK_THANKS_SOURCE_ID);
        this._commitState();
        // Module 141 — the gift card, centred, in place of a bottom toast.
        const seq = ++this._adGiftSeq;
        this._adGift = { coins, seq };
        if (this._adGiftTimer !== null)
            clearTimeout(this._adGiftTimer);
        this._adGiftTimer = setTimeout(() => this._endAdGift(seq), BlockJamPlugin.AD_GIFT_MAX_MS);
        this._renderHud();
    }
    /**
     * Module 96 (WP-96.3) — whether this win may offer the doubling ad at all.
     * True when the host does not pace wins (`null`), so a host without
     * `winAdMoment` keeps WP-41.11's behaviour.
     */
    get doubleCoinsOffered() {
        return this._winAdMoment === null || this._winAdMoment === 'doubleOffer';
    }
    /** The win popup's "double your coins" offer state (WP-41.11). */
    get doubleCoinsState() {
        return this._doubleCoins;
    }
    /**
     * Say the ad did not happen, then put the offer back.
     *
     * Modelled on `_showRefusalNotice`, including the two things that are easy to
     * miss: the timer is cleared before it is re-armed (a second failed attempt
     * must not stack a second timer), and the callback checks the HUD still
     * exists, because a plugin torn down between the timer firing and this line
     * would render into nothing.
     */
    /** Drop the retry timer. Idempotent; renders nothing of its own. */
    _clearDoubleCoinsRetry() {
        if (this._doubleCoinsRetryTimer === null)
            return;
        clearTimeout(this._doubleCoinsRetryTimer);
        this._doubleCoinsRetryTimer = null;
    }
    _showDoubleCoinsUnavailable() {
        if (this._doubleCoinsRetryTimer !== null)
            clearTimeout(this._doubleCoinsRetryTimer);
        this._doubleCoins = 'unavailable';
        this._doubleCoinsRetryTimer = setTimeout(() => {
            this._doubleCoinsRetryTimer = null;
            // Only back to `offer` from `unavailable`: the level may have moved on,
            // and re-offering a doubling for a payout that no longer exists is worse
            // than the silence this replaced.
            if (this._doubleCoins !== 'unavailable')
                return;
            this._doubleCoins = 'offer';
            if (this._hudRoot)
                this._renderHud();
        }, BlockJamPlugin.AD_UNAVAILABLE_MS);
        this._renderHud();
    }
    /**
     * The player took the "watch an ad, double your coins" offer.
     *
     * Credited **only** on a completed ad. A failure costs them nothing: they keep
     * the reward they already earned, see no error popup, and the offer comes
     * back. That is the platform's requirement and also the only humane reading —
     * the player did what was asked and an ad server did not.
     */
    async requestDoubleCoins() {
        const offer = this._context?.host?.offerRewardedAd;
        // `_isWon` matters: the offer belongs to the win popup, and a caller
        // reaching this any other way is asking to double a payout that has not
        // happened. `_levelCompleteGranted` is the same guard from the other side.
        if (!this._context || !offer)
            return;
        if (this._doubleCoins !== 'offer' || !this._levelCompleteGranted)
            return;
        // Module 96 — the host said this win has no offer (its ad break is due, or
        // the random share passed it by). The button is not rendered either.
        if (!this.doubleCoinsOffered)
            return;
        // WP-50.25 — twice nothing is nothing. A replay pays 0, and an ad that
        // doubles 0 is the clearest possible way to teach a player that rewarded
        // offers in this game are not worth taking. The button is not rendered in
        // that state either; this is the same rule from the other side.
        if (this._coinsEarned <= 0)
            return;
        // Captured BEFORE the await. `_coinsEarned` is reset by next-level/restart/
        // jump, any of which the player could reach if the ad were dismissed and the
        // popup interacted with; doubling would then credit whatever the field holds
        // at that later moment, which is 0 at best and the next level's reward at
        // worst.
        const base = this._coinsEarned;
        const placement = 'level_complete_double_coins';
        this._doubleCoins = 'pending';
        this._renderHud();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_SHOWN, { placement });
        const watched = await offer(placement);
        // The plugin can be destroyed while an ad plays — the tab was closed, the
        // player left. Crediting into a torn-down context writes to a wallet nothing
        // will persist.
        if (!this._context)
            return;
        if (!watched) {
            // WP-51.4 — say so, briefly, instead of silently restoring an identical
            // button. See `_doubleCoins`' doc for why the silence was the bug.
            this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_FAILED, { placement });
            this._showDoubleCoinsUnavailable();
            return;
        }
        this._wallet.credit('coins', base, REWARD_AD_DOUBLE_SOURCE_ID);
        this._coinsEarned = base * 2;
        this._doubleCoins = 'granted';
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_COMPLETED, { placement });
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_DOUBLED, {
            base_reward: base,
            final_reward: this._coinsEarned,
        });
        this._commitState();
        this._renderHud();
    }
    // ─── The move budget (Module 50, WP-50.28a) ───────────────────────────────
    /** Moves left on this attempt, or null on a board carrying no budget. */
    get movesRemaining() {
        return this._state ? movesRemaining(this._state) : null;
    }
    /** The budget for this attempt, or null on a board carrying no budget. */
    get moveBudget() {
        return this._state?.moveBudget ?? null;
    }
    /**
     * Everything {@link activeModal} ranks, gathered from the plugin's own state.
     *
     * `budgetSpent` is the RAW fact — `moveCount >= moveBudget`, unqualified. The
     * qualifications are the arbiter's job; folding any of them in here is how
     * this ended up with a different answer in each of four expressions.
     */
    _modalView() {
        return {
            isWon: this._isWon,
            isCelebrating: this._isCelebrating,
            isStuck: this._isStuck,
            budgetSpent: this._state ? isOutOfMoves(this._state) : false,
            blocksLeft: this._state ? this._state.blocks.length : null,
        };
    }
    /**
     * The budget is spent — the wall is up and the popup is showing.
     *
     * Module 53 (WP-53.1): was `isOutOfMoves(state) && !_isWon && !_isStuck`,
     * which is true for the whole ~1.4s of a winning last move's celebration,
     * because `_isWon` is not set until the confetti ends. See
     * {@link ./ui/active-modal} for the recording and the reasoning.
     */
    get isOutOfMoves() {
        return activeModal(this._modalView()) === 'outOfMoves';
    }
    /**
     * No legal move remains — and no win is in flight.
     *
     * Reads the same arbiter as {@link isOutOfMoves} rather than exposing
     * `_isStuck` directly, so the two popups cannot both claim the screen and the
     * ranking has one home.
     */
    get isStuck() {
        return activeModal(this._modalView()) === 'stuck';
    }
    /**
     * Moves a rewarded ad is worth here, or 0 when there is no such offer —
     * because the host provides no ads, or because this attempt has spent its
     * allowance. Both halves matter: an amount with no `offerRewardedAd` is a
     * price nothing can pay, and an `offerRewardedAd` with no amount is an offer
     * with no number in it.
     *
     * The cap is per ATTEMPT and restart hands it back, which is deliberate:
     * restarting costs the player every move they had made, so the cap still
     * bounds a single attempt, which is what it is for. (The same argument
     * `_adoptLevel` already makes for power-up uses.)
     */
    get extraMovesOffer() {
        if (!this._context?.host?.offerRewardedAd || !this._state)
            return 0;
        const cfg = this._generationConfig.moveBudget;
        // Module 118 (WP-118.7) — ad and coin rescues share one cap per attempt
        // (`RESCUES_PER_ATTEMPT`), and the i-th RESCUE (of either kind) is what
        // indexes the grant: +3 then +3 since 2026-10-14 (was +2 then +1, and a
        // rescue that still fails feels like being cheated).
        const used = this._rescuesUsed();
        if (used >= Math.min(cfg.maxAdsPerLevel, RESCUES_PER_ATTEMPT))
            return 0;
        if ((this._state.movesAdsUsed ?? 0) >= cfg.maxAdsPerLevel)
            return 0;
        return cfg.adGrants[used] ?? 0;
    }
    /** Module 118 (WP-118.7) — rescues (ad + coins) already used in this attempt. */
    _rescuesUsed() {
        return (this._state?.movesAdsUsed ?? 0) + (this._state?.movesBought ?? 0);
    }
    /**
     * Module 118 (WP-118.7) — the coin rescue on offer at the wall: +5 moves for
     * the i-th rescue's price (300, then 600), or null once both rescues are used.
     * `affordable` decides whether the button buys or answers "Get coins".
     */
    get coinMovesOffer() {
        if (!this._context || !this._state)
            return null;
        const used = this._rescuesUsed();
        if (used >= RESCUES_PER_ATTEMPT)
            return null;
        const price = EXTRA_MOVES_PRICES[used];
        if (price === undefined)
            return null;
        return {
            price,
            grant: EXTRA_MOVES_GRANT,
            affordable: this._wallet.getBalance('coins') >= price,
        };
    }
    /**
     * Module 118 (WP-118.7) — buy the coin rescue. Debit first: nothing changes
     * unless it went through ('short' answers with "Get coins"). Like the ad, it
     * moves the WALL (`moveBudget += grant`), never the counter.
     */
    buyExtraMoves() {
        const offer = this.coinMovesOffer;
        if (!offer || !this._context || !this._state)
            return 'none';
        if (!offer.affordable)
            return 'short';
        const debit = this._wallet.debit('coins', offer.price, EXTRA_MOVES_SOURCE_ID);
        if (!debit.ok)
            return 'short';
        const buyIndex = this._rescuesUsed() + 1;
        this._state = {
            ...this._state,
            moveBudget: (this._state.moveBudget ?? 0) + offer.grant,
            movesBought: (this._state.movesBought ?? 0) + 1,
        };
        this._context.analytics.logEvent(ANALYTICS_EVENTS.MOVES_BOUGHT, {
            price: offer.price,
            moves: offer.grant,
            buy_index: buyIndex,
            daily: this._dailyMode,
            level: this._state.puzzle.levelNumber,
            ...(this._dailyMode ? {} : { track: this._track, puzzle: this._ordinal }),
        });
        this._commitState();
        this._renderHud();
        return 'ok';
    }
    /**
     * Module 118 (WP-118.7, D11) — one paid retry of today's Daily, offered at the
     * wall once both rescues are used (or none can be offered), once per UTC day.
     */
    get dailyRetryOffer() {
        if (!this._context || !this._state || !this._dailyMode)
            return null;
        const record = this._daily;
        if (!record || record.date !== dailyDate(this._context.clock))
            return null;
        if (record.state !== 'in-progress' || record.retried)
            return null;
        if (!this.isOutOfMoves)
            return null;
        if (this.extraMovesOffer > 0 || this.coinMovesOffer !== null)
            return null;
        return { price: DAILY_RETRY_PRICE };
    }
    /**
     * Module 118 (WP-118.7) — pay for one more try: today's board again from
     * scratch (a new attempt, rescues handed back), the record marked `retried`
     * so it cannot happen twice and the share says "2 tries".
     */
    retryDaily() {
        const offer = this.dailyRetryOffer;
        const record = this._daily;
        if (!offer || !record || !this._context || !this._state)
            return 'none';
        if (this._wallet.getBalance('coins') < offer.price)
            return 'short';
        const debit = this._wallet.debit('coins', offer.price, DAILY_RETRY_SOURCE_ID);
        if (!debit.ok)
            return 'short';
        const rest = { ...record };
        delete rest.boardState;
        this._daily = { ...rest, retried: true };
        this._context.analytics.logEvent(ANALYTICS_EVENTS.DAILY_RETRIED, {
            challenge_date: record.date,
            price: offer.price,
        });
        this._attemptSeq += 1;
        this._resetPlayFlags();
        const date = record.date;
        const level = dailyEffectiveLevel(date);
        this._whenPrepared(this._dailyPrepared(date), (built) => {
            this._state = this._adoptLevel(built, level);
            this._commitState();
            this._renderBoard();
            this._renderHud();
        });
        this._renderHud();
        return 'ok';
    }
    /**
     * Module 118 (WP-118.7) — how many moves the player was actually short when
     * the wall hit: the worker's shortest route from this board (never on the
     * main thread; skipped without a worker). Logged on its own event so the
     * out-of-moves event's timing never changes. Null `moves_short` = the search
     * gave up (the board may need more than the node cap).
     */
    _measureMovesShort() {
        const solver = this._solver;
        const state = this._state;
        if (!solver || !state || !this._context)
            return;
        const attempt = this._attemptSeq;
        const fields = {
            level: state.puzzle.levelNumber,
            track: this._dailyMode ? 'daily' : this._track,
            puzzle: this._ordinal,
            blocks_left: state.blocks.length,
            rescues_used: this._rescuesUsed(),
        };
        solver(solveRequestFor(state, SHORT_SOLVE_DEFAULT_NODES)).then((moves) => {
            if (!this._context || attempt !== this._attemptSeq)
                return;
            this._context.analytics.logEvent(ANALYTICS_EVENTS.MOVES_SHORT, {
                ...fields,
                moves_short: moves ? moves.length : null,
            });
        }, () => { });
    }
    /** An extra-moves ad is in flight. */
    get extraMovesPending() {
        return this._extraMovesPending;
    }
    /**
     * The player took the "watch an ad for +N moves" offer at the wall.
     *
     * Modelled on `requestCoinTopUp` line for line, including the part that
     * matters most: a failed or unfilled ad costs the player nothing, shows no
     * error, and leaves the offer exactly where it was. That is both the existing
     * contract and CrazyGames' requirement.
     *
     * The grant moves the WALL, not the counter — `moveBudget += grant` rather
     * than `moveCount -= grant`. Moves only ever go up (the same rule undo now
     * follows), so the pill keeps counting a real total and the analytics keep
     * meaning what they say.
     */
    async requestExtraMoves() {
        const offer = this._context?.host?.offerRewardedAd;
        const amount = this.extraMovesOffer;
        if (!this._context || !offer || amount <= 0 || !this._state)
            return;
        if (this._extraMovesPending)
            return;
        const placement = 'out_of_moves';
        const level = this._state.puzzle.levelNumber;
        const attempt = this._attemptSeq;
        this._extraMovesPending = true;
        this._renderHud();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_SHOWN, { placement, level });
        const watched = await offer(placement);
        if (!this._context || !this._state)
            return;
        this._extraMovesPending = false;
        if (attempt !== this._attemptSeq) {
            // The attempt this ad was for is gone (Module 73, WP-73.8e).
            this._renderHud();
            return;
        }
        if (!watched) {
            this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_FAILED, { placement, level });
            this._renderHud();
            return;
        }
        const adsUsed = (this._state.movesAdsUsed ?? 0) + 1;
        this._state = {
            ...this._state,
            moveBudget: (this._state.moveBudget ?? 0) + amount,
            movesAdsUsed: adsUsed,
        };
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_COMPLETED, { placement, level });
        this._context.analytics.logEvent(ANALYTICS_EVENTS.MOVE_BUDGET_GRANTED, {
            level,
            moves: amount,
            ads_used: adsUsed,
        });
        if (adsUsed >= this._generationConfig.moveBudget.maxAdsPerLevel ||
            this._rescuesUsed() >= RESCUES_PER_ATTEMPT) {
            // Logged when the allowance RUNS OUT, not when an offer is declined: this
            // is the moment the placement stops being able to earn on this attempt,
            // which is the number WP-50.15 needs to read the funnel.
            this._context.analytics.logEvent(ANALYTICS_EVENTS.MOVE_BUDGET_AD_CAPPED, {
                level,
                ads_used: adsUsed,
            });
        }
        this._commitState();
        this._renderHud();
    }
    // ─── Module 80 (Increment F) — help for a player stuck on one puzzle ────────
    /** This puzzle's counters; zero for a stale record, the Daily, or none yet. */
    /** Module 83 — was the solution of the puzzle being played paid for this session? */
    /**
     * Module 103 (WP-103.2) — the plugin-only facts the HUD's status capsule
     * rotates through. The reward mirrors `onLevelComplete`'s rule exactly (first
     * clear of this track puzzle pays the curve; a revealed puzzle pays nothing;
     * the Daily pays its own once-per-day amount), so the number shown before the
     * win is the number paid by it.
     */
    _statusInfo() {
        const blocksLeft = this._state ? this._state.blocks.length : null;
        let winReward = null;
        if (this._dailyMode) {
            const clearedToday = this._context !== null &&
                this._daily?.date === dailyDate(this._context.clock) &&
                this._daily.state === 'cleared';
            winReward = clearedToday ? null : DAILY_REWARD_COINS;
        }
        else if (this._revealedHere()) {
            winReward = 'solved';
        }
        else if (this._context && this._ordinal > this._maxPaidOrdinal()) {
            winReward = coinsForTrackPuzzle(this._generationConfig, this._track, this._ordinal, this._context.remoteConfig.get(REMOTE_CONFIG_KEYS.REWARD_BASE, REWARD_CURVE_BASE), this._context.remoteConfig.get(REMOTE_CONFIG_KEYS.REWARD_PER_LEVEL, REWARD_CURVE_PER_LEVEL));
        }
        // 2026-10-25 (user, `Bugs/Empty_1.jpeg`, `Empty_2.jpeg`) — once the win has
        // PAID, the rule above answers null (the puzzle is now "already paid"), and
        // with the blocks count gone too the capsule's right side went blank through
        // the confetti and the win popup on every puzzle without a power-up unlock
        // close by (Beginner #3 every time). Keep showing what this win paid —
        // doubled, if Double Coins doubled it — until Next or Restart resets it.
        if (winReward === null && this._coinsEarned > 0)
            winReward = this._coinsEarned;
        return { blocksLeft, winReward };
    }
    _revealedHere() {
        if (this._dailyMode)
            return false;
        // Module 129 — the persisted mark, so a reload keeps a revealed puzzle unpaid.
        const attempt = this._trackProgress[this._track]?.attempt;
        if (attempt?.ordinal === this._ordinal && attempt.revealed === true)
            return true;
        return this._revealedThisSession.has(`${this._track}:${this._ordinal}`);
    }
    _attemptRecord() {
        const none = { outOfMoves: 0, restarts: 0, highlights: 0 };
        if (this._dailyMode)
            return none;
        const a = this._trackProgress[this._track]?.attempt;
        return a && a.ordinal === this._ordinal
            ? {
                outOfMoves: a.outOfMoves,
                restarts: a.restarts,
                highlights: a.highlights ?? 0,
                // Module 129 — carried, so bumping a counter never drops the SOLVE IT mark.
                ...(a.revealed === true ? { revealed: true } : {}),
            }
            : none;
    }
    /**
     * Count one more out-of-moves, restart, hint or highlight on THIS puzzle
     * (persisted by the caller). Every counter is carried, so bumping one never
     * resets another.
     */
    _bumpAttempt(field) {
        if (this._dailyMode)
            return;
        const cur = this._attemptRecord();
        this._trackProgress[this._track] = {
            ...this._trackProgress[this._track],
            attempt: { ordinal: this._ordinal, ...cur, [field]: cur[field] + 1 },
        };
    }
    /** `blockjam_level_failed` — an attempt ended without a win. */
    _logAttemptFailed(reason) {
        if (!this._state)
            return;
        const a = this._attemptRecord();
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.LEVEL_FAILED, {
            level_id: this._state.puzzle.levelNumber,
            track: this._dailyMode ? 'daily' : this._track,
            puzzle: this._ordinal,
            reason,
            move_count: this._state.moveCount,
            time_spent_s: Math.round((Date.now() - this._attemptStartedMs) / 1000),
            out_of_moves: a.outOfMoves,
            attempt_number: a.restarts + 1,
        });
    }
    /**
     * Module 130 (D2) — the blocks the out-of-moves tray draws: the board's own,
     * while the moves are spent and only 1–3 are left (with more, the card just
     * says how many). Undefined otherwise.
     */
    get outOfMovesLeftovers() {
        const blocks = this._state?.blocks;
        return this.isOutOfMoves && blocks && blocks.length >= 1 && blocks.length <= 3
            ? blocks
            : undefined;
    }
    // ─── Module 83 (Increment B) — the REVEAL button and page ───────────────────
    _revealConfig() {
        return this._generationConfig.moveBudget.reveal.tracks[this._track];
    }
    /**
     * How a Reveal is paid for here (user decision 2026-10-02): coins AND a
     * rewarded ad where the host offers one (Playgama), coins alone where it
     * does not (PuzzleMania Web, until web ads exist). Chosen by CAPABILITY,
     * never by platform name. Never in the Daily (one attempt, no help).
     */
    _revealPayment() {
        if (this._dailyMode || !this._context)
            return null;
        const cfg = this._generationConfig.moveBudget.reveal;
        const ad = Boolean(this._context.host?.offerRewardedAd);
        const price = ad ? cfg.coins : cfg.coinsWithoutAds;
        return { price, ad };
    }
    /** The stored route for the board being played, or null (never for a generated board). */
    _revealRoute() {
        return this._currentBuilt?.metrics.solution ?? null;
    }
    /**
     * The REVEAL button as the HUD draws it, or `null` when there is none.
     * `used`: already revealed on this puzzle (the tap then says so).
     */
    get revealButton() {
        const payment = this._revealPayment();
        if (!payment || !this._state)
            return null;
        const lit = this._revealHighlight();
        return {
            used: this._revealedHere(),
            pending: this._revealPending,
            lit: lit !== null,
            tip: lit !== null && lit.tip && this._revealTipUp,
            price: payment.price,
            ad: payment.ad,
        };
    }
    /** Module 83 — the Reveal notice strip, while it is up. */
    get revealNotice() {
        return this._revealNotice;
    }
    /** Module 83 — the confirmation card, while it is up. */
    get revealConfirm() {
        return this._revealConfirm;
    }
    /** Module 83 — the Reveal page, while it is up. */
    get revealPage() {
        return this._revealPage;
    }
    /** Anything else on screen that a highlight must not compete with. */
    _revealBusy() {
        return (this._dailyMode ||
            this._revealPending ||
            this._revealConfirm !== null ||
            this._revealPage !== null ||
            this._extraMovesPending ||
            this._tutorial !== null ||
            this._unlockQueue.length > 0 ||
            this._guidedPowerupId !== null ||
            this._armedPowerupId !== null ||
            this._refusalNotice !== null ||
            this._revealNotice !== null ||
            this._isInfoOpen ||
            this._trackEntry !== null ||
            activeModal(this._modalView()) !== null);
    }
    /**
     * Module 83 (WP-83.7) — the low-moves highlight, Module 81's rules carried
     * over: decided lazily at render like Module 73's try-it glow. Once lit it
     * holds until the next move, a restart, or a tap on REVEAL; then nothing
     * re-lights it in that attempt. Lighting it counts it on the puzzle
     * (persisted), logs it, and — the first time ever, or on a `tipEveryPuzzle`
     * track the first time on a puzzle — raises the "Stuck?" tip for `tipMs`.
     */
    _revealHighlight() {
        const state = this._state;
        if (!state)
            return null;
        const lit = this._revealLit;
        if (lit !== null) {
            if (lit.attemptSeq === this._attemptSeq && lit.moveCount === state.moveCount)
                return lit;
            this._endRevealHighlight();
        }
        if (!this._revealPayment())
            return null;
        const cfg = this._revealConfig();
        const record = this._attemptRecord();
        const fire = shouldHighlightReveal(cfg, {
            movesLeft: this.movesRemaining,
            blocksLeft: state.blocks.length,
            attemptNumber: record.restarts + 1,
            revealed: this._revealedHere(),
            highlightsThisPuzzle: record.highlights,
            highlightedThisAttempt: this._revealLitAttempt === this._attemptSeq,
            busy: this._revealBusy(),
        });
        if (!fire)
            return null;
        const tipSeen = this._seenTutorials.includes(REVEAL_TIP_SEEN);
        const tip = shouldShowRevealTip(cfg, tipSeen, record.highlights);
        this._revealLit = { attemptSeq: this._attemptSeq, moveCount: state.moveCount, tip };
        this._revealLitAttempt = this._attemptSeq;
        this._bumpAttempt('highlights');
        if (tip) {
            if (!tipSeen)
                this._seenTutorials = [...this._seenTutorials, REVEAL_TIP_SEEN];
            this._revealTipUp = true;
            if (this._revealTipTimer !== null)
                clearTimeout(this._revealTipTimer);
            this._revealTipTimer = setTimeout(() => {
                this._revealTipTimer = null;
                this._revealTipUp = false;
                if (this._hudRoot)
                    this._renderHud();
            }, this._generationConfig.moveBudget.reveal.tipMs);
        }
        this._commitState();
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.REVEAL_HIGHLIGHTED, {
            level: state.puzzle.levelNumber,
            track: this._track,
            puzzle: this._ordinal,
            moves_left: this.movesRemaining ?? 0,
            blocks_left: state.blocks.length,
            tip,
        });
        return this._revealLit;
    }
    /** Put the highlight (and its tip) away. */
    _endRevealHighlight() {
        this._revealLit = null;
        this._revealTipUp = false;
        if (this._revealTipTimer !== null) {
            clearTimeout(this._revealTipTimer);
            this._revealTipTimer = null;
        }
    }
    /** The player tapped anywhere while the tip was up: it goes, the ring stays. */
    dismissRevealTip() {
        if (!this._revealTipUp)
            return;
        this._revealTipUp = false;
        if (this._revealTipTimer !== null) {
            clearTimeout(this._revealTipTimer);
            this._revealTipTimer = null;
        }
        this._renderHud();
    }
    _clearRevealTimers() {
        this._endRevealHighlight();
        if (this._revealNoticeTimer !== null) {
            clearTimeout(this._revealNoticeTimer);
            this._revealNoticeTimer = null;
        }
        this._revealNotice = null;
        this._revealConfirm = null;
        this._revealPage = null;
    }
    _showRevealNotice(notice) {
        if (this._revealNoticeTimer !== null)
            clearTimeout(this._revealNoticeTimer);
        this._revealNotice = notice;
        // A refusal is heard (Module 76).
        this._playCue(BlockJamAudioId.INVALID_MOVE);
        const ms = notice.kind === 'short' ? BlockJamPlugin.ACTION_NOTICE_MS : BlockJamPlugin.LOCKED_NOTICE_MS;
        this._revealNoticeTimer = setTimeout(() => {
            this._revealNoticeTimer = null;
            this._revealNotice = null;
            if (this._hudRoot)
                this._renderHud();
        }, ms);
        this._renderHud();
    }
    _clearRevealNotice() {
        if (this._revealNoticeTimer !== null) {
            clearTimeout(this._revealNoticeTimer);
            this._revealNoticeTimer = null;
        }
        this._revealNotice = null;
    }
    _logRevealTapped(outcome, highlighted) {
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.REVEAL_TAPPED, {
            level: this._state?.puzzle.levelNumber ?? 0,
            track: this._track,
            puzzle: this._ordinal,
            moves_left: this.movesRemaining ?? 0,
            highlighted,
            outcome,
        });
    }
    /**
     * Module 83 (WP-83.7) — the REVEAL button: the stored solution of this
     * puzzle, shown on a full-screen page after the player confirms the terms.
     * Nothing is charged here: the tap opens the confirmation card, or says why
     * it cannot (no stored route). A puzzle already PAID for re-opens its page
     * at once, free — "once per puzzle" means pay once (user, 2026-10-02: the
     * page was lost to a bug and a reload, and every later tap refused). A tap
     * ends the low-moves highlight either way.
     */
    requestReveal() {
        const payment = this._revealPayment();
        const state = this._state;
        if (!this._context || !payment || !state)
            return;
        if (this._revealPending || this._revealConfirm || this._revealPage)
            return;
        if (this._extraMovesPending)
            return;
        if (activeModal(this._modalView()) !== null)
            return;
        const highlighted = this._revealLit !== null;
        this._endRevealHighlight();
        this._clearRevealNotice();
        const route = this._revealRoute();
        if (route === null) {
            this._logRevealTapped('unavailable', highlighted);
            this._showRevealNotice({ kind: 'unavailable' });
            return;
        }
        if (this._revealedHere()) {
            this._logRevealTapped('reopen', highlighted);
            this._revealPage = {
                board: this._currentBuilt.state,
                moves: route,
                budget: this._state?.moveBudget ?? route.length,
            };
            this._renderHud();
            return;
        }
        this._logRevealTapped('confirm', highlighted);
        this._revealConfirm = payment;
        this._renderHud();
    }
    /** "Not now" on the confirmation card. */
    cancelReveal() {
        if (!this._revealConfirm)
            return;
        this._logRevealTapped('cancel', false);
        this._revealConfirm = null;
        this._renderHud();
    }
    /**
     * "Reveal" on the confirmation card (user decision 2026-10-02): the balance
     * is checked, the coins are debited, THEN the rewarded ad plays where the
     * host has one — and the page opens whatever the ad did ("on failed ads,
     * just debit the coins and show solution"). Short of coins: Module 76's
     * strip with "Get coins", nothing charged.
     */
    async confirmReveal() {
        const confirm = this._revealConfirm;
        const state = this._state;
        const route = this._revealRoute();
        if (!this._context || !confirm || !state || !route)
            return;
        this._revealConfirm = null;
        // Module 90 — against the coins NOT held for an open Wild Gates, so a
        // Reveal can never leave the exit that uses it unable to pay.
        const spendable = this._powerups?.spendableCoins() ?? this._context.economy.getBalance('coins');
        if (spendable < confirm.price) {
            this._logRevealTapped('short', false);
            this._coinsCue = { id: (this._coinsCue?.id ?? 0) + 1, shake: true };
            this._showRevealNotice({ kind: 'short', price: confirm.price });
            return;
        }
        const paid = this._wallet.debit('coins', confirm.price, REVEAL_REASON_ID);
        if (!paid.ok) {
            this._coinsCue = { id: (this._coinsCue?.id ?? 0) + 1, shake: true };
            this._showRevealNotice({ kind: 'short', price: confirm.price });
            return;
        }
        const level = state.puzzle.levelNumber;
        let adWatched = false;
        const offer = confirm.ad ? this._context.host?.offerRewardedAd : undefined;
        // Module 110 (D8) — the price is SEEN leaving for SOLVE IT before anything
        // covers the screen (the ad, or the walkthrough page on a host without
        // ads): before this the −N played under the ad and was never seen.
        this._revealPending = true;
        this._renderHud();
        await this._spendBeat();
        if (!this._context || !this._state)
            return;
        this._revealPending = false;
        if (offer) {
            const placement = 'reveal';
            this._revealPending = true;
            this._revealAdInFlight = true;
            this._renderHud();
            this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_SHOWN, { placement, level });
            adWatched = await offer(placement);
            this._revealAdInFlight = false;
            if (!this._context || !this._state)
                return;
            this._revealPending = false;
            this._context.analytics.logEvent(adWatched ? ANALYTICS_EVENTS.REWARD_AD_COMPLETED : ANALYTICS_EVENTS.REWARD_AD_FAILED, { placement, level });
        }
        this._openRevealPage(route, confirm, adWatched);
    }
    _spendBeat() {
        if (this._reducedMotionFn() || !this._hudRoot)
            return Promise.resolve();
        return new Promise((resolve) => setTimeout(resolve, BlockJamPlugin.SPEND_BEAT_MS));
    }
    _openRevealPage(moves, confirm, adWatched) {
        const built = this._currentBuilt;
        if (!this._context || !this._state || !built)
            return;
        this._revealedThisSession.add(`${this._track}:${this._ordinal}`);
        // Module 129 (WP-129.3) — saved with the attempt record (by the commit below).
        const cur = this._attemptRecord();
        this._trackProgress[this._track] = {
            ...this._trackProgress[this._track],
            attempt: { ordinal: this._ordinal, ...cur, revealed: true },
        };
        const budget = this._state.moveBudget ?? moves.length;
        this._revealPage = { board: built.state, moves, budget };
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REVEAL_SHOWN, {
            level: this._state.puzzle.levelNumber,
            track: this._track,
            puzzle: this._ordinal,
            moves: moves.length,
            budget,
            cost_coins: confirm.price,
            ad: confirm.ad,
            ad_watched: adWatched,
        });
        this._commitState();
        this._renderHud();
    }
    /**
     * "Done" on the Reveal page: the page closes and the puzzle RESTARTS so the
     * player plays it through themselves (user decision 2026-10-02).
     */
    closeReveal(step) {
        const page = this._revealPage;
        if (!page || !this._state)
            return;
        this._revealPage = null;
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.REVEAL_DONE, {
            level: this._state.puzzle.levelNumber,
            track: this._track,
            puzzle: this._ordinal,
            step,
            total: page.moves.length,
        });
        this._restart();
    }
    /**
     * Coins the minimal ads-only Shop's "watch an ad" row is worth, or 0 where
     * the host offers none — same both-halves reasoning as `extraMovesOffer`.
     */
    get shopCoinTopUpAmount() {
        const host = this._context?.host;
        if (!host?.offerRewardedAd)
            return 0;
        return host.shopCoinTopUp ?? 0;
    }
    /**
     * Module 123 — how long until the home Free Coins button is ready, in ms
     * (0 = ready). Clamped to the cooldown, so a device clock moved backwards
     * can never stretch the wait past one cooldown.
     */
    get freeCoinsCooldownMs() {
        if (!this._context || this._freeCoinsReadyAt <= 0)
            return 0;
        const left = this._freeCoinsReadyAt - this._context.clock.now();
        return Math.min(Math.max(0, left), FREE_COINS_COOLDOWN_MS);
    }
    /** Module 123 — the home button's last ad was not completed (shown for 4 s). */
    get freeCoinsUnavailable() {
        return this._freeCoinsUnavailable;
    }
    _clearFreeCoinsRetry() {
        if (this._freeCoinsRetryTimer === null)
            return;
        clearTimeout(this._freeCoinsRetryTimer);
        this._freeCoinsRetryTimer = null;
    }
    /** Double Coins' `_showDoubleCoinsUnavailable`, for the home button. */
    _showFreeCoinsUnavailable() {
        this._clearFreeCoinsRetry();
        this._freeCoinsUnavailable = true;
        this._freeCoinsRetryTimer = setTimeout(() => {
            this._freeCoinsRetryTimer = null;
            this._freeCoinsUnavailable = false;
            if (this._hudRoot)
                this._renderHud();
        }, BlockJamPlugin.AD_UNAVAILABLE_MS);
        this._renderHud();
    }
    /** A shop coin-top-up ad is in flight, or null. */
    get shopTopUpPending() {
        return this._shopTopUpPending;
    }
    /** WP-52.6 — the last Shop ad request produced no ad. Test seam and HUD prop. */
    get shopTopUpUnavailable() {
        return this._shopTopUpUnavailable;
    }
    /**
     * The player took the ads-only Shop's "watch an ad for +N coins" offer.
     *
     * Since WP-50.29 retired the per-power-up top-up this modelled itself on, it
     * is the embed's only coin-granting rewarded placement. It closes nothing on
     * completion — the offer stays available for the next ad.
     */
    /**
     * `surface` (Module 118, WP-118.6): which button asked: the Shop popup or the
     * home row's "Free +100". One placement, one reward; the field only tells
     * the two apart in analytics.
     */
    async requestShopCoinTopUp(surface = 'shop') {
        const host = this._context?.host;
        const offer = host?.offerRewardedAd;
        const amount = host?.shopCoinTopUp ?? 0;
        if (!this._context || !offer || amount <= 0)
            return;
        if (this._shopTopUpPending)
            return;
        const home = surface === 'home';
        // Module 123 — the home button waits out its cooldown: no ad while it runs.
        if (home && this.freeCoinsCooldownMs > 0)
            return;
        const placement = 'shop_coin_topup';
        this._shopTopUpPending = true;
        // WP-52.6 — a fresh attempt clears the last one's verdict, so the notice can
        // never outlive the request it describes.
        if (home) {
            this._clearFreeCoinsRetry();
            this._freeCoinsUnavailable = false;
        }
        else {
            this._shopTopUpUnavailable = false;
        }
        this._renderHud();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_SHOWN, { placement, surface });
        let watched = false;
        try {
            watched = await offer(placement);
        }
        finally {
            // WP-52.6 — released in `finally`, without exception.
            //
            // `offerRewardedAd` is documented never to reject, and a host that broke
            // that promise would strand this flag `true` forever: the Shop's button is
            // `disabled={pending}`, so the player would find a permanently dead
            // control and no way to discover why. The reported symptom is exactly what
            // that looks like, and while the measured cause is elsewhere (an ad that
            // never starts), a state that CANNOT be recovered from is worth one
            // keyword regardless of whether it is today's bug.
            this._shopTopUpPending = false;
        }
        // The plugin was torn down while the ad ran. Nothing left to credit or
        // render — and the flag above is already released either way.
        if (!this._context)
            return;
        if (!watched) {
            // WP-52.6 — tell the PLAYER, not only the analytics pipe.
            //
            // Reported as "nothing happens at all", and that is precisely what this
            // branch produced: the portal's ad controller raises its block overlay
            // from the ad-started callback and from nowhere else, so a request that is
            // never filled — or one refused outright because the vendor SDK's ad
            // module never attached — shows no overlay, plays nothing, credits nothing
            // and said nothing. Indistinguishable, from the outside, from a dead
            // button.
            //
            // (Named in prose rather than as identifiers: the boundary guard in
            // `bj-rewarded-ads.test.ts` greps this tree for the vendor's callback
            // names, and cannot tell a mention from a call.)
            this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_FAILED, { placement, surface });
            // Module 123 — the home button: no coins, NO cooldown, "No ad now" for
            // 4 s and then the offer is back (Double Coins' behaviour). The Shop keeps
            // its own notice until the next attempt, as before.
            if (home) {
                this._showFreeCoinsUnavailable();
                return;
            }
            this._shopTopUpUnavailable = true;
            this._renderHud();
            return;
        }
        this._wallet.credit('coins', amount, REWARD_AD_SHOP_TOPUP_SOURCE_ID);
        // Module 123 — only a rewarded home ad starts the cooldown.
        if (home)
            this._freeCoinsReadyAt = this._context.clock.now() + FREE_COINS_COOLDOWN_MS;
        this._context.analytics.logEvent(ANALYTICS_EVENTS.REWARD_AD_COMPLETED, {
            placement,
            coins: amount,
            surface,
        });
        this._commitState();
        this._renderHud();
    }
    /**
     * The i18n key of the caption showing with the ghost hand, or null when no
     * tutorial is on screen (WP-43.5). Test seam, and the same value the HUD gets.
     */
    get tutorialCaptionKey() {
        return this._tutorial ? this._tutorialCaptionKey : null;
    }
    /**
     * WP-50.9 — the end of the board the caption must stay off, or null when the
     * demonstrated move names no gate on the top or bottom wall. Test seam, and
     * the same value the HUD gets.
     */
    get tutorialCaptionSide() {
        return this._tutorial ? this._tutorialCaptionSide : null;
    }
    /**
     * The power-up button the live tutorial moment points at, or null when it
     * points at the board. Test seam, and the same value the HUD gets.
     */
    get tutorialPowerupId() {
        return this._tutorial ? this._tutorialPowerupId : null;
    }
    /** Module 73 (WP-73.7) — the power-up the free-try glow points at. Test seam. */
    get freeHintPowerupId() {
        return this._freeHint();
    }
    /** Reset the free-try glow's per-puzzle memory when the puzzle changes. */
    _syncFreeHintPuzzle() {
        const key = `${this._dailyMode ? 'daily' : this._track}:${this._ordinal}`;
        if (key === this._freeHintKey)
            return;
        this._freeHintKey = key;
        this._freeHintUndos = 0;
        this._freeHintRestarted = false;
        this._freeHintId = null;
        this._freeHintDone = false;
    }
    /**
     * Module 73 (WP-73.7, decision D4) — which power-up, if any, may glow to say
     * "try it free" right now. Once chosen it holds for the puzzle until the
     * player uses it or anything else takes the screen; then it is done for the
     * puzzle. Each first showing is counted per track and persisted, because the
     * cap (3 puzzles per power-up per track) must survive a reload.
     */
    _freeHint() {
        this._syncFreeHintPuzzle();
        if (!this._state || !this._powerups)
            return null;
        const candidates = this.powerups.map((p) => ({
            id: p.id,
            unlocked: p.unlocked,
            freeUsesLeft: p.freeUsesLeft,
            freeUses: powerupDefinition(this._generationConfig, p.id)?.freeUses ?? 0,
            refusal: p.refusal,
        }));
        // Rule 4 — anything else asking for attention wins, always.
        const busy = this._dailyMode ||
            this._tutorial !== null ||
            // Module 81 — the low-moves Hint highlight outranks the try-it glow,
            // which then waits for the next puzzle (one glowing button at a time).
            this._revealLit !== null ||
            this._unlockQueue.length > 0 ||
            this._guidedPowerupId !== null ||
            this._armedPowerupId !== null ||
            this._refusalNotice !== null ||
            this._isInfoOpen ||
            this._trackEntry !== null ||
            activeModal(this._modalView()) !== null;
        if (this._freeHintId !== null) {
            const c = candidates.find((x) => x.id === this._freeHintId);
            if (!c || busy || c.freeUsesLeft !== c.freeUses) {
                this._freeHintId = null;
                this._freeHintDone = true;
            }
            return this._freeHintId;
        }
        if (this._freeHintDone)
            return null;
        const moment = {
            undos: this._freeHintUndos,
            restarted: this._freeHintRestarted,
            movesUsed: this._state.moveCount,
            budget: this.moveBudget,
            busy,
        };
        const record = this._trackProgress[this._track];
        const shown = record?.powerupHintsShown ?? {};
        const id = pickHint(candidates, moment, shown);
        if (id !== null) {
            this._freeHintId = id;
            this._trackProgress[this._track] = {
                ...record,
                powerupHintsShown: { ...shown, [id]: (shown[id] ?? 0) + 1 },
            };
            this._commitState();
        }
        return id;
    }
    /** The unlock popup the HUD should show right now, if any (WP-35.16). */
    get pendingUnlock() {
        return this._unlockQueue[0] ?? null;
    }
    /**
     * Dismiss the unlock popup: record it as seen (so it never returns), then start
     * the practice half by ringing the block that carries the new thing.
     */
    dismissUnlock() {
        const [current, ...rest] = this._unlockQueue;
        if (!current)
            return;
        this._seenUnlocks = markUnlocksSeen(this._seenUnlocks, [current]);
        this._unlockQueue = rest;
        this._commitState();
        if (rest.length === 0 && this._state) {
            // Two different surfaces: a shape/mechanic is a block on the canvas, a
            // power-up is a button in the HUD. Exactly one of these is ever non-null.
            const target = guidedTargetFor(current, this._state, this._generationConfig);
            this._hintedBlockId = target;
            this._board?.setGuidedBlock(target);
            this._guidedPowerupId = guidedPowerupFor(current);
            // WP-43.5 — the popup was the thing standing between this board and its
            // hand. Levels that unlock Arrows or the Hammer show both halves of the
            // lesson: the popup names it, then the hand does it.
            this._maybeShowTutorial();
        }
        this._renderHud();
    }
    // ─── Private helpers ──────────────────────────────────────────────────────
    /**
     * `gaveUp` (Module 130, user 2026-10-21): Home at the wall. The attempt ends
     * exactly as a restart would (Economy_Rules rule 3, "accepting a fail"): the
     * next visit opens the SAME puzzle, fresh, never the next one. It is reported
     * as a quit rather than a restart, and plays no restart cue: the player is
     * leaving, not retrying.
     */
    _restart({ gaveUp = false, streakSaved = false, } = {}) {
        if (!this._state)
            return;
        // Module 67 (WP-67.C4) — no restart inside the Daily: regenerating the
        // same board hands the ad-move allowance back, which is the one-attempt
        // rule's biggest loophole. The button is not rendered in daily mode; this
        // guard is for every other caller.
        if (this._dailyMode)
            return;
        // Module 80 (WP-80.16) — the attempt being thrown away, reported before
        // the board is reset (its move count is part of the event).
        this._logAttemptFailed(gaveUp ? 'quit' : 'restart');
        this._bumpAttempt('restarts');
        // Module 118 — a restart ends a run of wins (`win_streak_3`).
        this._questEvent({ type: 'attemptBroken' });
        this._milestoneEvent({ type: 'attemptBroken' });
        // Module 134 — a saved retry keeps the Win Streak; a replay or a revealed puzzle
        // (SOLVE IT's walkthrough closes through here) is outside it (`meta/win-streak.ts`).
        if (!streakSaved) {
            this._winStreakEvent({
                type: 'attemptEnded',
                puzzle: this._streakPuzzle(),
                firstClear: this._ordinal > this._maxPaidOrdinal(),
                revealed: this._revealedHere(),
            });
        }
        if (!gaveUp) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.LEVEL_RESTARTED, {
                level_id: this._state.puzzle.levelNumber,
                track: this._track,
                puzzle: this._ordinal,
                attempt_number: this._attemptRecord().restarts + 1,
            });
        }
        this._attemptStartedMs = Date.now();
        this._attemptSeq += 1;
        // Module 73 — a restart is a "looks stuck" signal (rule 3), and it keeps
        // the puzzle's key, so the counters survive into the new attempt.
        this._syncFreeHintPuzzle();
        this._freeHintRestarted = true;
        // WP-37.8 — restart gets its own cue and NOT `level_start`, even though it
        // re-enters a level: the two would stack on one tap, and "you threw that
        // attempt away" is a different thing to say than "here is a new board".
        if (!gaveUp)
            this._playCue(BlockJamAudioId.RESTART);
        const difficulty = this._state.puzzle.difficulty;
        const levelNumber = this._state.puzzle.levelNumber;
        // Restart re-adopts the board the player was on, as built — see
        // `_currentBuilt` for why re-generating from the level number is not the
        // same board once a track is in its endless phase.
        const current = this._currentBuilt;
        if (current) {
            // Module 87 — the queue is KEPT: this board is re-adopted as built, so
            // the walk has not moved and every board prepared after it still holds.
            this._state = this._adoptLevel(current, levelNumber);
        }
        else {
            this._state = this._generateLevel(levelNumber, difficulty);
        }
        // Leaving the level ends any celebration still playing (WP-36.5).
        this._cancelCelebration();
        this._isWon = false;
        this._moveUpOffer = null;
        this._isStuck = false;
        this._levelCompleteGranted = false;
        this._levelCompleteCuePlayed = false;
        this._coinsEarned = 0;
        // WP-50.25 — and so does "this one paid nothing"; the next board has not
        // been won yet, let alone re-won.
        this._replayNoReward = false;
        this._revealedNoReward = false;
        // WP-41.11 — the payout is gone, so the offer that belonged to it goes too.
        // A "double your coins" button surviving into the next level would double a
        // reward that has already been banked and reset.
        this._doubleCoins = 'offer';
        this._winAdMoment = null;
        // WP-51.4 — and its retry timer with it, or a failed ad on the last board
        // would put the offer "back" on this one.
        this._clearDoubleCoinsRetry();
        // WP-45.3 — same reasoning: the notice belongs to the moment the player
        // tapped a locked button on THIS board, and would otherwise reappear over
        // the next one with its timer still running.
        this._clearLockedNotice();
        this._selectedBlockId = null;
        this._hintedBlockId = null;
        // Module 129 (WP-129.3) — Restart ends the attempt: the fresh board (and the
        // restart count) is saved now, or a reload would resume the old attempt.
        this._commitState();
        this._renderBoard();
        this._renderHud();
    }
    /**
     * Module 116 (D5) — the win popup may carry the language line: a player who
     * came in through a first visit, on Beginner puzzles 1–3, not yet answered.
     * Whether there is a language worth suggesting is the HUD's call.
     */
    /**
     * Module 116 — the first playable board of this page life is on screen. The
     * embed adds the clock (`board_shown.ms_since_ready`): together with
     * `first_move` it splits "never saw a board" from "saw one and did not move".
     */
    _noteBoardShown() {
        if (this._boardShownLogged || this._needsTrackSelection || !this._state)
            return;
        this._boardShownLogged = true;
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.BOARD_SHOWN, {
            track: this._dailyMode ? 'daily' : this._track,
            puzzle: this._dailyMode ? 0 : this._ordinal,
        });
    }
    /**
     * Module 116 (D7) — one funnel milestone, once per save, and only for a player
     * who came in through a first visit (the cohort the funnel measures). Saved by
     * the caller's next `_commitState`.
     */
    _reachMilestone(milestone) {
        if (!this._bootedIntoPuzzle || this._earlyMilestones.includes(milestone))
            return;
        this._earlyMilestones = [...this._earlyMilestones, milestone];
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.EARLY_MILESTONE_REACHED, { milestone });
    }
    /**
     * Module 147 (D1, user 2026-10-24: "for new players only … not forever") —
     * the Top Bar's "Home" and "Settings" words show only to the first-visit
     * cohort (a save that booted into puzzle 1: latched this session, or one that
     * has reported a first-visit milestone), and only until the player has used
     * BOTH buttons or has played past Beginner puzzle
     * {@link TOP_BAR_LABEL_LAST_PUZZLE}. Re-read on each render, but the HUD only
     * swaps them between boards, so a label never vanishes under the player's
     * thumb mid-puzzle. Public for tests.
     */
    get topBarLabelsShown() {
        const cohort = this._bootedIntoPuzzle || this._earlyMilestones.length > 0;
        if (!cohort)
            return false;
        if (this._homeOpened && this._settingsOpened)
            return false;
        const beginner = this._trackProgress.beginner?.ordinal ?? 1;
        return beginner <= TOP_BAR_LABEL_LAST_PUZZLE;
    }
    /**
     * Module 147 (D2) — the one-time Home coach is owed. A save that booted into
     * puzzle 1 (Module 116), won puzzle 3, never opened home, and has not been
     * shown the step; never in the Daily or on the home screen. Public for tests.
     */
    get homeCoachOwed() {
        return (this._bootedIntoPuzzle &&
            !this._homeOpened &&
            !this._seenHomeCoach &&
            this._earlyMilestones.includes('puzzle3_won') &&
            !this._dailyMode &&
            !this._needsTrackSelection);
    }
    _languageLineOwed() {
        return (this._bootedIntoPuzzle &&
            !this._languageLineAnswered &&
            !this._dailyMode &&
            this._track === 'beginner' &&
            this._ordinal <= 3);
    }
    _nextLevel() {
        if (!this._state)
            return;
        // Module 126 (D3, D11) — signed in during that puzzle: load the account now,
        // between puzzles, instead of starting the next board.
        if (this._accountLoadOwed && !this._dailyMode) {
            this._loadAccountNow();
            return;
        }
        // Module 67 (WP-67.C4) — there is no "next" daily: the win popup's
        // continue returns to the home screen, whose Daily card now reads cleared.
        if (this._dailyMode) {
            this._commitState();
            this._needsTrackSelection = true;
            this._dailyMode = false;
            this._musicToHome();
            this._prewarmDaily();
            this._renderHud();
            return;
        }
        // Module 116 (D5) — the language line asks on puzzles 1–3 only. Leaving
        // puzzle 3 with it seen and unanswered retires it as `ignored`.
        if (this._languageLineShown && !this._languageLineAnswered && this._ordinal >= 3) {
            this._languageLineAnswered = true;
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.LANGUAGE_ANSWERED, { choice: 'ignored' });
        }
        const prevGridSize = this._state.puzzle.gridSize;
        // Module 65 — the player advances by ORDINAL. Which effective level that
        // lands on is the track map's business, and it may be lower than the one
        // just played once the track is cycling inside its ceiling band.
        const nextOrdinal = this._ordinal + 1;
        const nextLevel = trackPositionFor(this._generationConfig, this._track, nextOrdinal).effectiveLevel;
        // WP-36.11: the win celebration already built this board at idle. Consume it
        // when it matches; otherwise pay for it here exactly as before, so a missed
        // idle window costs the old click time rather than breaking anything.
        // No difficulty override: the framework curve owns level→band now (WP-35.10).
        // Module 87 — prepared ahead, usually several puzzles ago.
        const startedMs = performance.now();
        const { prepared, ready } = this._takeOrPrepare(nextOrdinal);
        // Module 83 (WP-83.5b) — the board may still be waiting on its route (a
        // fast player outran the worker); everything from here runs once it is in.
        this._whenPrepared(prepared, (built) => {
            if (!ready) {
                this._perf?.recordGeneration(performance.now() - startedMs, {
                    level: nextLevel,
                    blocks: built.metrics.blockCount,
                    pregenerated: false,
                });
            }
            this._ordinal = nextOrdinal;
            this._attemptStartedMs = Date.now();
            this._state = this._adoptLevel(built, built.state.puzzle.levelNumber);
            this._perf?.recordLevelEntered(nextLevel, ready);
            this._cancelCelebration();
            this._isWon = false;
            this._moveUpOffer = null;
            this._isStuck = false;
            this._levelCompleteGranted = false;
            this._levelCompleteCuePlayed = false;
            this._coinsEarned = 0;
            // WP-50.25 — and so does "this one paid nothing"; the next board has not
            // been won yet, let alone re-won.
            this._replayNoReward = false;
            this._revealedNoReward = false;
            // WP-41.11 — the payout is gone, so the offer that belonged to it goes too.
            // A "double your coins" button surviving into the next level would double a
            // reward that has already been banked and reset.
            this._doubleCoins = 'offer';
            this._winAdMoment = null;
            // WP-51.4 — and its retry timer with it, or a failed ad on the last board
            // would put the offer "back" on this one.
            this._clearDoubleCoinsRetry();
            // WP-45.3 — same reasoning: the notice belongs to the moment the player
            // tapped a locked button on THIS board, and would otherwise reappear over
            // the next one with its timer still running.
            this._clearLockedNotice();
            this._selectedBlockId = null;
            this._trackProgress[this._track] = {
                ...this._trackProgress[this._track],
                ordinal: nextOrdinal,
            };
            this._playCue(BlockJamAudioId.LEVEL_START);
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.LEVEL_STARTED, {
                level: nextLevel,
                track: this._track,
                puzzle: nextOrdinal,
                entry: 'next',
                lesson: this._lessonPending(),
            });
            this._questEvent({ type: 'puzzleStarted', track: this._track, puzzle: nextOrdinal }, false);
            this._milestoneEvent({ type: 'puzzleStarted', track: this._track, puzzle: nextOrdinal }, false);
            if (this._track === 'beginner' && nextOrdinal === 2)
                this._reachMilestone('puzzle2_started');
            this._commitState();
            // WP-65.B3 — the background and the music follow the ORDINAL, so each track
            // gets its own sequence from Puzzle 1 and a cycling endless phase does not
            // replay the same art every lap.
            this._applyBgTheme(this._containerEl, nextOrdinal);
            // WP-64.A6 — the music rotates on the same beat as the background.
            this._syncMusicToLevel();
            const newGridSize = this._state.puzzle.gridSize;
            if (newGridSize !== prevGridSize)
                this._resizeRenderer(newGridSize);
            this._renderBoard();
            // WP-43.5 — a tutorial moment can belong to any level now, so entering one is
            // an occasion to check. A level with an unlock popup queued is skipped here
            // and picked up by `dismissUnlock`. After `_renderBoard`, which calls
            // `setBoard` and would clear a ghost drawn before it.
            this._maybeShowTutorial();
            this._renderHud();
        });
    }
    /** Module 65 — which track the player is in, and whether they have chosen. */
    get track() {
        return this._track;
    }
    get needsTrackSelection() {
        return this._needsTrackSelection;
    }
    /** Module 65 — the puzzle number the player is on, within their track. */
    get puzzle() {
        return this._ordinal;
    }
    /**
     * Module 65 (WP-65.B1) — enter a track, resuming wherever it was left.
     *
     * Also the way the home screen's cards work and the way Back-then-choose-again
     * works, so it must be safe to call on a track the player is already in: that
     * case re-enters at the saved ordinal rather than rebuilding, which is what
     * stops a stray tap from restarting somebody's puzzle.
     *
     * WP-65.G2 — with the home screen up on EVERY entry, the same-track case is
     * now the ordinary resume, not a stray tap. The board behind the scrim is
     * already this entry's board — built at init (fixtures included), or left
     * live with the player's moves by the Back control — so it regenerates
     * NOTHING and fires only the entry beats: the level cue, the music, the
     * tutorial and the HUD. Only choosing a DIFFERENT track rebuilds.
     */
    selectTrack(id) {
        const fromHome = this._needsTrackSelection;
        // Module 114 — a card tapped on the chooser (a programmatic re-entry is not a choice).
        if (fromHome) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.TRACK_SELECTED, {
                track: id,
                resume: (this._trackProgress[id]?.ordinal ?? 1) > 1,
            });
        }
        this._needsTrackSelection = false;
        this._puzzleStartedThisSession = true; // Module 126 (1.2.2): a puzzle this session
        // Module 67 (WP-67.C3) — entering a track always leaves daily mode; and a
        // board left over from a daily session must never be "resumed" as the
        // track's own, so the same-track fast path below is bypassed for it.
        const boardWasDaily = this._boardIsDaily;
        this._dailyMode = false;
        // Module 116 (WP-116.9) — a WON board is never resumed: leaving it by Back
        // already moved the track on (see `onBack`), so entering builds the next one.
        if (id === this._track && !boardWasDaily && !this._isWon) {
            if (!fromHome)
                return; // repeat call with nothing to do
            this._isInfoOpen = false;
            this._playCue(BlockJamAudioId.LEVEL_START);
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.LEVEL_STARTED, {
                level: this._state?.puzzle.levelNumber ?? 0,
                track: id,
                puzzle: this._ordinal,
                entry: 'track',
                lesson: this._lessonPending(),
            });
            this._questEvent({ type: 'puzzleStarted', track: id, puzzle: this._ordinal }, false);
            this._milestoneEvent({ type: 'puzzleStarted', track: id, puzzle: this._ordinal }, false);
            this._commitState();
            // Same re-arm as the full path below — see the comment there.
            this._musicWanted = true;
            this._syncMusicToLevel();
            this._maybeShowTutorial();
            this._noteBoardShown();
            this._renderHud();
            return;
        }
        // Module 129 — the board being left is saved under ITS track before the switch.
        this._flushMoveCommit();
        this._track = id;
        // Each track keeps its OWN generator (Module 87, `_aheadFor`): the
        // anti-repetition window belongs to the sequence the player is walking in
        // that track, and carrying one track's window into another would make a
        // board depend on where they had been.
        this._ordinal = this._trackProgress[id]?.ordinal ?? 1;
        // WP-65.F2 — free uses are this track's: hand the controller the record
        // this track saved, or a clean slate on a track never played.
        this._powerups?.restoreFreeUses(this._trackProgress[id]?.powerupFreeUsed);
        this._cancelCelebration();
        this._isWon = false;
        this._moveUpOffer = null;
        this._isStuck = false;
        this._isInfoOpen = false;
        this._levelCompleteGranted = false;
        this._levelCompleteCuePlayed = false;
        this._coinsEarned = 0;
        this._replayNoReward = false;
        this._revealedNoReward = false;
        this._doubleCoins = 'offer';
        this._winAdMoment = null;
        this._clearDoubleCoinsRetry();
        this._clearLockedNotice();
        this._selectedBlockId = null;
        this._history.length = 0;
        const prevGridSize = this._state?.puzzle.gridSize;
        this._cancelLookahead();
        this._boardIsDaily = false;
        // Module 87 — back on a track already visited this session: the board the
        // player was on there, as built. Otherwise prepared ahead, or built now.
        // Module 83 (WP-83.5b) — a cold entry on a board outside the shipped table
        // waits for the worker's route behind "Preparing puzzle…".
        const ahead = this._aheadFor(id);
        const kept = ahead.current?.ordinal === this._ordinal ? ahead.current.built : null;
        this._whenPrepared(kept ?? this._takeOrPrepare(this._ordinal).prepared, (built) => {
            // Module 129 (WP-129.3) — the track's attempt in progress comes back.
            const state = this._adoptTrackLevel(built, built.state.puzzle.levelNumber);
            this._state = state;
            this._playCue(BlockJamAudioId.LEVEL_START);
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.LEVEL_STARTED, {
                level: state.puzzle.levelNumber,
                track: id,
                puzzle: this._ordinal,
                entry: 'track',
                lesson: this._lessonPending(),
            });
            this._questEvent({ type: 'puzzleStarted', track: id, puzzle: this._ordinal }, false);
            this._milestoneEvent({ type: 'puzzleStarted', track: id, puzzle: this._ordinal }, false);
            // Module 116 — puzzle 2 reached by leaving a won puzzle 1 and coming back.
            if (id === 'beginner' && this._ordinal === 2)
                this._reachMilestone('puzzle2_started');
            this._commitState();
            this._applyBgTheme(this._containerEl, this._ordinal);
            // WP-65.G2 — re-arm before syncing. `_syncMusicToLevel`'s first line
            // returns on `_musicWanted`, and a path that ended the music (an exit, a
            // teardown) leaves it false, which once kept the game silent for the rest
            // of the session. Since Module 72 the home screen normally has its OWN track
            // playing when this runs, and the sync switches from it to the puzzle's.
            // The card tap is also the gesture that lets music actually start.
            this._musicWanted = true;
            this._syncMusicToLevel();
            const newGridSize = state.puzzle.gridSize;
            if (prevGridSize !== undefined && newGridSize !== prevGridSize) {
                this._resizeRenderer(newGridSize);
            }
            this._renderBoard();
            this._maybeShowTutorial();
            this._noteBoardShown();
            this._renderHud();
        });
    }
    /**
     * Module 67 (WP-67.C3) — enter the Daily Challenge from the home screen.
     *
     * The fourth entry path beside `selectTrack`'s two branches and boot. The
     * board is `generateFreeLevel(dailyEffectiveLevel(date), { seed })` — one
     * deterministic call, the same for everyone on that UTC date, and
     * deliberately NOT routed through `TrackLevelGenerator`, whose
     * anti-repetition window belongs to a track's own sequence.
     *
     * One attempt per day, three doors closed:
     *  - Restart is refused in daily mode (`_restart` guard) — it re-rolls the
     *    ad allowance, the exact loophole WP-67.C4 names;
     *  - leaving mid-attempt RESUMES: the record persists `boardState` and this
     *    method restores it (with the move budget recomputed from the pristine
     *    board, so an edited save cannot smuggle a bigger one);
     *  - the coin reward is stamped server-side once per day (migration 0015),
     *    so everything client-side is UX, not enforcement.
     *
     * A terminal record for today (`cleared`/`failed`) refuses entry — the home
     * card says so and does not call this, and a direct call must not re-roll.
     */
    enterDaily() {
        if (!this._context || !this._state)
            return;
        const date = dailyDate(this._context.clock);
        const record = this._daily?.date === date ? this._daily : undefined;
        if (record && record.state !== 'in-progress')
            return;
        // Module 129 — the track board's last moves are saved before the Daily takes over.
        this._flushMoveCommit();
        this._needsTrackSelection = false;
        this._puzzleStartedThisSession = true; // Module 126 (1.2.2): a puzzle this session
        this._dailyMode = true;
        this._resetPlayFlags();
        const prevGridSize = this._state.puzzle.gridSize;
        const level = dailyEffectiveLevel(date);
        this._cancelLookahead();
        this._boardIsDaily = true;
        // Module 95 — usually prepared already, while the home screen showed; a tap
        // during that search waits on it rather than starting another.
        const prepared = this._dailyPrepared(date);
        // `_ordinal` drives only art in daily mode (background, music rotation) —
        // the level number keeps those deterministic per date too.
        this._ordinal = level;
        // Module 83 (WP-83.5b) — the Daily is never in the shipped table (it is
        // date-seeded), so with a host solver its route comes from the worker.
        this._whenPrepared(prepared, (built) => {
            const pristine = this._adoptLevel(built, level);
            const resumed = record?.boardState;
            if (resumed && record?.state === 'in-progress') {
                // Resume the attempt exactly — blocks, move count, ad allowance — but
                // the BUDGET comes from the pristine board: it is derived data, and
                // trusting the saved copy is the moveBudget-cheat the save validator's
                // own notes rule out.
                const caps = this._resumeCaps();
                const ads = Math.max(0, Math.min(resumed.movesAdsUsed ?? 0, caps.maxAds));
                const bought = Math.max(0, Math.min(resumed.movesBought ?? 0, RESCUES_PER_ATTEMPT));
                // Module 129 (WP-129.3) — the rescues' moves come back with them. This
                // used to reset to the pristine budget, so leaving after a rescue and
                // coming back took the bought moves away.
                const moveBudget = resumedBudget(resumed.moveBudget, pristine.moveBudget, ads, bought, caps);
                this._state = {
                    ...resumed,
                    ...(moveBudget !== undefined ? { moveBudget } : {}),
                    movesAdsUsed: ads,
                    movesBought: bought,
                };
            }
            else {
                this._state = pristine;
                const carry = this._daily; // yesterday's (or older) record carries the streak pair
                this._daily = {
                    date,
                    state: 'in-progress',
                    streak: carry?.streak ?? 0,
                    ...(carry?.lastClearDate !== undefined ? { lastClearDate: carry.lastClearDate } : {}),
                };
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.DAILY_STARTED, {
                    date,
                    level_id: level,
                    streak: this._daily.streak,
                });
            }
            this._playCue(BlockJamAudioId.LEVEL_START);
            this._commitState();
            this._applyBgTheme(this._containerEl, this._ordinal);
            this._musicWanted = true;
            this._syncMusicToLevel();
            const newGridSize = this._state.puzzle.gridSize;
            if (prevGridSize !== newGridSize)
                this._resizeRenderer(newGridSize);
            this._renderBoard();
            this._noteBoardShown();
            this._renderHud();
        });
    }
    /**
     * Every per-board flag a fresh Daily board starts from. Shared by
     * `enterDaily` and the paid retry (Module 118), so the retry can never
     * resume a won, stuck or half-celebrated state the entry path would clear.
     */
    _resetPlayFlags() {
        this._cancelCelebration();
        this._isWon = false;
        this._moveUpOffer = null;
        this._isStuck = false;
        this._isInfoOpen = false;
        this._levelCompleteGranted = false;
        this._levelCompleteCuePlayed = false;
        this._coinsEarned = 0;
        this._replayNoReward = false;
        this._revealedNoReward = false;
        this._doubleCoins = 'offer';
        this._winAdMoment = null;
        this._clearDoubleCoinsRetry();
        this._clearLockedNotice();
        this._selectedBlockId = null;
        this._hintedBlockId = null;
        this._inputLocked = false;
        this._history.length = 0;
    }
    /** Whether the game is currently in the Daily Challenge. */
    get isDailyMode() {
        return this._dailyMode;
    }
    /** Today's daily record as the HOME screen needs it (Module 67, WP-67.C5). */
    dailyStatus() {
        if (!this._context)
            return { state: 'unplayed', streak: 0, date: '1970-01-01' };
        const date = dailyDate(this._context.clock);
        const record = this._daily;
        if (!record || record.date !== date)
            return { state: 'unplayed', streak: record?.streak ?? 0, date };
        return { state: record.state, streak: record.streak, date };
    }
    /**
     * Module 67 (WP-67.C4) — the attempt ends: "Come back tomorrow".
     *
     * The one path that marks a daily `failed` — the out-of-moves popup's daily
     * button, replacing Restart. Leaving by Home instead keeps the attempt
     * `in-progress` (with the popup re-derived on resume), which is deliberate:
     * deferral is not a retry, and the ad offer they walked away from is the
     * same one they come back to.
     */
    failDailyAttempt() {
        if (!this._dailyMode || !this._context)
            return;
        const record = this._daily;
        if (record && record.state === 'in-progress') {
            // The board is dropped from the terminal record — nothing left to resume.
            const rest = { ...record };
            delete rest.boardState;
            this._daily = { ...rest, state: 'failed', ...this._dailyResult() };
            this._context.analytics.logEvent(ANALYTICS_EVENTS.DAILY_FAILED, {
                date: record.date,
                level_id: this._state?.puzzle.levelNumber ?? 0,
                move_count: this._state?.moveCount ?? 0,
            });
            // Module 118 — a lost Daily ends a run of wins; saved just below.
            this._questEvent({ type: 'attemptBroken' }, false);
            this._milestoneEvent({ type: 'attemptBroken' }, false);
        }
        // Back to the home screen, the same shape as onBack's in-track branch.
        this._dropPreparing();
        this._dailyMode = false;
        this._needsTrackSelection = true;
        this._musicToHome();
        this._commitState();
        this._prewarmDaily();
        this._renderHud();
    }
    /**
     * Module 79 (WP-79.7) — today's Daily as the share text needs it, once the
     * attempt has ended. Null before that, and for any earlier day: yesterday's
     * result is not today's to share.
     */
    _dailyShareView() {
        if (!this._context)
            return null;
        const record = this._daily;
        if (!record || record.date !== dailyDate(this._context.clock))
            return null;
        if (record.state !== 'cleared' && record.state !== 'failed')
            return null;
        return {
            date: record.date,
            state: record.state,
            streak: record.streak,
            ...(record.result ? { result: record.result } : {}),
            ...(record.shared ? { shared: true } : {}),
            ...(record.retried ? { retried: true } : {}),
        };
    }
    /** Module 79 (WP-79.7) — the attempt's moves and final budget, for the share. */
    _dailyResult() {
        const budget = this._state?.moveBudget;
        if (!this._state || budget === undefined || budget <= 0)
            return {};
        return { result: { movesUsed: this._state.moveCount, budget } };
    }
    /** The daily win's payout — `onLevelComplete`'s whole body in daily mode. */
    _onDailyComplete() {
        if (!this._context || this._levelCompleteGranted)
            return;
        this._levelCompleteGranted = true;
        const record = this._daily;
        if (!record)
            return;
        const first = record.state === 'in-progress';
        const streak = first
            ? streakAfterClear(record.date, record.lastClearDate, record.streak)
            : record.streak;
        const lastClearDate = first ? record.date : record.lastClearDate;
        this._daily = {
            date: record.date,
            state: 'cleared',
            streak,
            ...(lastClearDate !== undefined ? { lastClearDate } : {}),
            // A replay of an already-cleared day keeps the first result.
            ...(first ? this._dailyResult() : record.result ? { result: record.result } : {}),
        };
        this._coinsEarned = first ? DAILY_REWARD_COINS : 0;
        this._replayNoReward = !first;
        // The server's `lastBlockjamDailyDate` stamp is the authority; this credit
        // is refused there on a tampered second claim (migration 0015).
        if (first)
            this._wallet.credit('coins', DAILY_REWARD_COINS, DAILY_COMPLETE_SOURCE_ID);
        this._context.analytics.logEvent(ANALYTICS_EVENTS.DAILY_COMPLETED, {
            date: record.date,
            level_id: this._state?.puzzle.levelNumber ?? 0,
            move_count: this._state?.moveCount ?? 0,
            coins_earned: this._coinsEarned,
            streak,
        });
        // Module 118 — quests: the first clear of the day only (a replay is not a new finish).
        if (first) {
            this._questEvent({ type: 'win', movesLeft: this._movesLeftNow() }, false);
            this._milestoneEvent({
                type: 'win',
                movesLeft: this._ownMovesLeft(),
                track: null,
                powerupFree: Object.keys(this._powerups?.usedThisLevel() ?? {}).length === 0,
            }, false);
            this._questEvent({ type: 'dailyCleared', streak }, false);
            this._milestoneEvent({ type: 'dailyCleared', streak }, false);
        }
        this._buildWinLine();
        this._commitState();
    }
    /**
     * Dev/QA — put the game on a specific EFFECTIVE level.
     *
     * Not a player-facing affordance: there is no level selector any more, and a
     * player has no way to name a level. It survives because the level number is
     * still the coordinate everything about difficulty is expressed in — unlocks,
     * bands, the scripted board, the reward tier — so "put me on level 41 and show
     * me what happens" is what a developer and a test actually want to say, and
     * saying it as an ordinal would mean doing this arithmetic at every call site.
     *
     * Resolves the level to the track whose slice contains it, preferring the
     * current one, and enters that track at the matching ramp ordinal. Deliberately
     * NOT bounded by the player's progress: this is the QA jump.
     */
    jumpToLevel(n) {
        const tracks = this._generationConfig.tracks;
        const containing = tracks.find((t) => t.id === this._track && n >= t.firstLevel && n <= t.lastLevel) ??
            tracks.find((t) => n >= t.firstLevel && n <= t.lastLevel);
        if (!containing || !this._state)
            return;
        if (containing.id !== this._track)
            this._track = containing.id;
        this._needsTrackSelection = false;
        this._puzzleStartedThisSession = true; // Module 126 (1.2.2): a puzzle this session
        const ordinal = n - containing.firstLevel + 1;
        this._trackProgress[containing.id] = {
            ...this._trackProgress[containing.id],
            ordinal: Math.max(this._trackProgress[containing.id]?.ordinal ?? 1, ordinal),
        };
        this.jumpToPuzzle(ordinal);
    }
    /**
     * Module 65 (WP-65.D9) — jump to a puzzle of the current track.
     *
     * There is no player-facing level selector any more; this survives for the
     * dev/QA jump and for tests. The bound is the furthest puzzle this track has
     * reached, which is simply its saved ordinal — you can only ever arrive at a
     * puzzle by clearing the one before it.
     */
    jumpToPuzzle(n) {
        if (n < 1 || n > this._trackProgress[this._track].ordinal || !this._state)
            return;
        const prevGridSize = this._state.puzzle.gridSize;
        this._ordinal = n;
        // Module 87 — a QA jump: nothing queued describes this future.
        this._cancelLookahead();
        this._aheadFor(this._track).clear();
        this._boardIsDaily = false;
        this._whenPrepared(this._prepareOrdinal(n), (built) => {
            this._state = this._adoptLevel(built, built.state.puzzle.levelNumber);
            this._cancelCelebration();
            this._isWon = false;
            this._moveUpOffer = null;
            this._isStuck = false;
            this._isInfoOpen = false;
            this._levelCompleteGranted = false;
            this._levelCompleteCuePlayed = false;
            this._coinsEarned = 0;
            // WP-50.25 — and so does "this one paid nothing"; the next board has not
            // been won yet, let alone re-won.
            this._replayNoReward = false;
            this._revealedNoReward = false;
            // WP-41.11 — the payout is gone, so the offer that belonged to it goes too.
            // A "double your coins" button surviving into the next level would double a
            // reward that has already been banked and reset.
            this._doubleCoins = 'offer';
            this._winAdMoment = null;
            // WP-51.4 — and its retry timer with it, or a failed ad on the last board
            // would put the offer "back" on this one.
            this._clearDoubleCoinsRetry();
            // WP-45.3 — same reasoning: the notice belongs to the moment the player
            // tapped a locked button on THIS board, and would otherwise reappear over
            // the next one with its timer still running.
            this._clearLockedNotice();
            this._selectedBlockId = null;
            this._hintedBlockId = null;
            this._inputLocked = false;
            this._playCue(BlockJamAudioId.LEVEL_START);
            this._applyBgTheme(this._containerEl, this._ordinal);
            // WP-64.A6 — the music rotates on the same beat as the background.
            this._syncMusicToLevel();
            const newGridSize = this._state.puzzle.gridSize;
            if (newGridSize !== prevGridSize)
                this._resizeRenderer(newGridSize);
            // Intentionally NOT calling _commitState() — jump is play-only, save progress unchanged
            this._renderBoard();
            this._renderHud();
        });
    }
    /**
     * Layer-1 DOM background for the current level (Module 34, WP-34.4). The
     * target is `.screen--game` (the container's parent). When the composition
     * root injects a populated Block Jam pool, `getLevelBackground` returns the
     * assigned background's inline CSS — apply it and clear the fallback
     * `data-bg`. Otherwise (empty/absent pool, tests, standalone) fall back to
     * the original 6-variant `data-bg = (level-1) % 6` cycle, clearing any inline
     * pool background first so switching back is clean and no-regression.
     */
    /**
     * WP-65.B3 — takes the ORDINAL. The parameter is still called an index into a
     * pool; what changed is which number is handed in. `bgForLevel` is a
     * pool-agnostic index picker (Module 34) and does not care what it counts.
     */
    _applyBgTheme(containerEl, ordinal) {
        const screenEl = containerEl?.parentElement;
        if (!screenEl)
            return;
        const bg = this._context?.getLevelBackground?.('blockjam', ordinal) ?? null;
        if (bg) {
            screenEl.removeAttribute('data-bg');
            screenEl.style.backgroundImage = bg.backgroundImage;
            screenEl.style.backgroundSize = bg.backgroundSize;
            screenEl.style.backgroundRepeat = bg.backgroundRepeat;
            screenEl.style.backgroundPosition = bg.backgroundPosition;
            return;
        }
        // Fallback: the 6 hand-authored dark variants (unchanged behavior). Clearing
        // the inline longhands is a no-op when no pool background was ever applied.
        screenEl.style.backgroundImage = '';
        screenEl.style.backgroundSize = '';
        screenEl.style.backgroundRepeat = '';
        screenEl.style.backgroundPosition = '';
        screenEl.setAttribute('data-bg', String((ordinal - 1) % 6));
    }
    _resizeRenderer(newGridSize) {
        // The board resizes itself inside `setBoard`; this only keeps the plugin's
        // logical-pixel figure in step.
        this._canvasLogical = newGridSize * CELL_SIZE + 2 * FRAME_BORDER;
    }
    /** Logical (unscaled) canvas edge in px for the current grid. */
    get canvasLogicalSize() {
        return this._canvasLogical;
    }
    /**
     * Push plugin state into the board. Only called when the plugin CHANGED the
     * board (new level, undo, restart) — a player-driven move already left the
     * board in the right place, and re-pushing would fight the drag.
     */
    _renderBoard() {
        if (!this._board || this._board.isDestroyed || !this._state)
            return;
        this._board.setBoard(this._state);
        this._board.setGuidedBlock(this._hintedBlockId);
        this._publishBoardGeometry();
    }
    /**
     * Module 120 (WP-120.2) — the board's backing resolution follows the screen
     * (a window dragged bigger, a tablet rotated), so its intrinsic size — the
     * most the layout may give it — is re-published with it. Debounced like the
     * layout-mode signal; nothing happens while the size settles.
     */
    _watchBoardResolution() {
        if (typeof window === 'undefined' || this._resolutionWatch)
            return;
        let timer;
        const onResize = () => {
            if (timer !== undefined)
                clearTimeout(timer);
            timer = setTimeout(() => {
                timer = undefined;
                if (this._board && !this._board.isDestroyed && this._board.refreshResolution?.()) {
                    this._publishBoardGeometry();
                }
            }, 150);
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        this._resolutionWatch = () => {
            if (timer !== undefined)
                clearTimeout(timer);
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }
    /**
     * Module 122 — publish where the board's top edge REALLY is, in viewport px,
     * as `--bj-board-top-measured` on the root.
     *
     * The portrait Moves bar is centred between the Top Bar and the board by a CSS
     * formula built on `100dvh` (blockjam.css, Module 120). On an S25 Ultra in
     * Samsung Internet, `100dvh` and the height media queries read the full screen
     * (~890px) while the game's box gets ~700px under the toolbars, so the formula
     * put the bar on the board. The CSS takes `min(formula, measured + 0.5px)`:
     * wherever the two agree the formula wins and nothing moves by a pixel; only
     * where the formula would push the bar too far down does the measurement win.
     *
     * Measured on resize of the board's container and of the canvas (a toolbar
     * collapsing resizes the fixed box the container lives in), and written only
     * when it changes, so dragging a block causes no style work.
     */
    _watchBoardTop(containerEl) {
        if (typeof window === 'undefined' || typeof document === 'undefined' || this._boardTopWatch) {
            return;
        }
        // Some tests mount into a stand-in container with no DOM behind it.
        if (typeof containerEl.querySelector !== 'function')
            return;
        let last = '';
        let frame;
        let row2 = null;
        const measure = () => {
            frame = undefined;
            const canvas = containerEl.querySelector('canvas');
            if (!canvas)
                return;
            const rect = canvas.getBoundingClientRect();
            if (rect.height <= 0)
                return;
            const value = `${Math.round(rect.top * 10) / 10}px`;
            if (value !== last) {
                last = value;
                document.documentElement.style.setProperty('--bj-board-top-measured', value);
            }
            const found = document.querySelector('.bj-hud .game-layout__row2');
            if (found !== row2) {
                row2 = found;
                if (row2)
                    observer?.observe(row2);
            }
            if (row2)
                fitRow2(row2, rect.top);
        };
        const schedule = () => {
            if (frame === undefined)
                frame = window.requestAnimationFrame(measure);
        };
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => schedule());
        observer?.observe(containerEl);
        const canvas = containerEl.querySelector('canvas');
        if (canvas)
            observer?.observe(canvas);
        window.addEventListener('resize', schedule);
        window.visualViewport?.addEventListener('resize', schedule);
        // The HUD renders through its own root and can mount (or remount) Row 2
        // after the board: when the DOM changes while we hold no connected Row 2,
        // look for it, and measure on a frame only once there is one (so a page
        // without the HUD schedules nothing).
        const mutations = typeof MutationObserver === 'undefined'
            ? null
            : new MutationObserver(() => {
                if (row2?.isConnected)
                    return;
                if (document.querySelector('.bj-hud .game-layout__row2'))
                    schedule();
            });
        mutations?.observe(document.body, { childList: true, subtree: true });
        // The first reading is taken now, not on a frame: the canvas is mounted and
        // laid out, and a frame scheduled here would sit among the look-ahead's idle
        // callbacks (Module 87 counts them under fake timers).
        measure();
        this._boardTopWatch = () => {
            if (frame !== undefined)
                window.cancelAnimationFrame(frame);
            observer?.disconnect();
            mutations?.disconnect();
            window.removeEventListener('resize', schedule);
            window.visualViewport?.removeEventListener('resize', schedule);
            document.documentElement.style.removeProperty('--bj-board-top-measured');
            if (row2)
                clearRow2Fit(row2);
        };
    }
    /**
     * WP-45.7 — tell the layout how big the board can draw itself.
     *
     * The HUD places the goal line against the board's top edge, which means it
     * has to know the board square. Two of the three terms are viewport
     * arithmetic CSS can do on its own; the third — the canvas's intrinsic size,
     * past which it will not scale up — depends on the level's grid size and the
     * configured frame thickness, so it can only come from the board itself.
     *
     * Published on `<html>` rather than on the game's own element for the same
     * reason `data-powerup-bar` is (see `_applyPowerupBarReserve`): the HUD mounts
     * through a sibling root, and `:root` is the one node both trees share.
     *
     * Re-published on every board push, because a grid-size change changes it.
     */
    _publishBoardGeometry() {
        if (typeof document === 'undefined' || !this._board)
            return;
        const size = this._board.boardPixelSize;
        // A board that cannot answer gets no answer published, rather than
        // `undefinedpx` — an invalid declaration the browser drops silently, which
        // is the same shape of failure `stylesheet-coverage.test.ts` exists for. Not
        // hypothetical: `tsconfig` excludes test files, so every stub board in the
        // suite satisfies this interface without implementing this member.
        if (!Number.isFinite(size) || size <= 0)
            return;
        document.documentElement.style.setProperty('--bj-board-intrinsic', `${size}px`);
    }
    /**
     * Tell the layout whether the power-up bar is on screen (WP-35.23).
     *
     * In PORTRAIT the board's bottom reserve is a fixed padding sized for a ONE-row
     * action cluster, and WP-35.18's second row is ~84px taller — without this the
     * system row is pushed onto the board.
     *
     * WP-44.2 — the condition is "the config defines a power-up", not "one has
     * unlocked", and it has to stay in lockstep with `GameHUD`'s own test or the
     * reserve and the row it reserves for disagree: the bar now renders from level
     * 1, so a reserve that still waited for level 20 would put the locked row on
     * top of the board for nineteen levels. There is no longer a "row that is not
     * there" case for a config with power-ups in it.
     *
     * Landscape does not use this: WP-35.23 moved the bar to the right rail, so the
     * bottom is one row again there and the board keeps its full height.
     */
    _applyPowerupBarReserve() {
        const screenEl = this._containerEl?.parentElement;
        if (!screenEl)
            return;
        const showing = this.powerups.length > 0;
        if (showing)
            screenEl.setAttribute('data-powerup-bar', '1');
        else
            screenEl.removeAttribute('data-powerup-bar');
        // Also mirrored onto <html>: the HUD (tutorial caption) mounts through its
        // own root, a sibling of screenEl's tree rather than a descendant, so a
        // selector scoped to screenEl can never reach it. :root is the one node
        // both trees share — same mechanism data-layout already uses globally.
        if (showing)
            document.documentElement.setAttribute('data-powerup-bar', '1');
        else
            document.documentElement.removeAttribute('data-powerup-bar');
        // Module 80 (WP-80.4) — and HOW MANY, for the same reason. The portrait row's
        // buttons shrink below 58px on a narrow phone (the bar's own count-driven
        // clamp), and the board's bottom reserve on :root has to shrink with them or
        // it books space the row no longer uses and the board sits off-centre.
        if (showing) {
            document.documentElement.style.setProperty('--bj-powerup-count', String(this.powerups.length));
        }
        else
            document.documentElement.style.removeProperty('--bj-powerup-count');
    }
    /**
     * Take the reserve back down (WP-44.2) — see `destroy()`.
     *
     * Deliberately not `_applyPowerupBarReserve()` with the condition inverted:
     * this runs during teardown, when `this.powerups` and `this._containerEl` may
     * already be gone, and "remove it from both nodes, unconditionally" is the
     * only thing that is correct at that point.
     */
    _clearPowerupBarReserve() {
        this._containerEl?.parentElement?.removeAttribute('data-powerup-bar');
        if (typeof document !== 'undefined') {
            document.documentElement.removeAttribute('data-powerup-bar');
            // WP-45.7 — the board geometry goes back with it, and for the same reason
            // WP-44.2 learned to take the attribute down: `<html>` outlives this
            // plugin, so anything left stamped there is Block Jam state abandoned on
            // the platform's root node after the player has gone back to the lobby.
            document.documentElement.style.removeProperty('--bj-board-intrinsic');
            document.documentElement.style.removeProperty('--bj-powerup-count');
        }
    }
    /**
     * Module 66 — park (hide) the board's mount container while the home screen
     * is up. The home screen's ground is the pool background painted on
     * `.screen--game`, and the live board would otherwise show through it. A
     * class toggle rather than an unmount: choosing the same track must RESUME
     * the board (Module 65 Increment G), so the canvas stays alive underneath.
     */
    _syncBoardParked() {
        // `classList?.` too — headless test harnesses hand in bare stub objects.
        this._containerEl?.classList?.toggle('bj-board-parked', this._needsTrackSelection);
        // …and the RENDERER must stop, not only the paint: a hidden canvas that
        // keeps drawing queues GPU work the compositor pays for all at once when
        // the board is shown again (see `IBlockJamBoard.setParked`).
        //
        // Module 150 (WP-150.1) — and it stops whenever nobody can see it move:
        // under a popup (`board-cover.ts`, reported by the HUD) or while the host
        // has paused the game (an ad, a platform pause). Only the home screen also
        // HIDES the canvas; under a popup it keeps showing its last frame.
        this._board?.setParked?.(this._boardShouldPark());
    }
    /** Module 150 — every reason the board's renderer should be stopped. */
    _boardShouldPark() {
        return this._needsTrackSelection || this._hostPaused || this._boardCoveredByHud;
    }
    /** Module 150 — the HUD's report from `boardCovered`; parks or resumes the board. */
    _setBoardCovered(covered) {
        if (this._boardCoveredByHud === covered)
            return;
        this._boardCoveredByHud = covered;
        this._syncBoardParked();
    }
    /**
     * Module 80 (WP-80.12) — count each powered board once, the first time it is
     * rendered, and tell the board how many this player has now met: it wiggles
     * the shut padlocks on the first few (`power.closed.attractPuzzles`). Here,
     * on the render path, because a board can start through four different
     * paths and every one of them renders. Persisted through `_commitState()`.
     */
    _syncPoweredBoards() {
        if (!this._state)
            return;
        const powered = (this._state.puzzle.plates?.length ?? 0) > 0;
        const key = this._boardKey();
        if (powered && this._poweredCountedKey !== key) {
            this._poweredCountedKey = key;
            this._poweredBoardsSeen += 1;
            this._commitState();
        }
        this._board?.setPoweredBoardsSeen?.(powered ? this._poweredBoardsSeen : 0);
    }
    _renderHud() {
        // Module 126 (D11) — a sign-in's account load is owed and the player is on
        // home: commit and hand over (the host may reload the page).
        if (this._accountLoadOwed && this._needsTrackSelection)
            this._loadAccountNow();
        // Module 126 (D14) — signed in and settled: the Welcome gift, once ever.
        this._maybeWelcomeGift();
        this._syncBoardParked();
        this._syncPoweredBoards();
        this._applyPowerupBarReserve();
        // WP-47.3 — reserved here, beside the power-up reserve, so it is recomputed on exactly
        // the renders that can change it (a level push, a tutorial starting or being dismissed).
        if (!this._hudRoot)
            return;
        // Lazy import: updateHud is already loaded if mountHud was called
        import('./ui/mount-hud').then(({ updateHud }) => {
            // Module 65 — re-check the DOM as well as the root. The import resolves on
            // a later tick, and by then the plugin may be running in a torn-down
            // environment: React's scheduler then reaches for `window` and throws
            // `ReferenceError: window is not defined`, attributed to whichever file
            // happens to be running. Without a document there is no HUD to update, so
            // there is nothing to do — this is a guard, not a swallow.
            if (typeof document === 'undefined')
                return;
            if (this._hudRoot)
                updateHud(this._hudRoot, this._getHudProps());
        });
    }
    /**
     * Module 101 (WP-101.1) — the How to Play topics for the player's REGULAR
     * track (in the Daily `_track` is still the regular track; see
     * `howto/topics.ts`). WP-101.10: no puzzle number, nothing is locked.
     */
    _howToProps() {
        return {
            topics: howToTopics(this._generationConfig, this._track),
            seen: this._howToSeen,
            rewarded: this._howToRewarded,
        };
    }
    /**
     * Module 101 (WP-101.1) — the player opened a How to Play topic. Marks it
     * seen (one `_commitState()`, only the first time) and always logs the view,
     * so a revisit shows in analytics too.
     */
    onHowToTopicSeen(id, source) {
        const { topics } = this._howToProps();
        const firstTime = !this._howToSeen.includes(id);
        if (firstTime) {
            this._howToSeen = [...this._howToSeen, id];
            this._commitState();
        }
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.HOWTO_TOPIC_VIEWED, {
            topic: id,
            source,
            first_time: firstTime,
            explored: exploredCount(topics, this._howToSeen),
            explorable: explorableTopics(topics).length,
            level_id: this._state?.puzzle.levelNumber ?? 0,
            track: this._track,
        });
        this._renderHud();
    }
    /**
     * Module 101 (WP-101.4) — the player tapped Collect on the explored-everything
     * card: +{@link HOWTO_EXPLORED_COINS}, once per player, ever.
     *
     * "Everything" is every topic (`explorableTopics`). A player already
     * rewarded under an older rule keeps `_howToRewarded` and is never paid again.
     *
     * The UI only offers the card; the decision is made HERE, from the plugin's
     * own topics and seen list, never from the tap. A Collect that arrives when the
     * reward is not owed (a stale card, a double tap after the first one landed)
     * does nothing.
     *
     * The flag is set BEFORE the credit: a second tap in the same tick finds it
     * already true. If the credit throws, the flag stays true and nothing retries
     * — the Daily's rule (`_onDailyComplete`): a reward the client cannot pay
     * cleanly is lost, not paid twice. The server is the real once-ever guard:
     * migration 0023 stamps `howtoExploredAt` and refuses a second credit with
     * `already_exists` (a second device, a cleared save), which the economy's
     * retry wrapper treats as final — logged once, never retried.
     */
    onHowToRewardCollected() {
        if (this._howToRewarded)
            return;
        const { topics } = this._howToProps();
        if (!isAllExplored(topics, this._howToSeen))
            return;
        this._howToRewarded = true;
        // Module 132 — the How to Play badge (no coins of its own: this pays).
        this._milestoneEvent({ type: 'howToExplored' }, false);
        let paid = false;
        try {
            if (this._context) {
                this._wallet.credit('coins', HOWTO_EXPLORED_COINS, HOWTO_EXPLORED_SOURCE_ID);
                paid = true;
            }
        }
        catch {
            // Refused locally (no wallet, a bad id): keep the flag, show no coins,
            // never loop. Nothing else here can throw.
        }
        this._commitState();
        if (paid) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.HOWTO_EXPLORED_REWARDED, {
                amount: HOWTO_EXPLORED_COINS,
                explorable: explorableTopics(topics).length,
                level_id: this._state?.puzzle.levelNumber ?? 0,
                track: this._track,
            });
        }
        this._renderHud();
    }
    // ─── Module 118: daily reward, quests ─────────────────────────────────────
    /** Moves left in the current attempt (0 without a budget). */
    _movesLeftNow() {
        const budget = this._state?.moveBudget;
        if (!this._state || budget === undefined)
            return 0;
        return Math.max(0, budget - this._state.moveCount);
    }
    /**
     * Module 118 (WP-118.2) — a move covered plates that were not covered before
     * it: each one opened (powered) its gate. Power is a hold, so moving off and
     * back on counts again, which is what "open a powered gate 3 times" means.
     */
    _noteGatesPowered(before) {
        const plates = this._state?.puzzle.plates;
        if (!this._state || !plates || plates.length === 0)
            return;
        const was = poweredPlateIds(before, plates);
        let count = 0;
        for (const id of poweredPlateIds(this._state.blocks, plates))
            if (!was.has(id))
                count += 1;
        if (count > 0)
            this._questEvent({ type: 'gatesPowered', count });
        if (count > 0)
            this._milestoneEvent({ type: 'gatesPowered', count });
    }
    /**
     * Module 118 (WP-118.2) — feed one event to the quests. Logs each quest it
     * completes. Saves only when something changed, and only when `commit` is
     * true: callers that save right after (a win, a puzzle start) pass false so
     * the one writer runs once.
     */
    _questEvent(evt, commit = true) {
        const update = applyQuestEvent(this._quests, evt);
        if (!update.changed)
            return;
        this._quests = update.state;
        for (const questId of update.completed) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.QUEST_COMPLETED, {
                quest_id: questId,
                ...(this._dailyMode ? {} : { track: this._track, puzzle: this._ordinal }),
            });
        }
        if (commit)
            this._commitState();
    }
    // ─── Module 132: milestones ──────────────────────────────────────────────
    /**
     * Module 132 (WP-132.1) — milestones for a save written before them, from
     * what it can prove (`seedMilestones`): first clears per track, the furthest
     * puzzle, the quests, the Daily and Daily Bonus streaks, power-up uses.
     */
    _seedMilestones(saved) {
        const solvedByTrack = Object.fromEntries(TRACK_IDS.map((id) => {
            const p = this._trackProgress[id];
            return [id, Math.max(0, this._maxPaidOrdinal(id) - (p?.skipped ?? 0) - (p?.revealed ?? 0))];
        }));
        const sum = (r) => Object.values(r ?? {}).reduce((a, b) => a + b, 0);
        const freeUsed = TRACK_IDS.reduce((n, id) => n + sum(this._trackProgress[id]?.powerupFreeUsed), 0);
        return seedMilestones({
            quests: this._quests,
            solvedByTrack,
            maxPuzzle: Math.max(...TRACK_IDS.map((id) => this._trackProgress[id]?.ordinal ?? 0)),
            dailyStreak: saved?.daily?.streak ?? 0,
            loginDay: this._dailyLogin.day,
            powerupsUsed: Math.max(freeUsed, sum(this._powerupConfirms)),
            howToRewarded: this._howToRewarded,
        });
    }
    /**
     * Module 132 (WP-132.1) — feed one event to the milestones. A rung reached
     * is queued for the HUD and saved now when `commit` is true; everything else
     * (a counter that just moved) rides the next commit — every move schedules
     * one (Module 129), so a per-move counter never causes a save of its own.
     * Callers that save right after (a win, a puzzle start) pass false.
     */
    _milestoneEvent(evt, commit = true) {
        // Module 133 (WP-133.1) — the same event moves today's missions first. A
        // mission it finishes counts toward the "Missions completed" milestone.
        const finished = this._missionEvent(evt);
        this._applyMilestoneEvent(evt, commit && finished === 0);
        if (finished > 0)
            this._applyMilestoneEvent({ type: 'missionsCompleted', count: finished }, false);
        // The win that reaches the unlock threshold draws the first set now, so it
        // exists before home opens; that win itself doesn't count (D5).
        if (this._rollMissions() || (finished > 0 && commit)) {
            if (commit)
                this._commitState();
        }
    }
    _applyMilestoneEvent(evt, commit) {
        const update = applyMilestoneEvent(this._milestones, evt, this._todayUtc());
        if (!update.changed)
            return;
        this._milestones = update.state;
        if (update.earned.length === 0)
            return;
        // WP-132.11 — against the title last seen, not the state just before: a
        // Module 118 quest event runs first on the same win and can already vouch
        // for a badge this event reaches.
        const titleBefore = this._lastTitle;
        const titleAfter = buildMilestonesView(this._milestones, this._quests).title;
        this._lastTitle = titleAfter;
        if (titleBefore &&
            (titleAfter.index !== titleBefore.index || titleAfter.stars !== titleBefore.stars)) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.TITLE_REACHED, {
                title: PLAYER_TITLES[titleAfter.index],
                stars: titleAfter.stars,
                badges: titleAfter.badges,
            });
        }
        this._milestonesEarned = [...this._milestonesEarned, ...update.earned];
        // WP-132.8 — one event per rung; the embed submits `badge_points` (D9).
        const points = badgePoints(this._milestones, this._quests);
        for (const level of update.earned) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.MILESTONE_REACHED, {
                milestone: level.id,
                index: level.index,
                kind: level.index < 4 ? 'tier' : 'step',
                badge_points: points,
            });
        }
        // WP-132.7 — reached on home (a Daily Bonus streak, a purchase there): no move
        // will end to toast it, so a Gold or Platinum is revealed now and the rest is
        // left to home's own peek and count.
        if (this._needsTrackSelection)
            this._takeReveal(this.takeEarnedMilestones());
        if (commit)
            this._commitState();
    }
    /**
     * Moves left that are the player's own: an attempt rescued with bonus moves
     * (ad or coins) never counts as a win with moves to spare (Economy rule 15).
     */
    _ownMovesLeft() {
        const rescued = (this._state?.movesAdsUsed ?? 0) + (this._state?.movesBought ?? 0) > 0;
        return rescued ? 0 : this._movesLeftNow();
    }
    /** Module 132 — the milestones state, with the quests it reads for legacy tiers. */
    get milestones() {
        return { state: this._milestones, legacy: this._quests };
    }
    /** Module 132 (WP-132.4) — what the Milestones popup and the home icon show. */
    get milestonesView() {
        return buildMilestonesView(this._milestones, this._quests);
    }
    /** Module 132 — reached, unpaid rungs with coins: the "!" count and Claim buttons. */
    get claimableMilestones() {
        return claimableMilestones(this._milestones, this._quests);
    }
    /**
     * WP-132.5 — open the Status + Badges popup from outside the HUD: the avatar
     * ring / Profile header (Module 126), the home hero chip (Module 131) and the
     * win line (WP-132.6). `from` names the surface, for the analytics event the
     * later WP adds. The HUD ignores it until Milestones are unlocked (no view).
     */
    openBadges(from) {
        this._badgesRequest = { seq: (this._badgesRequest?.seq ?? 0) + 1, from };
        this._renderHud();
    }
    /** WP-132.9 — what an awareness surface showed, as its analytics event. */
    _logMilestoneUi(evt) {
        const analytics = this._context?.analytics;
        if (!analytics)
            return;
        switch (evt.type) {
            case 'line':
                analytics.logEvent(ANALYTICS_EVENTS.MILESTONE_LINE_SHOWN, {
                    state: evt.state,
                    milestone: evt.milestone,
                });
                return;
            case 'toast':
                analytics.logEvent(ANALYTICS_EVENTS.MILESTONE_TOAST_SHOWN, { count: evt.count });
                return;
            case 'reveal':
                analytics.logEvent(ANALYTICS_EVENTS.MILESTONE_REVEALED, {
                    milestone: evt.milestone,
                    index: evt.index,
                    still: evt.still,
                });
                return;
            case 'badges':
                analytics.logEvent(ANALYTICS_EVENTS.BADGES_OPENED, { from: evt.from });
                return;
        }
    }
    /** Module 132 — take the rungs reached since the last call (toast, win line). */
    takeEarnedMilestones() {
        const earned = this._milestonesEarned;
        this._milestonesEarned = [];
        return earned;
    }
    /**
     * WP-132.6 — the win popup's line, from everything since the last one. The
     * rungs this puzzle reached are the line's to show, so they are taken off the
     * toast queue: a win never toasts on the next board.
     */
    _buildWinLine() {
        this._winLine = pickWinLine(this._lineBase, this._milestones, this._quests);
        // Module 133 (D1a) — a mission finished by this puzzle (and not toasted) takes the line.
        const finished = this.takeCompletedMissions();
        this._missionWinSlot = finished[0] ?? null;
        this._lineBase = { ...this._milestones.counters };
        this._takeReveal(this.takeEarnedMilestones());
    }
    /**
     * WP-132.7 — a Gold or Platinum TIER among `levels` is owed the full-screen
     * reveal (the highest, ties to the first); ★ steps and lower tiers are not.
     * Returns the rest, for the toast. The HUD decides when it may show: never on
     * another popup, and on a win before the win popup.
     */
    _takeReveal(levels) {
        let best = null;
        for (const level of levels) {
            if (level.index !== 2 && level.index !== 3)
                continue;
            if (!best || level.index > best.index)
                best = level;
        }
        if (!best)
            return levels;
        this._milestoneReveal = { seq: (this._milestoneReveal?.seq ?? 0) + 1, level: best };
        return levels.filter((l) => l !== best);
    }
    /**
     * WP-132.6 — at the end of a committed move, the rungs it (or anything since
     * the last move) reached become ONE toast, merged. Only on a board still being
     * played: a winning move's rungs belong to the win line, and a move that
     * ends the attempt (stuck, out of moves) keeps them for the next move, so a
     * toast never lands on an outcome popup.
     */
    _queueMilestoneToast() {
        if (this._milestonesEarned.length === 0 || !this._state)
            return;
        if (this._state.blocks.length === 0 || this._isStuck || isOutOfMoves(this._state))
            return;
        // WP-132.7 — a Gold or Platinum tier gets the reveal instead of a toast line.
        const levels = this._takeReveal(this.takeEarnedMilestones());
        if (levels.length === 0)
            return;
        this._milestoneToast = { seq: (this._milestoneToast?.seq ?? 0) + 1, levels };
    }
    /**
     * Module 133 (WP-133.4, C1) — missions a move finished become ONE toast, on a
     * board still being played (the badge toast's rule): a winning move's belong
     * to the win line, and a move that ends the attempt keeps them for later.
     * A badge toast queued on the same move goes first (`delayMs`).
     */
    _queueMissionToast(afterBadge) {
        if (this._missionsCompleted.length === 0 || !this._state)
            return;
        if (this._state.blocks.length === 0 || this._isStuck || isOutOfMoves(this._state))
            return;
        const slots = this.takeCompletedMissions();
        this._missionToast = {
            seq: (this._missionToast?.seq ?? 0) + 1,
            slots,
            delayMs: afterBadge ? MILESTONE_TOAST_MS : 0,
        };
    }
    /**
     * Module 133 (WP-133.4) — what the Missions surfaces show; null until Missions
     * is open (shipped and unlocked) and today's set is drawn.
     */
    _missionsHud() {
        if (featureState('missions', counterOf(this._milestones, 'wins')).kind !== 'open')
            return null;
        const now = this._context?.clock.now();
        if (now === undefined)
            return null;
        return missionsView(this._missions, this._todayUtc(), now);
    }
    /**
     * Module 132 (WP-132.1) — pay one rung, once ever (the `claimQuestReward`
     * shape): state first so a double tap finds it paid, then the credit; a
     * credit that throws is lost, never retried (migration 0025's
     * `milestonesPaid` is the real guard on the web).
     */
    claimMilestone(key, from = 'popup') {
        // The server allows 20 economy transactions per player per minute
        // (supabase/migrations/0002). Past 15 claims in the last 60 s, refuse and
        // change nothing: the level stays claimable, so nothing is lost. The window
        // is memory only, not saved.
        const now = this._context?.clock.now() ?? 0;
        this._milestoneClaimTimes = this._milestoneClaimTimes.filter((at) => now - at < 60000);
        if (this._milestoneClaimTimes.length >= 15)
            return;
        const result = claimMilestoneLevel(this._milestones, key, this._quests);
        if (!result)
            return;
        this._milestones = result.state;
        this._milestoneClaimTimes.push(now);
        try {
            this._wallet.credit('coins', result.level.coins, `${MILESTONE_SOURCE_PREFIX}${key}`);
            // WP-132.9 — after the credit, as `blockjam_quest_claimed` was.
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.MILESTONE_CLAIMED, {
                milestone: result.level.id,
                index: result.level.index,
                coins: result.level.coins,
                from,
            });
        }
        catch {
            // Refused locally: keep the claim, never loop.
        }
        this._commitState();
        this._renderHud();
    }
    // ─── Module 133: Daily Missions ───────────────────────────────────────────
    /**
     * Module 133 (WP-133.1) — what the draw needs to know, read now (D1: the set
     * is drawn for the player's current track and never changes that day).
     */
    _missionDrawContext(today) {
        const config = this._generationConfig;
        const ordinal = this._trackProgress[this._track]?.ordinal ?? 1;
        const position = trackPositionFor(config, this._track, ordinal);
        return {
            track: this._track,
            dailyDoneToday: this._daily?.date === today && this._daily.state === 'cleared',
            powerupsReachable: powerupRegistry(config, this._track).some((p) => isPowerupUnlocked(p, ordinal)),
            poweredGatesReachable: position.effectiveLevel >= config.unlocks.poweredGatesUnlockLevel,
        };
    }
    /**
     * Module 133 (WP-133.1) — today's set: kept while it is today's, drawn anew
     * on a new UTC day (an unclaimed finished mission is lost, user 2026-11-02).
     * Nothing before Missions unlock (`unlocks.missions` total wins). The first
     * draw makes the save's salt. True when the state changed (the caller saves).
     */
    _rollMissions() {
        const today = this._todayUtc();
        if (today === null)
            return false;
        if (!isUnlocked('missions', counterOf(this._milestones, 'wins')))
            return false;
        const base = this._missions ?? { salt: newMissionSalt(Math.random) };
        const ctx = this._missionDrawContext(today);
        const next = rollMissions(base, today, ctx);
        if (next === this._missions)
            return false;
        const drawn = next.set !== undefined && next.set.day !== this._missions?.set?.day;
        this._missions = next;
        // WP-133.5 — once per player per UTC day: which three, for which track.
        if (drawn && next.set) {
            const [m1 = '', m2 = '', m3 = ''] = next.set.ids;
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.MISSIONS_DRAWN, {
                track: ctx.track,
                m1,
                m2,
                m3,
            });
        }
        return true;
    }
    /** Module 133 — feed one event to today's missions; how many it finished. */
    _missionEvent(evt) {
        this._rollMissions();
        if (!this._missions)
            return 0;
        const update = applyMissionEvent(this._missions, evt, this._todayUtc());
        if (!update.changed)
            return 0;
        this._missions = update.state;
        this._missionsCompleted = [...this._missionsCompleted, ...update.completed];
        // WP-133.5 — each mission once, the moment it reaches its target.
        for (const slot of update.completed) {
            const def = missionDef(update.state.set?.ids[slot] ?? '');
            if (!def)
                continue;
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.MISSION_COMPLETED, {
                mission: def.id,
                coins: def.coins,
            });
        }
        return update.completed.length;
    }
    /**
     * Module 133 — a fresh look at today's set (home calls it when it opens: a
     * new UTC day may have begun with no event since). Saves only on a new draw.
     */
    refreshMissions() {
        if (this._rollMissions()) {
            this._commitState();
            this._renderHud();
        }
    }
    /** Module 133 — today's missions state (null before the first draw). */
    get missions() {
        return this._missions;
    }
    /** Module 133 — the set is today's: progress counts and claims pay. */
    get missionsLive() {
        return this._missions !== null && missionsLive(this._missions, this._todayUtc());
    }
    /** Module 133 (D1a) — the mission the latest win finished (its win-popup line), or null. */
    get missionWinSlot() {
        return this._missionWinSlot;
    }
    /** Module 133 — slots finished since the HUD last took them (the completion toast). */
    takeCompletedMissions() {
        const done = this._missionsCompleted;
        this._missionsCompleted = [];
        return done;
    }
    /**
     * Module 133 (WP-133.1/133.2) — pay one finished mission (the
     * `claimMilestone` shape): claimed first so a double tap finds it paid, then
     * the credit under `blockjam_mission_<slot>_<coins>`; a credit that throws is
     * lost, never retried (migration 0028's `missionsPaid` is the web's guard).
     * Shares the milestones' 15-claims-a-minute window (one server rate limit).
     */
    claimMission(slot, from = 'popup') {
        if (!this._missions)
            return;
        const now = this._context?.clock.now() ?? 0;
        this._milestoneClaimTimes = this._milestoneClaimTimes.filter((at) => now - at < 60000);
        if (this._milestoneClaimTimes.length >= 15)
            return;
        const result = claimMissionSlot(this._missions, slot, this._todayUtc());
        if (!result)
            return;
        this._missions = result.state;
        this._milestoneClaimTimes.push(now);
        try {
            this._wallet.credit('coins', result.coins, result.sourceId);
            // WP-133.5 — after the credit, as `blockjam_milestone_claimed`.
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.MISSION_CLAIMED, {
                mission: result.id,
                coins: result.coins,
                from,
            });
        }
        catch {
            // Refused locally: keep the claim, never loop.
        }
        this._commitState();
        this._renderHud();
    }
    /**
     * Module 133 (D2) — all three claimed: one free power-up use for any
     * power-up (the Module 112/118 shared pool). Local, like the Daily Bonus's
     * day-7 ticket: no coins, so no server row.
     */
    claimMissionSetBonus(from = 'popup') {
        if (!this._missions)
            return;
        const result = claimMissionSetBonus(this._missions, this._todayUtc());
        if (!result)
            return;
        this._missions = result.state;
        this._powerups?.grantBonusFreeUses(result.tickets);
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.MISSION_BONUS_COLLECTED, { from });
        this._commitState();
        this._renderHud();
    }
    /** Module 118 — today's UTC date, from the injected clock. */
    _todayUtc() {
        return this._context ? dailyDate(this._context.clock) : null;
    }
    /**
     * Module 118 (WP-118.2) — what the daily reward card shows. `streakDay` and
     * `coins` are today's claim as things stand (the start-over day while a
     * repair is on offer). Null without a context.
     */
    get dailyRewardStatus() {
        const today = this._todayUtc();
        if (!today)
            return null;
        const offer = repairOffer(this._dailyLogin, today);
        return {
            claimable: claimableToday(this._dailyLogin, today),
            streakDay: nextStreakDay(this._dailyLogin, today),
            lastDay: this._dailyLogin.day,
            repair: offer ? { streakDay: offer.streakDay, price: STREAK_REPAIR_PRICE } : null,
        };
    }
    /**
     * Module 118 (WP-118.2) — claim today's streak reward. The `onHowToRewardCollected`
     * pattern: the decision is made here, never from the tap; the state is set
     * BEFORE the credit so a double tap finds it claimed; a credit that throws
     * is lost, never retried (the server's once-per-UTC-day stamp, migration
     * 0024, is the real guard). Day 7 also adds one free use for any power-up.
     */
    claimDailyReward() {
        const today = this._todayUtc();
        if (!today)
            return;
        const result = claimDailyLogin(this._dailyLogin, today);
        if (!result)
            return;
        // Module 132 — the Daily Bonus streak milestone counts days past the cycle.
        const continued = continuesStreak(this._dailyLogin, today);
        this._dailyLogin = result.state;
        this._milestoneEvent({ type: 'loginClaimed', continued }, false);
        let paid = false;
        try {
            this._wallet.credit('coins', result.coins, DAILY_LOGIN_SOURCE_ID);
            paid = true;
        }
        catch {
            // Refused locally: keep the claim, show no coins, never loop.
        }
        if (paid && result.ticket)
            this._powerups?.grantBonusFreeUses(1);
        this._commitState();
        if (paid) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.DAILY_REWARD_CLAIMED, {
                streak_day: result.streakDay,
                coins: result.coins,
                ticket: result.ticket,
            });
        }
        this._renderHud();
    }
    /**
     * Module 118 (WP-118.2) — buy back a streak after exactly one missed day.
     * `short` when the wallet refuses (the UI answers with "Get coins", Module 76);
     * `none` when no repair is on offer. The debit comes first: nothing changes
     * unless it went through.
     */
    repairStreak() {
        const today = this._todayUtc();
        if (!today || !this._context)
            return 'none';
        const offer = repairOffer(this._dailyLogin, today);
        const next = repairDailyLogin(this._dailyLogin, today);
        if (!offer || !next)
            return 'none';
        if (this._wallet.getBalance('coins') < STREAK_REPAIR_PRICE)
            return 'short';
        const debit = this._wallet.debit('coins', STREAK_REPAIR_PRICE, STREAK_REPAIR_SOURCE_ID);
        if (!debit.ok)
            return 'short';
        this._dailyLogin = next;
        this._commitState();
        this._context.analytics.logEvent(ANALYTICS_EVENTS.STREAK_REPAIRED, {
            streak_day: offer.streakDay,
            price: STREAK_REPAIR_PRICE,
        });
        this._renderHud();
        return 'ok';
    }
    /**
     * Module 118 — the Daily Bonus, Quests (and, from the HUD, Free Coins) on
     * home. WP-118.9 (user, 2026-10-14): shown from day one, never opened by
     * itself for anyone; a new player is simply not nudged: `nudge` (the gift's
     * wiggle) waits until 3 puzzles are won.
     */
    _dailyRewardHudProps() {
        const status = this.dailyRewardStatus;
        if (!status)
            return {};
        return {
            dailyReward: { ...status, nudge: this._puzzlesSolved() >= 3 },
            onClaimDailyReward: () => this.claimDailyReward(),
            onRepairStreak: () => this.repairStreak(),
            onDailyRewardShown: (auto) => this._onDailyRewardShown(auto),
            // Module 132 (WP-132.4) — the quests are milestones on screen; the analytics
            // event keeps its name (D1). Claiming pays the milestone key, never the quest.
            milestones: this.milestonesView,
            onClaimMilestone: (key, from) => this.claimMilestone(key, from),
            badgesRequest: this._badgesRequest,
            milestoneLine: this._winLine,
            milestoneToast: this._milestoneToast,
            milestoneReveal: this._milestoneReveal,
            onMilestonesOpened: (from) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.QUESTS_OPENED, {
                    claimable: this.claimableMilestones.length,
                    from,
                });
            },
            onMilestoneUi: (evt) => this._logMilestoneUi(evt),
            // Module 133 (WP-133.4) — Daily Missions.
            missions: this._missionsHud(),
            onClaimMission: (slot, from) => this.claimMission(slot, from),
            onCollectMissionBonus: (from) => this.claimMissionSetBonus(from),
            onMissionsOpened: (from) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.MISSIONS_OPENED, {
                    from,
                    ready: this._missionsHud()?.readyCount ?? 0,
                });
            },
            onRefreshMissions: () => this.refreshMissions(),
            missionToast: this._missionToast,
            missionWinSlot: this._missionWinSlot,
            // Module 134 — the Win Streak.
            winStreak: this._winStreakHud(),
            onSaveWinStreak: () => this.saveWinStreak(),
        };
    }
    /** Module 118 — the card opened (by a tap: it never opens by itself since WP-118.9). */
    _onDailyRewardShown(auto) {
        const status = this.dailyRewardStatus;
        if (!status)
            return;
        this._context?.analytics.logEvent(ANALYTICS_EVENTS.DAILY_REWARD_SHOWN, {
            streak_day: status.streakDay,
            auto,
            repair_offered: status.repair !== null,
        });
    }
    /** Module 118 (WP-118.2) — every quest with its progress, in `QUESTS` order. */
    get questStatus() {
        return QUESTS.map((q) => {
            const progress = Math.min(q.target, this._quests.progress[q.id] ?? 0);
            return {
                id: q.id,
                target: q.target,
                progress,
                coins: q.coins,
                complete: progress >= q.target,
                claimed: this._quests.claimed.includes(q.id),
            };
        });
    }
    /** Module 118 (WP-118.2) — completed, unpaid quests: the "!" dot's count. */
    get claimableQuestCount() {
        return claimableQuests(this._quests).length;
    }
    /** Module 118 (WP-118.2) — pay one completed quest, once ever (same shape as `claimDailyReward`). */
    claimQuestReward(id) {
        // Module 132 — a quest's tier already paid as a milestone is never paid again.
        const tier = LEGACY_QUEST_TIER[id];
        if (tier !== undefined && this._milestones.claimed.includes(tier))
            return;
        const result = claimQuest(this._quests, id);
        if (!result)
            return;
        this._quests = result.state;
        let paid = false;
        try {
            this._wallet.credit('coins', result.coins, `${QUEST_SOURCE_PREFIX}${id}`);
            paid = true;
        }
        catch {
            // Refused locally: keep the claim, never loop.
        }
        this._commitState();
        if (paid) {
            this._context?.analytics.logEvent(ANALYTICS_EVENTS.QUEST_CLAIMED, {
                quest_id: id,
                coins: result.coins,
            });
        }
        this._renderHud();
    }
    _getHudProps() {
        return {
            // Module 65 (D8) — the pill reads `Puzzle N`, and N is the ordinal. The
            // effective level is an internal coordinate and never reaches the player.
            level: this._ordinal,
            track: this._track,
            needsTrackSelection: this._needsTrackSelection,
            preparing: this._preparing !== null,
            boardRenderLost: this._boardRenderLost,
            onReloadBoard: () => this.reloadBoard(),
            // Module 79 (WP-79.3) / Module 116 — the home tour, while it is owed.
            ...(this._homeTourOwed && this._needsTrackSelection
                ? {
                    homeTour: {
                        hostPaused: this._hostPaused,
                        onShown: () => {
                            if (this._seenHomeTour)
                                return;
                            this._seenHomeTour = true;
                            this._commitState();
                        },
                        onEnded: (outcome, target, step) => {
                            this._homeTourOwed = false;
                            this._context?.analytics.logEvent(ANALYTICS_EVENTS.HOME_TOUR_ENDED, {
                                outcome,
                                target,
                                step,
                            });
                        },
                    },
                }
                : {}),
            trackProgress: this._trackProgress,
            // Module 131 (WP-131.4) — the hero's bar: each track's Reach-badge ladder.
            trackLadder: Object.fromEntries(TRACK_IDS.map((id) => [id, trackLadder(this._milestones, id)])),
            // Module 131 (WP-131.7) — staged unlocks by total wins (D6/D7).
            featureSlots: featureStates(counterOf(this._milestones, 'wins')),
            featureIntro: introDue(counterOf(this._milestones, 'wins'), this._seenFeatureIntros),
            // Module 133 (D3) — a player who has never opened home meets the intro on
            // the unlocking win's popup instead of waiting for a home visit.
            featureIntroOnWin: !this._homeOpened,
            onFeatureIntroSeen: (id) => {
                if (this._seenFeatureIntros.includes(id))
                    return;
                this._seenFeatureIntros = [...this._seenFeatureIntros, id];
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.FEATURE_UNLOCKED, { feature: id });
                this._commitState();
                this._renderHud();
            },
            onSelectTrack: (id) => {
                this.selectTrack(id);
            },
            // Module 67 (WP-67.C5) — the Daily Challenge: home banner + in-play mode.
            daily: this.dailyStatus(),
            dailyMode: this._dailyMode,
            onEnterDaily: () => {
                this.enterDaily();
            },
            onFailDaily: () => {
                this.failDailyAttempt();
            },
            // Module 118 (WP-118.4) — the daily reward, only where this module may show.
            ...this._dailyRewardHudProps(),
            // Module 79 (WP-79.9) — the Daily share. The HUD builds the text and
            // calls the host itself (the tap's user activation must reach the share
            // sheet); the plugin supplies the facts and logs what happened.
            dailyShare: this._dailyShareView(),
            onDailyShareTapped: (from) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.DAILY_SHARE_TAPPED, {
                    from,
                    state: this._daily?.state ?? 'unplayed',
                });
            },
            onDailyShared: (from, outcome) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.DAILY_SHARED, { from, outcome });
                // A closed share sheet is not a share; the other three put the text
                // somewhere the player can send it.
                if (outcome !== 'cancelled' && this._daily && !this._daily.shared) {
                    this._daily = { ...this._daily, shared: true };
                    this._commitState();
                    this._renderHud();
                }
            },
            coins: this._context?.economy.getBalance('coins') ?? 0,
            // Module 90 — set aside for open, unpaid modes (Module 91: and whose).
            heldCoins: this._powerups?.heldCoins() ?? 0,
            heldFor: this._powerups?.heldFor() ?? [],
            gems: this._context?.economy.getBalance('gems') ?? 0,
            ...(this._assets?.coinIconUrl ? { coinIconUrl: this._assets.coinIconUrl } : {}),
            // WP-101.9 — the demos draw from the configs the board draws from.
            ...(this._visualConfig ? { visuals: this._visualConfig } : {}),
            ...(this._boardVisualConfig ? { boardVisuals: this._boardVisualConfig } : {}),
            moveCount: this._state?.moveCount ?? 0,
            isWon: this._isWon,
            // WP-36.5 — the celebration's tap-to-skip layer. Only on screen between
            // the last block leaving and the popup appearing.
            isCelebrating: this._isCelebrating,
            onSkipCelebration: () => {
                this.skipCelebration();
            },
            // WP-53.1 — the getter, not the raw field: both outcome props come from
            // the one arbiter, so the HUD cannot be handed a pair that contradict.
            isStuck: this.isStuck,
            // Module 50 (WP-50.28a) — the move budget. `isOutOfMoves` is DERIVED from
            // the state rather than kept as a flag, so restarting, entering a level and
            // being granted ad moves all clear it without a reset site each.
            moveBudget: this.moveBudget,
            isOutOfMoves: this.isOutOfMoves,
            movesLow: (() => {
                const left = this.movesRemaining;
                return (left !== null && left > 0 && left <= this._generationConfig.moveBudget.warnAtRemaining);
            })(),
            // Module 103 (WP-103.2) — the status capsule's rotating items.
            status: this._statusInfo(),
            movesAlert: this._movesAlert,
            extraMovesOffer: this.extraMovesOffer,
            // Module 118 (WP-118.7) — the coin rescue and the Daily's paid retry.
            coinMovesOffer: this.coinMovesOffer,
            onBuyMoves: () => this.buyExtraMoves(),
            dailyRetryOffer: this.dailyRetryOffer,
            onRetryDaily: () => this.retryDaily(),
            extraMovesPending: this._extraMovesPending,
            // Module 130 (D2) — the out-of-moves tray shows the last 1–3 blocks.
            leftoverBlocks: this.outOfMovesLeftovers,
            // Module 81 (WP-81.4/81.6) — the in-game Hint button. Evaluated BEFORE
            // `freeHintPowerupId` below (object literals evaluate in source order):
            // a lit hint is on the try-it glow's busy list, so it outranks the glow.
            revealButton: this.revealButton,
            revealNotice: this._revealNotice,
            revealConfirm: this._revealConfirm,
            revealPage: this._revealPage,
            revealedNoReward: this._revealedNoReward,
            onReveal: () => {
                this.requestReveal();
            },
            onDismissRevealTip: () => {
                this.dismissRevealTip();
            },
            onConfirmReveal: () => {
                void this.confirmReveal();
            },
            onCancelReveal: () => {
                this.cancelReveal();
            },
            onRevealDone: (step) => {
                this.closeReveal(step);
            },
            // Module 83 — the page draws its frames with the game's own renderer: a
            // second `FreeSlidePlayer` on the same visuals, no callbacks, input off
            // by CSS. A host that overrode the board (tests) gets no factory and the
            // page falls back to the SVG preview.
            ...(this._boardOverride || !this._boardClass
                ? {}
                : {
                    revealBoardFactory: (state) => new this._boardClass(state, {
                        reducedMotion: this._reducedMotionFn(),
                        ...(this._visualConfig ? { visuals: this._visualConfig } : {}),
                        ...(this._boardVisualConfig ? { boardVisuals: this._boardVisualConfig } : {}),
                    }),
                }),
            onWatchAdForMoves: () => {
                void this.requestExtraMoves();
            },
            isInfoOpen: this._isInfoOpen,
            // WP-59.9 — the game logs the fact on every platform; what consumes it
            // is per-platform composition (see constants/analytics.ts).
            onDialogOpenChange: (open) => {
                this._context?.analytics.logEvent(open ? ANALYTICS_EVENTS.DIALOG_OPENED : ANALYTICS_EVENTS.DIALOG_CLOSED, {});
            },
            pendingUnlock: this.pendingUnlock,
            onDismissUnlock: () => {
                this.dismissUnlock();
            },
            // Module 101 (WP-101.1) — the How to Play map's data and its one write path.
            howTo: this._howToProps(),
            onHowToTopicSeen: (id, source) => this.onHowToTopicSeen(id, source),
            onHowToRewardCollected: () => this.onHowToRewardCollected(),
            // Module 114 — telemetry funnel facts the HUD owns.
            onHomeShown: () => {
                // Module 133 — a new UTC day may have begun with no event since.
                this.refreshMissions();
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.HOME_SHOWN, {
                    first_visit: this._homeTourOwed,
                    // Module 131 (WP-131.8) — the hero + zones home, so telemetry can tell
                    // the redesign's sessions from the three-card home's.
                    layout: 'v2',
                });
            },
            // Module 116 (D5) — the win-popup language line.
            languageLineOwed: this._languageLineOwed(),
            onLanguageLineShown: () => {
                this._languageLineShown = true;
            },
            onLanguageAnswered: (choice) => {
                if (this._languageLineAnswered)
                    return;
                // Module 116 (WP-116.9) — opening the full list is browsing, not an
                // answer: logged, and the question stays (user play-test 2026-10-12).
                if (choice === 'more') {
                    this._context?.analytics.logEvent(ANALYTICS_EVENTS.LANGUAGE_ANSWERED, { choice });
                    return;
                }
                this._languageLineAnswered = true;
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.LANGUAGE_ANSWERED, { choice });
                this._commitState();
                this._renderHud();
            },
            // Module 116 (D8) — every switch, from the win line or Settings.
            onLanguageChanged: (from, to, via) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.LANGUAGE_CHANGED, { from, to, via });
            },
            // Module 100 (WP-100.6) — the unlock intro's stages, timed by the card.
            onPowerupIntroStage: (powerupId, stage, ms) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.POWERUP_INTRO_STAGE_COMPLETED, {
                    powerup_id: powerupId,
                    stage,
                    ms_on_stage: Math.round(ms),
                    level_id: this._state?.puzzle.levelNumber ?? 0,
                    track: this._track,
                    puzzle: this._ordinal,
                });
            },
            coinsEarned: this._coinsEarned,
            // Module 97 — Module 63's first-clear coins sentence ("Coins buy
            // power-ups. Tap + at the top for more.") is gone: it showed on puzzle 1,
            // when every power-up is still locked. Coins are taught where they are
            // spent — the first unlock, the "Not enough coins → Get coins" card, Rules.
            replayNoReward: this._replayNoReward,
            // WP-35.16.5: undo is snapshot-based on the free model, so the button
            // follows the plugin's own history, not the legacy board `undoStack`.
            canUndo: this._history.length > 0,
            // Module 67 — no Restart inside the Daily (the one-attempt rule); the
            // plugin-side `_restart` guard is the enforcement, this is the honest UI.
            canRestart: !this._dailyMode && (this._state?.moveCount ?? 0) > 0,
            onUndo: () => {
                this.undo();
            },
            onRestart: () => {
                this._restart();
            },
            onInfo: () => {
                this._isInfoOpen = !this._isInfoOpen;
                this._renderHud();
            },
            onNextLevel: () => {
                if (this._moveUpOffer?.active) {
                    this._context?.analytics.logEvent(ANALYTICS_EVENTS.TRACK_SUGGESTION_DECLINED, {
                        track: this._track,
                        offer: this._moveUpOffer.offer,
                    });
                }
                this._nextLevel();
            },
            // Module 77 (D6) — track guidance.
            ...this._guidanceProps(),
            onBack: () => {
                // Module 65 (D13) — Back returns to the Blockway home when the player is
                // IN a track; the home screen's own control is what leaves the game.
                // The Top Bar control is re-pointed, not added, so no chrome moves and
                // no layout invariant is disturbed. On the portal this also stops Back
                // being a dead-end exit.
                if (!this._needsTrackSelection) {
                    // Module 130 (user, 2026-10-21) — Home at the wall gives the attempt
                    // up: the next visit opens the same puzzle, fresh. With moves still
                    // left, leaving is a pause and the attempt resumes exactly (rule 3).
                    if (!this._dailyMode && this.isOutOfMoves)
                        this._restart({ gaveUp: true });
                    // Module 80 (WP-80.16) — leaving a puzzle mid-attempt is a failure
                    // the funnel needs to see; leaving a won or untouched one is not.
                    if (!this._dailyMode && !this._isWon && (this._state?.moveCount ?? 0) > 0) {
                        this._logAttemptFailed('quit');
                    }
                    // Module 116 (WP-116.9) — leaving a WON puzzle (the web's win-popup
                    // "Back to Lobby") is the same decision as Next: the win is counted
                    // and paid, so the track moves on and home's card names the next
                    // puzzle. Entering it builds that one (`selectTrack` never resumes a
                    // won board) — no second win popup, no ad (a track entry, not Next).
                    if (!this._dailyMode && this._isWon) {
                        if (this._languageLineShown && !this._languageLineAnswered && this._ordinal >= 3) {
                            this._languageLineAnswered = true;
                            this._context?.analytics.logEvent(ANALYTICS_EVENTS.LANGUAGE_ANSWERED, {
                                choice: 'ignored',
                            });
                        }
                        this._trackProgress[this._track] = {
                            ...this._trackProgress[this._track],
                            ordinal: this._ordinal + 1,
                        };
                    }
                    // Module 67 (WP-67.C3) — commit BEFORE the flags flip: while
                    // `_dailyMode` is still true, `_commitState` folds the live board
                    // into the daily record, so leaving by Home keeps the attempt
                    // `in-progress` and re-entry RESUMES it. Deferral is not a retry.
                    // Module 116 (WP-116.2) — the player has now opened home, by their
                    // own tap; every later boot lands there.
                    this._homeOpened = true;
                    this._commitState();
                    this._dropPreparing();
                    this._needsTrackSelection = true;
                    this._dailyMode = false;
                    this._musicToHome();
                    // Module 95 — back on the home screen: today's Daily, if still owed.
                    this._prewarmDaily();
                    this._renderHud();
                    return;
                }
                // WP-37.13 — end the music HERE rather than waiting for the teardown
                // that follows. The host fades the game out over FADE_OUT_MS before it
                // pauses and destroys the plugin, and music still playing over a
                // screen that is visibly leaving reads as a hang.
                this._endMusic();
                this._context?.requestExit();
            },
            onOpenLegalDoc: (doc) => {
                this._context?.openLegalDoc(doc);
            },
            onOpenShop: () => {
                this._context?.openShop();
            },
            // WP-35.18 — the power-up bar. The HUD renders `powerups` and reports arm
            // taps; every rule (unlock, cap, cost) is enforced in the plugin, never in
            // the button.
            powerups: this.powerups,
            armedPowerupId: this._armedPowerupId,
            armExplains: this._armExplains,
            // Module 144 — which card the armed confirm shows (WP-144.1), and its
            // first-use badges on the board (WP-144.3), placed by the board itself.
            ...(this._armedPowerupId &&
                (chargesWhenSpent(this._armedPowerupId) || activationFor(this._armedPowerupId) === 'target')
                ? { confirmStage: this.confirmStageFor(this._armedPowerupId) }
                : {}),
            ...(this._confirmPreview?.handBlock
                ? {
                    targetHandBlock: this._confirmPreview.handBlock,
                    measureBlock: (blockId) => this._board?.blockClientRect?.(blockId) ?? null,
                }
                : {}),
            ...(this._confirmPreview && this._confirmPreview.badges.length > 0
                ? {
                    gateBadges: {
                        kind: this._confirmPreview.id === SURGE_ID ? 'opens' : 'anyColour',
                        keys: this._confirmPreview.badges,
                    },
                    measureGates: (keys) => this._board?.gateClientRects?.(keys) ?? [],
                }
                : {}),
            // WP-50.27 — Rainbow Gates has no armed state to show, so the strip that
            // used to say "tap a block" now says the gates are open and that sliding a
            // block out closes them. Derived from the board, like every other rainbow
            // fact in this file.
            rainbowActive: this._state ? isRainbowActive(this._state) : false,
            // Module 63 — Surge's strip, same derivation.
            surgeActive: this._state ? isSurgeActive(this._state) : false,
            guidedPowerupId: this._guidedPowerupId,
            // Module 73 (WP-73.7) — at most one button, gated by the no-nag rules.
            freeHintPowerupId: this._freeHint(),
            onArmPowerup: (id) => {
                this.armPowerup(id);
            },
            // WP-37.11 — the HUD's cue player. Handed down rather than looked up, so
            // the React chrome holds no service of its own and a HUD rendered in a
            // test is silent by default.
            playCue: (id) => this._playCue(id),
            // 2026-08-05 audit — the in-game Settings popup's sliders, routed
            // through the SAME `IGameContext.audio` this plugin plays every cue
            // through, so a slider reaches whatever it started (the background
            // music most of all).
            onSetVolume: (channel, value) => this._context?.audio.setVolume(channel, value),
            // WP-41.6 — the host's capability set, straight through. The plugin makes
            // no decision here on purpose: it neither knows nor asks which host it is
            // in, and `host` being undefined (a context built before this field, or a
            // test) leaves the HUD on its app-equivalent default.
            host: this._context?.host,
            // WP-41.8 — the caption that accompanies the ghost hand. The hand itself
            // is drawn on the board; this is the one line of text with it. WP-43.5:
            // the wording belongs to the MOMENT, so the trigger carries its own key.
            showTutorial: this._tutorial !== null,
            tutorialCaptionKey: this.tutorialCaptionKey,
            tutorialCaptionSide: this.tutorialCaptionSide,
            // 2026-08-14 — which SURFACE the live moment is about. Non-null puts the
            // spotlight on that power-up button instead of on the canvas, and the hand
            // with it; the board draws nothing for this one.
            tutorialPowerupId: this.tutorialPowerupId,
            onDismissTutorial: () => this.handleGhostHandDismissed(),
            // The shared pointer (Module 44). The renderer already draws it on the
            // canvas for board moments; the HUD needs the same URL to draw the same
            // hand over a DOM button, because one game must teach touch with one hand.
            ...(this._assets?.tutorialHandUrl ? { tutorialHandUrl: this._assets.tutorialHandUrl } : {}),
            // WP-48.10 — the goal tooltip's one-time introduction. The HUD decides the
            // MOMENT (it holds off while a ghost hand or an unlock popup is up); the
            // plugin owns whether it is still owed, because only the plugin writes to
            // the save.
            introduceGoal: !this._seenGoalIntro,
            // Module 147 (D2) — the one-time Home coach: owed to a first-visit player
            // who won puzzle 3 and still has never opened home, on a regular board.
            introduceHome: this.homeCoachOwed,
            // Module 147 (D1) — the "Home" / "Settings" words under the Top Bar icons.
            showTopBarLabels: this.topBarLabelsShown,
            onSettingsOpened: () => {
                if (this._settingsOpened)
                    return;
                this._settingsOpened = true;
                this._commitState();
            },
            // Module 126 (WP-126.3) — the Top Bar avatar, its Profile panel, the picker
            // and the host's sign-in. `onSignIn` only where the host offers one (D2).
            avatar: this._avatar,
            profileStats: this._profileStats(),
            onUnlockAvatar: (id, from) => this.buyAvatar(id, from),
            // Module 152 (WP-152.3) — the upgrade nudges.
            avatarGoal: this._avatarGoalView(),
            onSetAvatarGoal: (id) => this.setAvatarGoal(id),
            avatarNewIds: this.newAvatarIds,
            avatarWaveCard: this._avatarWaveCardOwed(),
            onAvatarWaveSeen: (from) => this.markAvatarWaveSeen(from),
            onChooseAvatar: (id) => {
                const previous = displayedAvatar(this._avatar).id;
                if (this.chooseAvatar(id) && id !== previous) {
                    this._context?.analytics.logEvent(ANALYTICS_EVENTS.AVATAR_CHANGED, {
                        avatar: id,
                        previous,
                    });
                }
            },
            onSignIn: this._context?.host?.account?.signIn ? () => this._signIn('profile') : undefined,
            saveChoice: this._saveChoiceView(),
            signInNudgeOwed: this._signInNudgeOwed(),
            onSignInNudgeShown: () => {
                if (this._seenSignInNudge)
                    return;
                this._seenSignInNudge = true;
                this._commitState();
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.SIGN_IN_PROMPT_SHOWN, {
                    trigger: 'nudge',
                });
            },
            welcomeGift: this._welcomeGiftCard,
            onWelcomeGiftDone: () => {
                this._welcomeGiftCard = null;
                this._renderHud();
            },
            accountLoadsAfterPuzzle: this._accountLoadOwed,
            onProfileOpened: (from) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.PROFILE_OPENED, {
                    from,
                    signed_in: (this._context?.host?.account?.current() ?? null) !== null,
                });
            },
            onHomeCoachShown: () => {
                if (this._seenHomeCoach)
                    return;
                this._seenHomeCoach = true;
                this._commitState();
            },
            onHomeCoachEnded: (outcome) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.HOME_COACH_ENDED, {
                    outcome,
                    puzzle: this._ordinal,
                });
            },
            onGoalIntroduced: () => {
                if (this._seenGoalIntro)
                    return;
                this._seenGoalIntro = true;
                this._commitState();
            },
            // Module 77 (D5) — the plate chip shows on every board with a powered gate.
            hasPoweredGate: (this._state?.puzzle.plates?.length ?? 0) > 0,
            autoOpenPoweredHelp: this._owesPoweredHelp(),
            onPoweredHelpOpened: (auto) => {
                this._context?.analytics.logEvent(ANALYTICS_EVENTS.POWERED_HELP_OPENED, {
                    track: this._dailyMode ? 'daily' : this._track,
                    puzzle: this._ordinal,
                    auto,
                });
                if (this._seenPoweredHelp)
                    return;
                this._seenPoweredHelp = true;
                this._commitState();
                // Re-render so `autoOpenPoweredHelp` goes false before the popup closes;
                // a stale true re-opened it the moment the board went quiet again.
                this._renderHud();
            },
            // WP-41.11 — the win popup's optional "double your coins" offer. The HUD
            // renders it only when the host supplies `offerRewardedAd`; `host` above
            // is that same object, so there is one source for the question.
            doubleCoinsState: this._doubleCoins,
            // Module 96 — absent when this win's ad moment is not the offer, so the
            // popup renders no ad button (and Next takes the gold, Module 94).
            onDoubleCoins: this.doubleCoinsOffered
                ? () => {
                    void this.requestDoubleCoins();
                }
                : undefined,
            // The minimal ads-only Shop's own "watch an ad for coins" offer.
            shopCoinTopUpAmount: this.shopCoinTopUpAmount,
            // Module 141 — the ad-break gift card, while it shows.
            adGift: this._adGift,
            onAdGiftDone: (seq) => this._endAdGift(seq),
            onBoardCovered: (covered) => this._setBoardCovered(covered),
            shopTopUpPending: this.shopTopUpPending,
            shopTopUpUnavailable: this.shopTopUpUnavailable,
            // Module 123 — the home Free Coins button's own state.
            freeCoinsReadyAt: this.freeCoinsCooldownMs > 0 ? this._freeCoinsReadyAt : 0,
            freeCoinsUnavailable: this._freeCoinsUnavailable,
            now: () => this._context?.clock.now() ?? Date.now(),
            // Module 137 — the Shop showed the coin pack (`iap` `shown`).
            onCoinPackShown: () => this._context?.analytics.logEvent(ANALYTICS_EVENTS.IAP, {
                stage: 'shown',
                product_id: IAP_COIN_PACK_PRODUCT_ID,
            }),
            onRequestShopCoinTopUp: (surface) => {
                void this.requestShopCoinTopUp(surface);
            },
            // WP-45.3 — the "not unlocked yet" notice. Passed as the whole status, not
            // as an id, so the HUD reads the name and the unlock level from the same
            // `generation.json`-derived object every other power-up affordance reads —
            // there is no second place where an unlock level could be written.
            refusalNotice: this._refusalNoticeView(),
            coinsCue: this._coinsCue,
            freeUseCue: this._freeUseCue,
            // Module 110 — the Top Bar's shown balance (see `useShownBalance`).
            coinMotion: this._coinMotion,
            readCoins: () => this._context?.economy.getBalance('coins') ?? 0,
            // A reader, not a value: the win is credited between two HUD renders, and
            // the pill must see the cover the moment the credit lands.
            isCoinsCovered: () => this.coinsCovered,
            isCoinsBlocked: () => this.coinsBlocked,
            activeFreeUses: Object.fromEntries(this._activeFreeUses),
            onConfirmPowerup: () => {
                this.confirmPowerup();
            },
            // Module 90 — "Not now" on the confirm card: a cancel, nothing charged.
            onDeclinePowerup: () => this.cancelArm('not_now'),
            // Module 144 — the ✕ on a refusal card closes it before its timer.
            onDismissNotice: () => this._clearLockedNotice(),
            // Module 90 (D5) — the ✕ on an open mode's active strip (Module 91: by id).
            onCancelOpenPowerup: (id) => this.cancelOpenPowerup(id),
            // Module 90 (D4) — each open mode's button is drawn "on" (Module 91: all).
            openPowerupIds: this._state ? openUnpaidPowerups(this._state) : [],
        };
    }
    /**
     * Module 41 (WP-41.8), generalized in WP-43.5 — show the ghost hand for
     * whichever tutorial moment this board is due, if any.
     *
     * Which moments exist, when each applies and what each demonstrates all live
     * in `onboarding/tutorial-triggers.ts`; this method is only the wiring. Level 1
     * is a row in that table, not a branch here.
     *
     * Deliberately not gated on the host: a first-time player on the main site
     * benefits from the same thing, and a game that shows onboarding only on one
     * platform is a game that has learned where it is running (Module 42, rule 3).
     */
    _maybeShowTutorial() {
        // Module 67 (WP-67.C3) — no coached lessons inside the Daily, for the same
        // reason `_adoptLevel` empties the unlock queue there.
        if (this._dailyMode)
            return;
        if (!this._board || !this._state)
            return;
        // WP-78.3 — nothing is taught under the entry card; dismissing it calls back in.
        if (this._trackEntry !== null)
            return;
        // An unlock popup is a modal over the board — a hand tracing underneath it
        // teaches nothing and is dismissed by a press meant for the popup. Dismissing
        // the last popup calls back in here, which is where those levels' hands
        // actually appear.
        if (this._unlockQueue.length > 0)
            return;
        const trigger = pendingTutorial(this._generationConfig, this._state, this._seenTutorials, this._ordinal, this._tutorialOpts());
        if (!trigger)
            return;
        const demo = trigger.resolve(this._state, this._generationConfig);
        if (!demo)
            return;
        // The board refuses a gesture it cannot actually act out, and the refusal is
        // respected rather than overridden: a caption with no hand under it teaches
        // nothing, so if there is nothing to draw there is nothing to show and the
        // key stays unseen for a future attempt.
        const shown = demo.gesture === 'button'
            ? // Nothing for the board to refuse: the target is a HUD button, drawn by
                // `OnboardingCoach`'s `powerup` step over the DOM. The board is not
                // asked, so it cannot say no.
                true
            : demo.gesture === 'tap'
                ? this._board.showGhostTap(demo.blockId)
                : this._board.showGhostHand(demo.blockId, demo.direction, { full: demo.exit === true });
        if (!shown)
            return;
        // Which surface this moment is about, for the HUD. Null for every board
        // gesture, so the coach keeps drawing those over the canvas exactly as
        // before.
        this._tutorialPowerupId = demo.gesture === 'button' ? demo.powerupId : null;
        this._tutorial = trigger;
        // WP-50.9 (AUD-013) — which END of the board the caption must NOT sit on.
        //
        // The audit's finding, exactly: on level 1 the hand draws a path DOWN to the
        // yellow gate on the bottom wall, and the caption — anchored to the bottom in
        // portrait — was rendered across that gate. "The one thing the sentence names
        // is the one thing the player cannot see."
        //
        // The rule is general rather than level-1-shaped: an exit demo knows the
        // direction the block leaves in, so it knows which wall its gate is on, and
        // the caption goes to the other end. A left/right exit names a gate on a side
        // wall, which neither anchor can cover, so it keeps whichever end the
        // orientation prefers (`null`) — as does a demo that names no gate at all.
        this._tutorialCaptionSide =
            demo.gesture === 'drag' && demo.exit === true
                ? // The wall the GATE is on, which is the direction the block leaves in.
                    demo.direction === 'down'
                        ? 'bottom'
                        : demo.direction === 'up'
                            ? 'top'
                            : null
                : null;
        // WP-45.4 — the caption belongs to the MOVE, not to the moment. A resolver
        // that fell back to demonstrating something other than what its trigger
        // promises says so here, so the hand can never be captioned "out through its
        // matching gate" over a drag that goes somewhere else (BUG-003).
        this._tutorialCaptionKey = demo.captionKey ?? trigger.captionKey;
        // Marked seen on SHOW, not on dismiss. Marking on dismiss would replay the
        // tutorial for a player who reloads without touching the board — which, on a
        // portal where a session is a page load, is every bounced visit.
        this._seenTutorials = markTutorialSeen(this._seenTutorials, trigger.key);
        if (trigger.key.startsWith('powerup:')) {
            this._powerupLessonShownOn = `${this._track}:${this._ordinal}`;
        }
        if (trigger.key === 'poweredPlate' || trigger.key === 'poweredHold') {
            this._poweredLessonShownOn = this._boardKey();
        }
        this._commitState();
        this._renderHud();
    }
    /**
     * The player's first press landed (or the board went away) — WP-41.8. Only the
     * caption needs clearing; the board has already removed the hand, and that
     * same press is at this moment being handled as an ordinary pointer-down.
     */
    handleGhostHandDismissed() {
        if (!this._tutorial)
            return;
        this._tutorial = null;
        this._tutorialCaptionKey = null;
        this._tutorialCaptionSide = null;
        this._tutorialPowerupId = null;
        this._renderHud();
    }
    _commitState() {
        if (this._moveCommitTimer !== null) {
            clearTimeout(this._moveCommitTimer);
            this._moveCommitTimer = null;
        }
        if (!this._context || !this._state)
            return;
        const module = this._context.save.getModule(SAVE_KEYS.BLOCKJAM_PROGRESS);
        // WP-65.F2 — the current track's consumed free uses ride its own record;
        // the other two tracks' records are whatever they saved. Folded in here
        // rather than kept in step elsewhere so there is exactly one place the
        // controller's counter meets the save.
        // Written back into `_trackProgress` — not folded into a local copy —
        // because `selectTrack` re-saves every track's record from memory: a copy
        // left the in-memory Beginner record without its consumed uses, so the
        // first switch away erased them and the switch back re-granted the Surges.
        // The per-track test in `bj-surge.test.ts` is what caught it.
        const consumed = this._powerups?.freeUsesConsumed() ?? {};
        this._trackProgress[this._track] = {
            ...this._trackProgress[this._track],
            powerupFreeUsed: consumed,
        };
        // Module 129 (WP-129.3) — the track's puzzle in progress, in its own record
        // so switching tracks or playing the Daily keeps it. Only while the TRACK
        // board is the one held (the Daily's lives in `daily.boardState`); a
        // cleared board has nothing to resume.
        if (!this._boardIsDaily) {
            const rest = { ...this._trackProgress[this._track] };
            delete rest.board;
            this._trackProgress[this._track] =
                this._state.blocks.length > 0
                    ? {
                        ...rest,
                        board: {
                            ordinal: this._ordinal,
                            state: { ...this._state, undoStack: [] },
                            powerupsUsed: this._powerups?.usedThisLevel() ?? {},
                        },
                    }
                    : rest;
        }
        module.save({
            // Module 65 (WP-65.B1) — a track and three positions, not a level number.
            // WP-65.G2 — always the LAST TRACK ENTERED, never nulled while the home
            // screen is up: the home screen shows on every entry now, so `null` no
            // longer means anything, and forgetting the track on a Home-screen exit
            // would rebuild the wrong board behind the next boot's scrim.
            currentTrack: this._track,
            tracks: this._trackProgress,
            boardState: this._state,
            hintsUsed: this._state.hintsUsed,
            // WP-35.16: which unlock popups the player has already seen. Persisted here
            // because `_commitState` is the one writer to SaveService (module rule 3).
            seenUnlocks: this._seenUnlocks,
            // WP-41.8/43.5 — same reasoning as `seenUnlocks`: `_commitState` is the one
            // writer to SaveService (module rule 3). Only the new field is written;
            // WP-41.8's `seenTutorial` boolean is read on load and never again, so a
            // save passes through the migration exactly once.
            seenTutorials: this._seenTutorials,
            // WP-48.10 — written here for the same reason as the two sets above:
            // `_commitState` is the one writer to SaveService (module rule 3).
            seenGoalIntro: this._seenGoalIntro,
            // WP-52.1 — the language step's own mark, written through the same single
            // writer as everything else here (module rule 3).
            // Module 79 (WP-79.4) — the step this marked is gone; the flag is carried
            // through unchanged so a save round-trips byte-for-byte.
            seenLanguageIntro: this._seenLanguageIntro,
            seenPoweredHelp: this._seenPoweredHelp,
            seenHomeTour: this._seenHomeTour,
            homeOpened: this._homeOpened,
            seenHomeCoach: this._seenHomeCoach,
            settingsOpened: this._settingsOpened,
            // Module 123 — the home Free Coins cooldown survives a reload.
            ...(this._freeCoinsReadyAt > 0 ? { freeCoinsReadyAt: this._freeCoinsReadyAt } : {}),
            languageLineAnswered: this._languageLineAnswered,
            earlyMilestones: this._earlyMilestones,
            explainedPowerups: this._explainedPowerups,
            // Module 144 (WP-144.1) — the confirm-card Use counts.
            powerupConfirms: this._powerupConfirms,
            // Module 101 (WP-101.1) — the How to Play map's seen set and reward flag.
            howToSeen: this._howToSeen,
            seenFeatureIntros: this._seenFeatureIntros,
            howToRewarded: this._howToRewarded,
            poweredBoardsSeen: this._poweredBoardsSeen,
            // Module 87 (WP-87.2b) — routes already found, so no board is searched twice.
            routeCache: this._routeCache.entries(),
            // Module 118 (WP-118.2) — daily reward streak, quests, day-7 tickets.
            dailyLogin: this._dailyLogin,
            quests: this._quests,
            // Module 132 (WP-132.1) — milestone counters and paid levels.
            milestones: this._milestones,
            // Module 133 (WP-133.1) — the salt and today's set.
            ...(this._missions ? { missions: this._missions } : {}),
            // Module 134 (WP-134.1) — written once it differs, so an untouched save round-trips.
            ...(this._winStreak !== EMPTY_WIN_STREAK ? { winStreak: this._winStreak } : {}),
            bonusFreeUses: this._powerups?.bonusFreeUses() ?? 0,
            // Module 126 (WP-126.2) — written only once it differs from the default,
            // so a save that never touched the picker round-trips unchanged. An id
            // this build doesn't know is carried, never dropped (`meta/avatars.ts`).
            ...(this._avatar.chosen !== defaultAvatarState().chosen || this._avatar.owned.length > 0
                ? { avatar: this._avatar }
                : {}),
            // Module 126 (D14, D7) — once-ever markers, written only once true.
            ...(this._welcomeGift ? { welcomeGift: true } : {}),
            ...(this._seenSignInNudge ? { seenSignInNudge: true } : {}),
            // Module 152 (WP-152.3) — written only once set / past the launch wave.
            ...(this._avatarGoal ? { avatarGoal: this._avatarGoal } : {}),
            ...(this._lastSeenAvatarWave > 1 ? { lastSeenAvatarWave: this._lastSeenAvatarWave } : {}),
            // Module 67 (WP-67.C3) — the daily record, board folded in only while
            // the daily is LIVE and undecided: that board is what re-entry resumes.
            // Folded into `_daily` ITSELF (not a save-local copy) so a same-session
            // re-entry resumes too — the WP-65.F2 free-uses lesson, one field over.
            // A terminal record drops its board, and a record is never invented
            // here — only carried.
            ...(this._daily
                ? {
                    daily: (this._daily =
                        this._dailyMode && this._daily.state === 'in-progress' && this._state
                            ? { ...this._daily, boardState: this._state }
                            : this._daily),
                }
                : {}),
            version: 2,
        });
    }
    _hitTestBlock(x, y) {
        if (!this._state)
            return null;
        for (const block of this._state.blocks) {
            const left = block.position.col * CELL_SIZE;
            const top = block.position.row * CELL_SIZE;
            const right = left + (block.orientation === 'horizontal' ? block.size * CELL_SIZE : CELL_SIZE);
            const bottom = top + (block.orientation === 'horizontal' ? CELL_SIZE : block.size * CELL_SIZE);
            if (x >= left && x < right && y >= top && y < bottom)
                return block;
        }
        return null;
    }
    _slideDirection(block, tapX, tapY) {
        const halfSize = block.size / 2;
        const centerX = (block.position.col + (block.orientation === 'horizontal' ? halfSize : 0.5)) * CELL_SIZE;
        const centerY = (block.position.row + (block.orientation === 'horizontal' ? 0.5 : halfSize)) * CELL_SIZE;
        if (block.orientation === 'horizontal') {
            const dx = tapX - centerX;
            if (Math.abs(dx) < CELL_SIZE / 2)
                return null;
            return dx > 0 ? 'right' : 'left';
        }
        else {
            const dy = tapY - centerY;
            if (Math.abs(dy) < CELL_SIZE / 2)
                return null;
            return dy > 0 ? 'down' : 'up';
        }
    }
}
/**
 * Module 129 (WP-129.3) — moves are saved as they're played (resume exactly),
 * but at most once per {@link MOVE_COMMIT_MS}: on Playgama every save is a
 * platform storage write, on the web a server call. The page hiding or
 * closing writes a pending save at once, so a normal reload loses nothing;
 * only a hard crash can lose the last two seconds. Any `_commitState` writes
 * everything, so it cancels the pending one.
 */
BlockJamPlugin.MOVE_COMMIT_MS = 2000;
/**
 * How long the locked notice stays up (WP-45.3).
 *
 * Long enough to read one short sentence and no longer: the notice sits over
 * the board, and the player is meant to carry on playing while it fades.
 */
BlockJamPlugin.LOCKED_NOTICE_MS = 2500;
/** Module 76 — a notice with a button in it ("Get coins") stays up this long. */
BlockJamPlugin.ACTION_NOTICE_MS = 4000;
// ─── Rewarded ads (WP-41.11) ──────────────────────────────────────────────
//
// Every placement goes through `IGameContext.host.offerRewardedAd`, and that is
// the whole of Block Jam's relationship with advertising. It does not know
// what an ad network is, which host it is in, or that the promise it awaits is
// an ad at all — only that the host may or may not owe it a reward afterwards.
// Where the host supplies no such function, neither offer renders.
/**
 * Module 141 — the longest the ad-break gift card may stay up: Economy Rule
 * 9's exception ("at most 2 s"). The card ends itself sooner
 * (`AD_GIFT_TIMING`); this is the cap, held here whatever the HUD does.
 */
BlockJamPlugin.AD_GIFT_MAX_MS = 2000;
/**
 * How long the "no ad available" line stays before the offer returns
 * (WP-51.4). Long enough to read, short enough that a player who wants to try
 * again is not left waiting on a button that has stopped explaining itself.
 */
BlockJamPlugin.AD_UNAVAILABLE_MS = 4000;
/**
 * Module 110 (D8) — how long a spend's coins take to leave the Top Bar for
 * their button: the release beat (60 ms), four coins 110 ms apart and a
 * 750 ms trip (the reward's trail, reversed — user: the shorter one was not
 * noticeable), with a little to spare. A wait for the EYES, so it is skipped
 * where nobody can watch (reduced motion, no HUD mounted).
 */
BlockJamPlugin.SPEND_BEAT_MS = 1200;
/**
 * Module 122 (second fix) — the portrait Moves bar's last line of defence.
 *
 * The stylesheet slides Row 2 down to sit centred between the Top Bar and the
 * board (blockjam.css, Module 120), capped by the measured board top. On the
 * user's S25 Ultra — Chrome AND Samsung Internet, 384px wide at DPR 3.75 — the
 * browser still came out 44px further down than that arithmetic gives in every
 * desktop engine (`row2 shift 44.4` where it should be 0.4: the bar on the
 * board). The board, the Top Bar and Row 2's own box were all where they should
 * be; only the CSS shift was wrong, and it could not be reproduced off the
 * device.
 *
 * So the bar is judged by where it REALLY is: if the stylesheet's placement puts
 * Row 2's band past the board's top edge, an inline shift computed from measured
 * rects takes over (centred in the space that is really there, never upward).
 * Everywhere the stylesheet is right the band ends at or above the board top
 * (its shift is capped at the centred value + 0.5px), nothing is written, and
 * the page renders exactly as before.
 */
const ROW2_FIT_ATTR = 'data-bj-row2-fit';
function clearRow2Fit(row2) {
    if (!row2.hasAttribute(ROW2_FIT_ATTR))
        return;
    row2.style.removeProperty('transform');
    row2.removeAttribute(ROW2_FIT_ATTR);
}
/** The Y translation of a computed `transform` (`none`, `matrix()` or `matrix3d()`). */
export function translateYOf(transform) {
    const m = /^matrix(3d)?\(([^)]*)\)$/.exec(transform.trim());
    if (!m)
        return 0;
    const v = m[2].split(',').map((n) => Number(n));
    const y = m[1] ? v[13] : v[5];
    return Number.isFinite(y) ? y : 0;
}
export function fitRow2(row2, boardTop) {
    // Judge the stylesheet's own placement, not a fit written earlier.
    clearRow2Fit(row2);
    if (document.documentElement.getAttribute('data-layout') !== 'portrait')
        return;
    const band = row2.getBoundingClientRect();
    if (band.height <= 0 || band.bottom <= boardTop + 1)
        return;
    const restTop = band.top - translateYOf(getComputedStyle(row2).transform);
    const fit = Math.max(0, (boardTop - (restTop + band.height)) / 2);
    row2.style.transform = `translateY(${Math.round(fit * 10) / 10}px)`;
    row2.setAttribute(ROW2_FIT_ATTR, '1');
}
